/**
 * Generated by @openapi-codegen
 *
 * @version 1.0.0
 */
import * as reactQuery from "@tanstack/react-query";
import { useEstabrookContext, EstabrookContext } from "./estabrookContext";
import type * as Fetcher from "./estabrookFetcher";
import { estabrookFetch } from "./estabrookFetcher";
import type * as Schemas from "./estabrookSchemas";
import type { ClientErrorStatus, ServerErrorStatus } from "./estabrookUtils";

export type AdminOrganizationContextControllerGetCurrentError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminOrganizationContextControllerGetCurrentResponse = {
  data?: Schemas.CurrentOrganizationResDto;
  statusCode?: number;
};

export type AdminOrganizationContextControllerGetCurrentVariables =
  EstabrookContext["fetcherOptions"];

export const fetchAdminOrganizationContextControllerGetCurrent = (
  variables: AdminOrganizationContextControllerGetCurrentVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminOrganizationContextControllerGetCurrentResponse,
    AdminOrganizationContextControllerGetCurrentError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/organizations/current",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminOrganizationContextControllerGetCurrent = <
  TData = AdminOrganizationContextControllerGetCurrentResponse,
>(
  variables: AdminOrganizationContextControllerGetCurrentVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminOrganizationContextControllerGetCurrentResponse,
      AdminOrganizationContextControllerGetCurrentError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminOrganizationContextControllerGetCurrentResponse,
    AdminOrganizationContextControllerGetCurrentError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/organizations/current",
      operationId: "adminOrganizationContextControllerGetCurrent",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminOrganizationContextControllerGetCurrent(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminOrganizationContextControllerUpdateCurrentError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminOrganizationContextControllerUpdateCurrentResponse = {
  data?: Schemas.CurrentOrganizationResDto;
  statusCode?: number;
};

export type AdminOrganizationContextControllerUpdateCurrentVariables = {
  body: Schemas.AdminPatchCurrentOrganizationReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminOrganizationContextControllerUpdateCurrent = (
  variables: AdminOrganizationContextControllerUpdateCurrentVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminOrganizationContextControllerUpdateCurrentResponse,
    AdminOrganizationContextControllerUpdateCurrentError,
    Schemas.AdminPatchCurrentOrganizationReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/organizations/current",
    method: "patch",
    ...variables,
    signal,
  });

export const useAdminOrganizationContextControllerUpdateCurrent = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminOrganizationContextControllerUpdateCurrentResponse,
      AdminOrganizationContextControllerUpdateCurrentError,
      AdminOrganizationContextControllerUpdateCurrentVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminOrganizationContextControllerUpdateCurrentResponse,
    AdminOrganizationContextControllerUpdateCurrentError,
    AdminOrganizationContextControllerUpdateCurrentVariables
  >({
    mutationFn: (
      variables: AdminOrganizationContextControllerUpdateCurrentVariables,
    ) =>
      fetchAdminOrganizationContextControllerUpdateCurrent({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminOrganizationControllerCreateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminOrganizationControllerCreateResponse = {
  data?: Schemas.AdminOrganizationHomeImageResDto;
  statusCode?: number;
};

export type AdminOrganizationControllerCreateRequestBody = {
  /**
   * Organization name
   */
  name: string;
  /**
   * State
   */
  state: string;
  /**
   * Street address
   */
  address: string;
  /**
   * City
   */
  city?: string | null;
  /**
   * ZIP code
   */
  zipCode?: string | null;
  /**
   * Email address used as Reply-To on outbound messages for this organization
   */
  replyToEmail?: string;
  /**
   * Show the promotional banner on the customer app home screen. Headline is required when this is true.
   */
  showPromotionalBanner?: boolean;
  /**
   * Promotional banner headline. Required when showPromotionalBanner is true.
   *
   * @maxLength 120
   * @example Up to $300 OFF
   */
  homeTitle?: string | null;
  /**
   * Optional subline under the promotional banner headline.
   *
   * @maxLength 200
   * @example 2024/25 season service cost
   */
  homeSubtitle?: string | null;
  /**
   * Optional JPEG or PNG, 3:1 ratio, at least 1200 × 400 pixels, up to 5 MB
   *
   * @format binary
   */
  file?: Blob;
};

export type AdminOrganizationControllerCreateVariables = {
  body: AdminOrganizationControllerCreateRequestBody;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminOrganizationControllerCreate = (
  variables: AdminOrganizationControllerCreateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminOrganizationControllerCreateResponse,
    AdminOrganizationControllerCreateError,
    AdminOrganizationControllerCreateRequestBody,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/organizations",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminOrganizationControllerCreate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminOrganizationControllerCreateResponse,
      AdminOrganizationControllerCreateError,
      AdminOrganizationControllerCreateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminOrganizationControllerCreateResponse,
    AdminOrganizationControllerCreateError,
    AdminOrganizationControllerCreateVariables
  >({
    mutationFn: (variables: AdminOrganizationControllerCreateVariables) =>
      fetchAdminOrganizationControllerCreate({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminOrganizationControllerFindManyQueryParams = {
  limit?: number;
  page?: number;
  /**
   * Search by organization name, city, state or address
   */
  search?: string;
  /**
   * Filter by active status
   */
  isActive?: boolean;
  /**
   * Sort field
   */
  sort?: "name" | "location" | "isActive" | "createdAt" | "updatedAt";
  /**
   * Sort direction
   */
  order?: "asc" | "desc";
};

export type AdminOrganizationControllerFindManyError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminOrganizationControllerFindManyResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.AdminOrganizationResDto[];
  };

export type AdminOrganizationControllerFindManyVariables = {
  queryParams?: AdminOrganizationControllerFindManyQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminOrganizationControllerFindMany = (
  variables: AdminOrganizationControllerFindManyVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminOrganizationControllerFindManyResponse,
    AdminOrganizationControllerFindManyError,
    undefined,
    {},
    AdminOrganizationControllerFindManyQueryParams,
    {}
  >({
    url: "/api/v1/admin/organizations",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminOrganizationControllerFindMany = <
  TData = AdminOrganizationControllerFindManyResponse,
>(
  variables: AdminOrganizationControllerFindManyVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminOrganizationControllerFindManyResponse,
      AdminOrganizationControllerFindManyError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminOrganizationControllerFindManyResponse,
    AdminOrganizationControllerFindManyError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/organizations",
      operationId: "adminOrganizationControllerFindMany",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminOrganizationControllerFindMany(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminOrganizationControllerFindByIdPathParams = {
  id: string;
};

export type AdminOrganizationControllerFindByIdError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminOrganizationControllerFindByIdResponse = {
  data?: Schemas.AdminOrganizationDetailResDto;
  statusCode?: number;
};

export type AdminOrganizationControllerFindByIdVariables = {
  pathParams: AdminOrganizationControllerFindByIdPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminOrganizationControllerFindById = (
  variables: AdminOrganizationControllerFindByIdVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminOrganizationControllerFindByIdResponse,
    AdminOrganizationControllerFindByIdError,
    undefined,
    {},
    {},
    AdminOrganizationControllerFindByIdPathParams
  >({
    url: "/api/v1/admin/organizations/{id}",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminOrganizationControllerFindById = <
  TData = AdminOrganizationControllerFindByIdResponse,
>(
  variables: AdminOrganizationControllerFindByIdVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminOrganizationControllerFindByIdResponse,
      AdminOrganizationControllerFindByIdError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminOrganizationControllerFindByIdResponse,
    AdminOrganizationControllerFindByIdError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/organizations/{id}",
      operationId: "adminOrganizationControllerFindById",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminOrganizationControllerFindById(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminOrganizationControllerUpdatePathParams = {
  id: string;
};

export type AdminOrganizationControllerUpdateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminOrganizationControllerUpdateResponse = {
  data?: Schemas.AdminOrganizationHomeImageResDto;
  statusCode?: number;
};

export type AdminOrganizationControllerUpdateRequestBody = {
  /**
   * Organization name
   */
  name: string;
  /**
   * Street address
   */
  address: string;
  /**
   * State
   */
  state: string;
  /**
   * City. Omit, or pass an empty string, to clear it.
   */
  city?: string | null;
  /**
   * ZIP code. Omit, or pass an empty string, to clear it.
   */
  zipCode?: string | null;
  /**
   * Email address used as Reply-To on outbound messages for this organization. Pass an empty string to clear.
   */
  replyToEmail?: string | null;
  /**
   * Whether the organization is active
   */
  isActive?: boolean;
  /**
   * Show the promotional banner on the customer app home screen. Headline is required when this is true.
   */
  showPromotionalBanner?: boolean;
  /**
   * Promotional banner headline. Required when the banner is shown. Omit to leave it unchanged. Pass an empty string to clear it.
   *
   * @maxLength 120
   * @example Up to $300 OFF
   */
  homeTitle?: string | null;
  /**
   * Optional subline under the headline. Omit to leave it unchanged. Pass an empty string to clear it.
   *
   * @maxLength 200
   * @example 2024/25 season service cost
   */
  homeSubtitle?: string | null;
  /**
   * Remove the current banner image and keep the headline. Cannot be combined with a new file.
   */
  removeHomeImage?: boolean;
  /**
   * Optional JPEG or PNG, 3:1 ratio, at least 1200 × 400 pixels, up to 5 MB. Omit to leave the current image.
   *
   * @format binary
   */
  file?: Blob;
};

export type AdminOrganizationControllerUpdateVariables = {
  body: AdminOrganizationControllerUpdateRequestBody;
  pathParams: AdminOrganizationControllerUpdatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminOrganizationControllerUpdate = (
  variables: AdminOrganizationControllerUpdateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminOrganizationControllerUpdateResponse,
    AdminOrganizationControllerUpdateError,
    AdminOrganizationControllerUpdateRequestBody,
    {},
    {},
    AdminOrganizationControllerUpdatePathParams
  >({
    url: "/api/v1/admin/organizations/{id}",
    method: "patch",
    ...variables,
    signal,
  });

export const useAdminOrganizationControllerUpdate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminOrganizationControllerUpdateResponse,
      AdminOrganizationControllerUpdateError,
      AdminOrganizationControllerUpdateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminOrganizationControllerUpdateResponse,
    AdminOrganizationControllerUpdateError,
    AdminOrganizationControllerUpdateVariables
  >({
    mutationFn: (variables: AdminOrganizationControllerUpdateVariables) =>
      fetchAdminOrganizationControllerUpdate({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminOrganizationControllerSoftDeletePathParams = {
  id: string;
};

export type AdminOrganizationControllerSoftDeleteError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminOrganizationControllerSoftDeleteResponse = {
  data?: Schemas.AdminOrganizationResDto;
  statusCode?: number;
};

export type AdminOrganizationControllerSoftDeleteVariables = {
  pathParams: AdminOrganizationControllerSoftDeletePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminOrganizationControllerSoftDelete = (
  variables: AdminOrganizationControllerSoftDeleteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminOrganizationControllerSoftDeleteResponse,
    AdminOrganizationControllerSoftDeleteError,
    undefined,
    {},
    {},
    AdminOrganizationControllerSoftDeletePathParams
  >({
    url: "/api/v1/admin/organizations/{id}",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminOrganizationControllerSoftDelete = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminOrganizationControllerSoftDeleteResponse,
      AdminOrganizationControllerSoftDeleteError,
      AdminOrganizationControllerSoftDeleteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminOrganizationControllerSoftDeleteResponse,
    AdminOrganizationControllerSoftDeleteError,
    AdminOrganizationControllerSoftDeleteVariables
  >({
    mutationFn: (variables: AdminOrganizationControllerSoftDeleteVariables) =>
      fetchAdminOrganizationControllerSoftDelete({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminOrganizationControllerUpdateStatusPathParams = {
  id: string;
};

export type AdminOrganizationControllerUpdateStatusError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminOrganizationControllerUpdateStatusResponse = {
  data?: Schemas.AdminOrganizationResDto;
  statusCode?: number;
};

export type AdminOrganizationControllerUpdateStatusVariables = {
  body: Schemas.AdminPatchOrganizationStatusReqDto;
  pathParams: AdminOrganizationControllerUpdateStatusPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminOrganizationControllerUpdateStatus = (
  variables: AdminOrganizationControllerUpdateStatusVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminOrganizationControllerUpdateStatusResponse,
    AdminOrganizationControllerUpdateStatusError,
    Schemas.AdminPatchOrganizationStatusReqDto,
    {},
    {},
    AdminOrganizationControllerUpdateStatusPathParams
  >({
    url: "/api/v1/admin/organizations/{id}/status",
    method: "patch",
    ...variables,
    signal,
  });

export const useAdminOrganizationControllerUpdateStatus = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminOrganizationControllerUpdateStatusResponse,
      AdminOrganizationControllerUpdateStatusError,
      AdminOrganizationControllerUpdateStatusVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminOrganizationControllerUpdateStatusResponse,
    AdminOrganizationControllerUpdateStatusError,
    AdminOrganizationControllerUpdateStatusVariables
  >({
    mutationFn: (variables: AdminOrganizationControllerUpdateStatusVariables) =>
      fetchAdminOrganizationControllerUpdateStatus({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminOrganizationControllerSetHomeImagePathParams = {
  id: string;
};

export type AdminOrganizationControllerSetHomeImageError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminOrganizationControllerSetHomeImageResponse = {
  data?: Schemas.AdminOrganizationHomeImageResDto;
  statusCode?: number;
};

export type AdminOrganizationControllerSetHomeImageRequestBody = {
  /**
   * Show the promotional banner on the customer app home screen. Headline is required when this is true.
   */
  showPromotionalBanner?: boolean;
  /**
   * Promotional banner headline. Required when the banner is shown. Omit to leave it unchanged. Pass an empty string to clear it.
   *
   * @maxLength 120
   * @example Up to $300 OFF
   */
  homeTitle?: string | null;
  /**
   * Optional subline under the headline. Omit to leave it unchanged. Pass an empty string to clear it.
   *
   * @maxLength 200
   * @example 2024/25 season service cost
   */
  homeSubtitle?: string | null;
  /**
   * Remove the current banner image and keep the headline. Cannot be combined with a new file.
   */
  removeHomeImage?: boolean;
  /**
   * Optional JPEG or PNG, 3:1 ratio, at least 1200 × 400 pixels, up to 5 MB. Omit to leave the current image.
   *
   * @format binary
   */
  file?: Blob;
};

export type AdminOrganizationControllerSetHomeImageVariables = {
  body?: AdminOrganizationControllerSetHomeImageRequestBody;
  pathParams: AdminOrganizationControllerSetHomeImagePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminOrganizationControllerSetHomeImage = (
  variables: AdminOrganizationControllerSetHomeImageVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminOrganizationControllerSetHomeImageResponse,
    AdminOrganizationControllerSetHomeImageError,
    AdminOrganizationControllerSetHomeImageRequestBody,
    {},
    {},
    AdminOrganizationControllerSetHomeImagePathParams
  >({
    url: "/api/v1/admin/organizations/{id}/home-image",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminOrganizationControllerSetHomeImage = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminOrganizationControllerSetHomeImageResponse,
      AdminOrganizationControllerSetHomeImageError,
      AdminOrganizationControllerSetHomeImageVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminOrganizationControllerSetHomeImageResponse,
    AdminOrganizationControllerSetHomeImageError,
    AdminOrganizationControllerSetHomeImageVariables
  >({
    mutationFn: (variables: AdminOrganizationControllerSetHomeImageVariables) =>
      fetchAdminOrganizationControllerSetHomeImage({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminOrganizationControllerClearHomeImagePathParams = {
  id: string;
};

export type AdminOrganizationControllerClearHomeImageError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminOrganizationControllerClearHomeImageResponse = {
  data?: Schemas.AdminOrganizationHomeImageResDto;
  statusCode?: number;
};

export type AdminOrganizationControllerClearHomeImageVariables = {
  pathParams: AdminOrganizationControllerClearHomeImagePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminOrganizationControllerClearHomeImage = (
  variables: AdminOrganizationControllerClearHomeImageVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminOrganizationControllerClearHomeImageResponse,
    AdminOrganizationControllerClearHomeImageError,
    undefined,
    {},
    {},
    AdminOrganizationControllerClearHomeImagePathParams
  >({
    url: "/api/v1/admin/organizations/{id}/home-image",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminOrganizationControllerClearHomeImage = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminOrganizationControllerClearHomeImageResponse,
      AdminOrganizationControllerClearHomeImageError,
      AdminOrganizationControllerClearHomeImageVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminOrganizationControllerClearHomeImageResponse,
    AdminOrganizationControllerClearHomeImageError,
    AdminOrganizationControllerClearHomeImageVariables
  >({
    mutationFn: (
      variables: AdminOrganizationControllerClearHomeImageVariables,
    ) =>
      fetchAdminOrganizationControllerClearHomeImage({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type CustomerDeleteOrchestratorControllerDeletePathParams = {
  customerId: string;
};

export type CustomerDeleteOrchestratorControllerDeleteError =
  Fetcher.ErrorWrapper<undefined>;

export type CustomerDeleteOrchestratorControllerDeleteResponse = {
  data?: Schemas.AdminCustomerResDto;
  statusCode?: number;
};

export type CustomerDeleteOrchestratorControllerDeleteVariables = {
  pathParams: CustomerDeleteOrchestratorControllerDeletePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchCustomerDeleteOrchestratorControllerDelete = (
  variables: CustomerDeleteOrchestratorControllerDeleteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    CustomerDeleteOrchestratorControllerDeleteResponse,
    CustomerDeleteOrchestratorControllerDeleteError,
    undefined,
    {},
    {},
    CustomerDeleteOrchestratorControllerDeletePathParams
  >({
    url: "/api/v1/admin/customers/{customerId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useCustomerDeleteOrchestratorControllerDelete = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CustomerDeleteOrchestratorControllerDeleteResponse,
      CustomerDeleteOrchestratorControllerDeleteError,
      CustomerDeleteOrchestratorControllerDeleteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    CustomerDeleteOrchestratorControllerDeleteResponse,
    CustomerDeleteOrchestratorControllerDeleteError,
    CustomerDeleteOrchestratorControllerDeleteVariables
  >({
    mutationFn: (
      variables: CustomerDeleteOrchestratorControllerDeleteVariables,
    ) =>
      fetchCustomerDeleteOrchestratorControllerDelete({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminCustomerControllerGetOnePathParams = {
  customerId: string;
};

export type AdminCustomerControllerGetOneError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminCustomerControllerGetOneResponse = {
  data?: Schemas.AdminCustomerResDto;
  statusCode?: number;
};

export type AdminCustomerControllerGetOneVariables = {
  pathParams: AdminCustomerControllerGetOnePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminCustomerControllerGetOne = (
  variables: AdminCustomerControllerGetOneVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminCustomerControllerGetOneResponse,
    AdminCustomerControllerGetOneError,
    undefined,
    {},
    {},
    AdminCustomerControllerGetOnePathParams
  >({
    url: "/api/v1/admin/customers/{customerId}",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminCustomerControllerGetOne = <
  TData = AdminCustomerControllerGetOneResponse,
>(
  variables: AdminCustomerControllerGetOneVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminCustomerControllerGetOneResponse,
      AdminCustomerControllerGetOneError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminCustomerControllerGetOneResponse,
    AdminCustomerControllerGetOneError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/customers/{customerId}",
      operationId: "adminCustomerControllerGetOne",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminCustomerControllerGetOne(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminCustomerControllerUpdatePathParams = {
  customerId: string;
};

export type AdminCustomerControllerUpdateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminCustomerControllerUpdateResponse = {
  data?: Schemas.AdminCustomerResDto;
  statusCode?: number;
};

export type AdminCustomerControllerUpdateVariables = {
  body?: Schemas.AdminPutCustomerReqDto;
  pathParams: AdminCustomerControllerUpdatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminCustomerControllerUpdate = (
  variables: AdminCustomerControllerUpdateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminCustomerControllerUpdateResponse,
    AdminCustomerControllerUpdateError,
    Schemas.AdminPutCustomerReqDto,
    {},
    {},
    AdminCustomerControllerUpdatePathParams
  >({
    url: "/api/v1/admin/customers/{customerId}",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminCustomerControllerUpdate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminCustomerControllerUpdateResponse,
      AdminCustomerControllerUpdateError,
      AdminCustomerControllerUpdateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminCustomerControllerUpdateResponse,
    AdminCustomerControllerUpdateError,
    AdminCustomerControllerUpdateVariables
  >({
    mutationFn: (variables: AdminCustomerControllerUpdateVariables) =>
      fetchAdminCustomerControllerUpdate({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type CustomerDeleteOrchestratorControllerDeleteManyError =
  Fetcher.ErrorWrapper<undefined>;

export type CustomerDeleteOrchestratorControllerDeleteManyResponse = {
  data?: Schemas.AdminCustomerResDto[];
  statusCode?: number;
};

export type CustomerDeleteOrchestratorControllerDeleteManyRequestBody =
  string[];

export type CustomerDeleteOrchestratorControllerDeleteManyVariables = {
  body?: CustomerDeleteOrchestratorControllerDeleteManyRequestBody;
} & EstabrookContext["fetcherOptions"];

export const fetchCustomerDeleteOrchestratorControllerDeleteMany = (
  variables: CustomerDeleteOrchestratorControllerDeleteManyVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    CustomerDeleteOrchestratorControllerDeleteManyResponse,
    CustomerDeleteOrchestratorControllerDeleteManyError,
    CustomerDeleteOrchestratorControllerDeleteManyRequestBody,
    {},
    {},
    {}
  >({ url: "/api/v1/admin/customers", method: "delete", ...variables, signal });

export const useCustomerDeleteOrchestratorControllerDeleteMany = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CustomerDeleteOrchestratorControllerDeleteManyResponse,
      CustomerDeleteOrchestratorControllerDeleteManyError,
      CustomerDeleteOrchestratorControllerDeleteManyVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    CustomerDeleteOrchestratorControllerDeleteManyResponse,
    CustomerDeleteOrchestratorControllerDeleteManyError,
    CustomerDeleteOrchestratorControllerDeleteManyVariables
  >({
    mutationFn: (
      variables: CustomerDeleteOrchestratorControllerDeleteManyVariables,
    ) =>
      fetchCustomerDeleteOrchestratorControllerDeleteMany({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminCustomerControllerGetAllQueryParams = {
  limit?: number;
  page?: number;
  /**
   * The sort
   */
  sort?: "fullName" | "email";
  /**
   * The order
   */
  order?: "asc" | "desc";
  /**
   * The search
   */
  search?: string;
  /**
   * The email confirmed
   */
  emailConfirmed?: boolean;
  /**
   * IANA timezone used to format CSV datetime columns. Defaults to UTC when omitted.
   */
  timezone?: string;
};

export type AdminCustomerControllerGetAllError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminCustomerControllerGetAllResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.AdminCustomerResDto[];
  };

export type AdminCustomerControllerGetAllVariables = {
  queryParams?: AdminCustomerControllerGetAllQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminCustomerControllerGetAll = (
  variables: AdminCustomerControllerGetAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminCustomerControllerGetAllResponse,
    AdminCustomerControllerGetAllError,
    undefined,
    {},
    AdminCustomerControllerGetAllQueryParams,
    {}
  >({ url: "/api/v1/admin/customers", method: "get", ...variables, signal });

export const useAdminCustomerControllerGetAll = <
  TData = AdminCustomerControllerGetAllResponse,
>(
  variables: AdminCustomerControllerGetAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminCustomerControllerGetAllResponse,
      AdminCustomerControllerGetAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminCustomerControllerGetAllResponse,
    AdminCustomerControllerGetAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/customers",
      operationId: "adminCustomerControllerGetAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminCustomerControllerGetAll(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminCustomerControllerCreateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminCustomerControllerCreateResponse = {
  data?: Schemas.AdminCustomerResDto;
  statusCode?: number;
};

export type AdminCustomerControllerCreateVariables = {
  body: Schemas.AdminPostCustomerReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminCustomerControllerCreate = (
  variables: AdminCustomerControllerCreateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminCustomerControllerCreateResponse,
    AdminCustomerControllerCreateError,
    Schemas.AdminPostCustomerReqDto,
    {},
    {},
    {}
  >({ url: "/api/v1/admin/customers", method: "post", ...variables, signal });

export const useAdminCustomerControllerCreate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminCustomerControllerCreateResponse,
      AdminCustomerControllerCreateError,
      AdminCustomerControllerCreateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminCustomerControllerCreateResponse,
    AdminCustomerControllerCreateError,
    AdminCustomerControllerCreateVariables
  >({
    mutationFn: (variables: AdminCustomerControllerCreateVariables) =>
      fetchAdminCustomerControllerCreate({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type AdminRegionV2ControllerGetRegionsError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerGetRegionsResponse = {
  data?: Schemas.AdminRegionResDto[];
  statusCode?: number;
};

export type AdminRegionV2ControllerGetRegionsVariables =
  EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerGetRegions = (
  variables: AdminRegionV2ControllerGetRegionsVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRegionV2ControllerGetRegionsResponse,
    AdminRegionV2ControllerGetRegionsError,
    undefined,
    {},
    {},
    {}
  >({ url: "/api/v2/admin/regions", method: "get", ...variables, signal });

export const useAdminRegionV2ControllerGetRegions = <
  TData = AdminRegionV2ControllerGetRegionsResponse,
>(
  variables: AdminRegionV2ControllerGetRegionsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminRegionV2ControllerGetRegionsResponse,
      AdminRegionV2ControllerGetRegionsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminRegionV2ControllerGetRegionsResponse,
    AdminRegionV2ControllerGetRegionsError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/regions",
      operationId: "adminRegionV2ControllerGetRegions",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminRegionV2ControllerGetRegions(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminRegionV2ControllerCreateRegionError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerCreateRegionResponse = {
  data?: Schemas.AdminRegionResDto;
  statusCode?: number;
};

export type AdminRegionV2ControllerCreateRegionVariables = {
  body: Schemas.AdminPostRegionReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerCreateRegion = (
  variables: AdminRegionV2ControllerCreateRegionVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRegionV2ControllerCreateRegionResponse,
    AdminRegionV2ControllerCreateRegionError,
    Schemas.AdminPostRegionReqDto,
    {},
    {},
    {}
  >({ url: "/api/v2/admin/regions", method: "post", ...variables, signal });

export const useAdminRegionV2ControllerCreateRegion = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRegionV2ControllerCreateRegionResponse,
      AdminRegionV2ControllerCreateRegionError,
      AdminRegionV2ControllerCreateRegionVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRegionV2ControllerCreateRegionResponse,
    AdminRegionV2ControllerCreateRegionError,
    AdminRegionV2ControllerCreateRegionVariables
  >({
    mutationFn: (variables: AdminRegionV2ControllerCreateRegionVariables) =>
      fetchAdminRegionV2ControllerCreateRegion({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRegionV2ControllerGetRegionsForSelectOptionsError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerGetRegionsForSelectOptionsResponse = {
  data?: Schemas.AdminRegionSelectOptionsResDto[];
  statusCode?: number;
};

export type AdminRegionV2ControllerGetRegionsForSelectOptionsVariables =
  EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerGetRegionsForSelectOptions = (
  variables: AdminRegionV2ControllerGetRegionsForSelectOptionsVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRegionV2ControllerGetRegionsForSelectOptionsResponse,
    AdminRegionV2ControllerGetRegionsForSelectOptionsError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/api/v2/admin/regions/select-options",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminRegionV2ControllerGetRegionsForSelectOptions = <
  TData = AdminRegionV2ControllerGetRegionsForSelectOptionsResponse,
>(
  variables: AdminRegionV2ControllerGetRegionsForSelectOptionsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminRegionV2ControllerGetRegionsForSelectOptionsResponse,
      AdminRegionV2ControllerGetRegionsForSelectOptionsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminRegionV2ControllerGetRegionsForSelectOptionsResponse,
    AdminRegionV2ControllerGetRegionsForSelectOptionsError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/regions/select-options",
      operationId: "adminRegionV2ControllerGetRegionsForSelectOptions",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminRegionV2ControllerGetRegionsForSelectOptions(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminRegionV2ControllerGetRegionsPagedQueryParams = {
  limit?: number;
  page?: number;
};

export type AdminRegionV2ControllerGetRegionsPagedError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerGetRegionsPagedResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.AdminRegionResDto[];
  };

export type AdminRegionV2ControllerGetRegionsPagedVariables = {
  queryParams?: AdminRegionV2ControllerGetRegionsPagedQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerGetRegionsPaged = (
  variables: AdminRegionV2ControllerGetRegionsPagedVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRegionV2ControllerGetRegionsPagedResponse,
    AdminRegionV2ControllerGetRegionsPagedError,
    undefined,
    {},
    AdminRegionV2ControllerGetRegionsPagedQueryParams,
    {}
  >({
    url: "/api/v2/admin/regions/paged",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminRegionV2ControllerGetRegionsPaged = <
  TData = AdminRegionV2ControllerGetRegionsPagedResponse,
>(
  variables: AdminRegionV2ControllerGetRegionsPagedVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminRegionV2ControllerGetRegionsPagedResponse,
      AdminRegionV2ControllerGetRegionsPagedError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminRegionV2ControllerGetRegionsPagedResponse,
    AdminRegionV2ControllerGetRegionsPagedError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/regions/paged",
      operationId: "adminRegionV2ControllerGetRegionsPaged",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminRegionV2ControllerGetRegionsPaged(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminRegionV2ControllerAddLocationsToRegionPathParams = {
  id: string;
};

export type AdminRegionV2ControllerAddLocationsToRegionError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerAddLocationsToRegionResponse = {
  data?: Schemas.AdminRegionResDto;
  statusCode?: number;
};

export type AdminRegionV2ControllerAddLocationsToRegionVariables = {
  body: Schemas.AdminPatchRegionLocationsReqDto;
  pathParams: AdminRegionV2ControllerAddLocationsToRegionPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerAddLocationsToRegion = (
  variables: AdminRegionV2ControllerAddLocationsToRegionVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRegionV2ControllerAddLocationsToRegionResponse,
    AdminRegionV2ControllerAddLocationsToRegionError,
    Schemas.AdminPatchRegionLocationsReqDto,
    {},
    {},
    AdminRegionV2ControllerAddLocationsToRegionPathParams
  >({
    url: "/api/v2/admin/regions/{id}/locations",
    method: "patch",
    ...variables,
    signal,
  });

export const useAdminRegionV2ControllerAddLocationsToRegion = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRegionV2ControllerAddLocationsToRegionResponse,
      AdminRegionV2ControllerAddLocationsToRegionError,
      AdminRegionV2ControllerAddLocationsToRegionVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRegionV2ControllerAddLocationsToRegionResponse,
    AdminRegionV2ControllerAddLocationsToRegionError,
    AdminRegionV2ControllerAddLocationsToRegionVariables
  >({
    mutationFn: (
      variables: AdminRegionV2ControllerAddLocationsToRegionVariables,
    ) =>
      fetchAdminRegionV2ControllerAddLocationsToRegion({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRegionV2ControllerDeleteRegionLocationsPathParams = {
  id: string;
};

export type AdminRegionV2ControllerDeleteRegionLocationsError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerDeleteRegionLocationsResponse = {
  data?: Schemas.AdminRegionResDto;
  statusCode?: number;
};

export type AdminRegionV2ControllerDeleteRegionLocationsVariables = {
  body: Schemas.AdminRegionDeleteLocationsReqDto;
  pathParams: AdminRegionV2ControllerDeleteRegionLocationsPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerDeleteRegionLocations = (
  variables: AdminRegionV2ControllerDeleteRegionLocationsVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRegionV2ControllerDeleteRegionLocationsResponse,
    AdminRegionV2ControllerDeleteRegionLocationsError,
    Schemas.AdminRegionDeleteLocationsReqDto,
    {},
    {},
    AdminRegionV2ControllerDeleteRegionLocationsPathParams
  >({
    url: "/api/v2/admin/regions/{id}/locations",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminRegionV2ControllerDeleteRegionLocations = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRegionV2ControllerDeleteRegionLocationsResponse,
      AdminRegionV2ControllerDeleteRegionLocationsError,
      AdminRegionV2ControllerDeleteRegionLocationsVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRegionV2ControllerDeleteRegionLocationsResponse,
    AdminRegionV2ControllerDeleteRegionLocationsError,
    AdminRegionV2ControllerDeleteRegionLocationsVariables
  >({
    mutationFn: (
      variables: AdminRegionV2ControllerDeleteRegionLocationsVariables,
    ) =>
      fetchAdminRegionV2ControllerDeleteRegionLocations({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRegionV2ControllerTransferLocationsToRegionPathParams = {
  id: string;
};

export type AdminRegionV2ControllerTransferLocationsToRegionError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerTransferLocationsToRegionResponse = {
  data?: Schemas.TransferRegionResDto;
  statusCode?: number;
};

export type AdminRegionV2ControllerTransferLocationsToRegionVariables = {
  body: Schemas.AdminPostRegionLocationsReqDto;
  pathParams: AdminRegionV2ControllerTransferLocationsToRegionPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerTransferLocationsToRegion = (
  variables: AdminRegionV2ControllerTransferLocationsToRegionVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRegionV2ControllerTransferLocationsToRegionResponse,
    AdminRegionV2ControllerTransferLocationsToRegionError,
    Schemas.AdminPostRegionLocationsReqDto,
    {},
    {},
    AdminRegionV2ControllerTransferLocationsToRegionPathParams
  >({
    url: "/api/v2/admin/regions/{id}/locations",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminRegionV2ControllerTransferLocationsToRegion = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRegionV2ControllerTransferLocationsToRegionResponse,
      AdminRegionV2ControllerTransferLocationsToRegionError,
      AdminRegionV2ControllerTransferLocationsToRegionVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRegionV2ControllerTransferLocationsToRegionResponse,
    AdminRegionV2ControllerTransferLocationsToRegionError,
    AdminRegionV2ControllerTransferLocationsToRegionVariables
  >({
    mutationFn: (
      variables: AdminRegionV2ControllerTransferLocationsToRegionVariables,
    ) =>
      fetchAdminRegionV2ControllerTransferLocationsToRegion({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRegionV2ControllerTriggerEstCalculationForRegionError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerTriggerEstCalculationForRegionVariables =
  EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerTriggerEstCalculationForRegion = (
  variables: AdminRegionV2ControllerTriggerEstCalculationForRegionVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    undefined,
    AdminRegionV2ControllerTriggerEstCalculationForRegionError,
    undefined,
    {},
    {},
    {}
  >({ url: "/api/v2/admin/regions/est", method: "post", ...variables, signal });

export const useAdminRegionV2ControllerTriggerEstCalculationForRegion = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      AdminRegionV2ControllerTriggerEstCalculationForRegionError,
      AdminRegionV2ControllerTriggerEstCalculationForRegionVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    undefined,
    AdminRegionV2ControllerTriggerEstCalculationForRegionError,
    AdminRegionV2ControllerTriggerEstCalculationForRegionVariables
  >({
    mutationFn: (
      variables: AdminRegionV2ControllerTriggerEstCalculationForRegionVariables,
    ) =>
      fetchAdminRegionV2ControllerTriggerEstCalculationForRegion({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRegionV2ControllerDeleteLocationsFromManyRegionsError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerDeleteLocationsFromManyRegionsResponse = {
  data?: Schemas.AdminRegionResDto[];
  statusCode?: number;
};

export type AdminRegionV2ControllerDeleteLocationsFromManyRegionsVariables = {
  body: Schemas.AdminRegionDeleteLocationsReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerDeleteLocationsFromManyRegions = (
  variables: AdminRegionV2ControllerDeleteLocationsFromManyRegionsVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRegionV2ControllerDeleteLocationsFromManyRegionsResponse,
    AdminRegionV2ControllerDeleteLocationsFromManyRegionsError,
    Schemas.AdminRegionDeleteLocationsReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v2/admin/regions/locations",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminRegionV2ControllerDeleteLocationsFromManyRegions = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRegionV2ControllerDeleteLocationsFromManyRegionsResponse,
      AdminRegionV2ControllerDeleteLocationsFromManyRegionsError,
      AdminRegionV2ControllerDeleteLocationsFromManyRegionsVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRegionV2ControllerDeleteLocationsFromManyRegionsResponse,
    AdminRegionV2ControllerDeleteLocationsFromManyRegionsError,
    AdminRegionV2ControllerDeleteLocationsFromManyRegionsVariables
  >({
    mutationFn: (
      variables: AdminRegionV2ControllerDeleteLocationsFromManyRegionsVariables,
    ) =>
      fetchAdminRegionV2ControllerDeleteLocationsFromManyRegions({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRegionV2ControllerUpdateRegionLocationsSequencePathParams = {
  id: string;
};

export type AdminRegionV2ControllerUpdateRegionLocationsSequenceError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerUpdateRegionLocationsSequenceResponse = {
  data?: Schemas.AdminRegionResDto;
  statusCode?: number;
};

export type AdminRegionV2ControllerUpdateRegionLocationsSequenceVariables = {
  body: Schemas.AdminRegionLocationReSequenceReqDto;
  pathParams: AdminRegionV2ControllerUpdateRegionLocationsSequencePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerUpdateRegionLocationsSequence = (
  variables: AdminRegionV2ControllerUpdateRegionLocationsSequenceVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRegionV2ControllerUpdateRegionLocationsSequenceResponse,
    AdminRegionV2ControllerUpdateRegionLocationsSequenceError,
    Schemas.AdminRegionLocationReSequenceReqDto,
    {},
    {},
    AdminRegionV2ControllerUpdateRegionLocationsSequencePathParams
  >({
    url: "/api/v2/admin/regions/{id}/locations/sequence",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminRegionV2ControllerUpdateRegionLocationsSequence = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRegionV2ControllerUpdateRegionLocationsSequenceResponse,
      AdminRegionV2ControllerUpdateRegionLocationsSequenceError,
      AdminRegionV2ControllerUpdateRegionLocationsSequenceVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRegionV2ControllerUpdateRegionLocationsSequenceResponse,
    AdminRegionV2ControllerUpdateRegionLocationsSequenceError,
    AdminRegionV2ControllerUpdateRegionLocationsSequenceVariables
  >({
    mutationFn: (
      variables: AdminRegionV2ControllerUpdateRegionLocationsSequenceVariables,
    ) =>
      fetchAdminRegionV2ControllerUpdateRegionLocationsSequence({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRegionV2ControllerUpdateRegionServiceTimePathParams = {
  id: string;
};

export type AdminRegionV2ControllerUpdateRegionServiceTimeError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerUpdateRegionServiceTimeResponse = {
  data?: Schemas.AdminRegionResDto;
  statusCode?: number;
};

export type AdminRegionV2ControllerUpdateRegionServiceTimeVariables = {
  body: Schemas.AdminPutRegionServiceTimeReqDto;
  pathParams: AdminRegionV2ControllerUpdateRegionServiceTimePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerUpdateRegionServiceTime = (
  variables: AdminRegionV2ControllerUpdateRegionServiceTimeVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRegionV2ControllerUpdateRegionServiceTimeResponse,
    AdminRegionV2ControllerUpdateRegionServiceTimeError,
    Schemas.AdminPutRegionServiceTimeReqDto,
    {},
    {},
    AdminRegionV2ControllerUpdateRegionServiceTimePathParams
  >({
    url: "/api/v2/admin/regions/{id}/service-time",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminRegionV2ControllerUpdateRegionServiceTime = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRegionV2ControllerUpdateRegionServiceTimeResponse,
      AdminRegionV2ControllerUpdateRegionServiceTimeError,
      AdminRegionV2ControllerUpdateRegionServiceTimeVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRegionV2ControllerUpdateRegionServiceTimeResponse,
    AdminRegionV2ControllerUpdateRegionServiceTimeError,
    AdminRegionV2ControllerUpdateRegionServiceTimeVariables
  >({
    mutationFn: (
      variables: AdminRegionV2ControllerUpdateRegionServiceTimeVariables,
    ) =>
      fetchAdminRegionV2ControllerUpdateRegionServiceTime({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRegionV2ControllerGetSuggestedDriversPathParams = {
  id: string;
};

export type AdminRegionV2ControllerGetSuggestedDriversError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerGetSuggestedDriversResponse = {
  data?: Schemas.AdminRegionSuggestedDriversResDto[];
  statusCode?: number;
};

export type AdminRegionV2ControllerGetSuggestedDriversVariables = {
  pathParams: AdminRegionV2ControllerGetSuggestedDriversPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerGetSuggestedDrivers = (
  variables: AdminRegionV2ControllerGetSuggestedDriversVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRegionV2ControllerGetSuggestedDriversResponse,
    AdminRegionV2ControllerGetSuggestedDriversError,
    undefined,
    {},
    {},
    AdminRegionV2ControllerGetSuggestedDriversPathParams
  >({
    url: "/api/v2/admin/regions/{id}/suggested-drivers",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminRegionV2ControllerGetSuggestedDrivers = <
  TData = AdminRegionV2ControllerGetSuggestedDriversResponse,
>(
  variables: AdminRegionV2ControllerGetSuggestedDriversVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminRegionV2ControllerGetSuggestedDriversResponse,
      AdminRegionV2ControllerGetSuggestedDriversError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminRegionV2ControllerGetSuggestedDriversResponse,
    AdminRegionV2ControllerGetSuggestedDriversError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/regions/{id}/suggested-drivers",
      operationId: "adminRegionV2ControllerGetSuggestedDrivers",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminRegionV2ControllerGetSuggestedDrivers(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminRegionV2ControllerGetRegionByIdPathParams = {
  id: string;
};

export type AdminRegionV2ControllerGetRegionByIdError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerGetRegionByIdResponse = {
  data?: Schemas.AdminRegionResDto;
  statusCode?: number;
};

export type AdminRegionV2ControllerGetRegionByIdVariables = {
  pathParams: AdminRegionV2ControllerGetRegionByIdPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerGetRegionById = (
  variables: AdminRegionV2ControllerGetRegionByIdVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRegionV2ControllerGetRegionByIdResponse,
    AdminRegionV2ControllerGetRegionByIdError,
    undefined,
    {},
    {},
    AdminRegionV2ControllerGetRegionByIdPathParams
  >({ url: "/api/v2/admin/regions/{id}", method: "get", ...variables, signal });

export const useAdminRegionV2ControllerGetRegionById = <
  TData = AdminRegionV2ControllerGetRegionByIdResponse,
>(
  variables: AdminRegionV2ControllerGetRegionByIdVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminRegionV2ControllerGetRegionByIdResponse,
      AdminRegionV2ControllerGetRegionByIdError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminRegionV2ControllerGetRegionByIdResponse,
    AdminRegionV2ControllerGetRegionByIdError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/regions/{id}",
      operationId: "adminRegionV2ControllerGetRegionById",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminRegionV2ControllerGetRegionById(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminRegionV2ControllerDeleteRegionByIdPathParams = {
  id: string;
};

export type AdminRegionV2ControllerDeleteRegionByIdError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerDeleteRegionByIdResponse = {
  data?: Schemas.AdminRegionResDto;
  statusCode?: number;
};

export type AdminRegionV2ControllerDeleteRegionByIdVariables = {
  pathParams: AdminRegionV2ControllerDeleteRegionByIdPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerDeleteRegionById = (
  variables: AdminRegionV2ControllerDeleteRegionByIdVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRegionV2ControllerDeleteRegionByIdResponse,
    AdminRegionV2ControllerDeleteRegionByIdError,
    undefined,
    {},
    {},
    AdminRegionV2ControllerDeleteRegionByIdPathParams
  >({
    url: "/api/v2/admin/regions/{id}",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminRegionV2ControllerDeleteRegionById = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRegionV2ControllerDeleteRegionByIdResponse,
      AdminRegionV2ControllerDeleteRegionByIdError,
      AdminRegionV2ControllerDeleteRegionByIdVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRegionV2ControllerDeleteRegionByIdResponse,
    AdminRegionV2ControllerDeleteRegionByIdError,
    AdminRegionV2ControllerDeleteRegionByIdVariables
  >({
    mutationFn: (variables: AdminRegionV2ControllerDeleteRegionByIdVariables) =>
      fetchAdminRegionV2ControllerDeleteRegionById({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRegionV2ControllerUpdateRegionPathParams = {
  id: string;
};

export type AdminRegionV2ControllerUpdateRegionError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerUpdateRegionResponse = {
  data?: Schemas.AdminRegionResDto;
  statusCode?: number;
};

export type AdminRegionV2ControllerUpdateRegionVariables = {
  body: Schemas.AdminPutRegionReqDto;
  pathParams: AdminRegionV2ControllerUpdateRegionPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerUpdateRegion = (
  variables: AdminRegionV2ControllerUpdateRegionVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRegionV2ControllerUpdateRegionResponse,
    AdminRegionV2ControllerUpdateRegionError,
    Schemas.AdminPutRegionReqDto,
    {},
    {},
    AdminRegionV2ControllerUpdateRegionPathParams
  >({ url: "/api/v2/admin/regions/{id}", method: "put", ...variables, signal });

export const useAdminRegionV2ControllerUpdateRegion = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRegionV2ControllerUpdateRegionResponse,
      AdminRegionV2ControllerUpdateRegionError,
      AdminRegionV2ControllerUpdateRegionVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRegionV2ControllerUpdateRegionResponse,
    AdminRegionV2ControllerUpdateRegionError,
    AdminRegionV2ControllerUpdateRegionVariables
  >({
    mutationFn: (variables: AdminRegionV2ControllerUpdateRegionVariables) =>
      fetchAdminRegionV2ControllerUpdateRegion({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRegionV2ControllerUpdateRegionPriorityPathParams = {
  id: string;
};

export type AdminRegionV2ControllerUpdateRegionPriorityError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRegionV2ControllerUpdateRegionPriorityResponse = {
  data?: Schemas.AdminRegionResDto[];
  statusCode?: number;
};

export type AdminRegionV2ControllerUpdateRegionPriorityVariables = {
  body: Schemas.AdminPutRegionPriorityReqDto;
  pathParams: AdminRegionV2ControllerUpdateRegionPriorityPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRegionV2ControllerUpdateRegionPriority = (
  variables: AdminRegionV2ControllerUpdateRegionPriorityVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRegionV2ControllerUpdateRegionPriorityResponse,
    AdminRegionV2ControllerUpdateRegionPriorityError,
    Schemas.AdminPutRegionPriorityReqDto,
    {},
    {},
    AdminRegionV2ControllerUpdateRegionPriorityPathParams
  >({
    url: "/api/v2/admin/regions/{id}/priority",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminRegionV2ControllerUpdateRegionPriority = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRegionV2ControllerUpdateRegionPriorityResponse,
      AdminRegionV2ControllerUpdateRegionPriorityError,
      AdminRegionV2ControllerUpdateRegionPriorityVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRegionV2ControllerUpdateRegionPriorityResponse,
    AdminRegionV2ControllerUpdateRegionPriorityError,
    AdminRegionV2ControllerUpdateRegionPriorityVariables
  >({
    mutationFn: (
      variables: AdminRegionV2ControllerUpdateRegionPriorityVariables,
    ) =>
      fetchAdminRegionV2ControllerUpdateRegionPriority({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type VehicleControllerGetAllQueryParams = {
  /**
   * @default 10
   */
  limit?: number;
  page?: number;
  /**
   * Search
   */
  search?: string;
  /**
   * Sort
   */
  sort?: string;
  /**
   * Order
   */
  order?: "asc" | "desc";
  /**
   * IANA timezone used to format CSV datetime columns. Defaults to UTC when omitted.
   */
  timezone?: string;
};

export type VehicleControllerGetAllError = Fetcher.ErrorWrapper<undefined>;

export type VehicleControllerGetAllResponse = Schemas.PaginatedResponse & {
  data?: Schemas.AdminVehicleResDto[];
};

export type VehicleControllerGetAllVariables = {
  queryParams?: VehicleControllerGetAllQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchVehicleControllerGetAll = (
  variables: VehicleControllerGetAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    VehicleControllerGetAllResponse,
    VehicleControllerGetAllError,
    undefined,
    {},
    VehicleControllerGetAllQueryParams,
    {}
  >({ url: "/api/v1/admin/vehicles", method: "get", ...variables, signal });

export const useVehicleControllerGetAll = <
  TData = VehicleControllerGetAllResponse,
>(
  variables: VehicleControllerGetAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      VehicleControllerGetAllResponse,
      VehicleControllerGetAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    VehicleControllerGetAllResponse,
    VehicleControllerGetAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/vehicles",
      operationId: "vehicleControllerGetAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchVehicleControllerGetAll({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type VehicleControllerCreateError = Fetcher.ErrorWrapper<undefined>;

export type VehicleControllerCreateResponse = {
  data?: Schemas.AdminVehicleResDto;
  statusCode?: number;
};

export type VehicleControllerCreateVariables = {
  body: Schemas.AdminPostVehicleReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchVehicleControllerCreate = (
  variables: VehicleControllerCreateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    VehicleControllerCreateResponse,
    VehicleControllerCreateError,
    Schemas.AdminPostVehicleReqDto,
    {},
    {},
    {}
  >({ url: "/api/v1/admin/vehicles", method: "post", ...variables, signal });

export const useVehicleControllerCreate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      VehicleControllerCreateResponse,
      VehicleControllerCreateError,
      VehicleControllerCreateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    VehicleControllerCreateResponse,
    VehicleControllerCreateError,
    VehicleControllerCreateVariables
  >({
    mutationFn: (variables: VehicleControllerCreateVariables) =>
      fetchVehicleControllerCreate({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type VehicleControllerGetAllCsvQueryParams = {
  /**
   * @default 10
   */
  limit?: number;
  page?: number;
  /**
   * Search
   */
  search?: string;
  /**
   * Sort
   */
  sort?: string;
  /**
   * Order
   */
  order?: "asc" | "desc";
  /**
   * IANA timezone used to format CSV datetime columns. Defaults to UTC when omitted.
   */
  timezone?: string;
};

export type VehicleControllerGetAllCsvError = Fetcher.ErrorWrapper<undefined>;

export type VehicleControllerGetAllCsvResponse = {
  data?: Schemas.String;
  statusCode?: number;
};

export type VehicleControllerGetAllCsvVariables = {
  queryParams?: VehicleControllerGetAllCsvQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchVehicleControllerGetAllCsv = (
  variables: VehicleControllerGetAllCsvVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    VehicleControllerGetAllCsvResponse,
    VehicleControllerGetAllCsvError,
    undefined,
    {},
    VehicleControllerGetAllCsvQueryParams,
    {}
  >({ url: "/api/v1/admin/vehicles/csv", method: "get", ...variables, signal });

export const useVehicleControllerGetAllCsv = <
  TData = VehicleControllerGetAllCsvResponse,
>(
  variables: VehicleControllerGetAllCsvVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      VehicleControllerGetAllCsvResponse,
      VehicleControllerGetAllCsvError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    VehicleControllerGetAllCsvResponse,
    VehicleControllerGetAllCsvError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/vehicles/csv",
      operationId: "vehicleControllerGetAllCsv",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchVehicleControllerGetAllCsv(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type VehicleControllerGetUnassignedVehiclesError =
  Fetcher.ErrorWrapper<undefined>;

export type VehicleControllerGetUnassignedVehiclesResponse = {
  data?: Schemas.AdminVehicleResDto[];
  statusCode?: number;
};

export type VehicleControllerGetUnassignedVehiclesVariables =
  EstabrookContext["fetcherOptions"];

export const fetchVehicleControllerGetUnassignedVehicles = (
  variables: VehicleControllerGetUnassignedVehiclesVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    VehicleControllerGetUnassignedVehiclesResponse,
    VehicleControllerGetUnassignedVehiclesError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/vehicles/unassigned",
    method: "get",
    ...variables,
    signal,
  });

export const useVehicleControllerGetUnassignedVehicles = <
  TData = VehicleControllerGetUnassignedVehiclesResponse,
>(
  variables: VehicleControllerGetUnassignedVehiclesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      VehicleControllerGetUnassignedVehiclesResponse,
      VehicleControllerGetUnassignedVehiclesError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    VehicleControllerGetUnassignedVehiclesResponse,
    VehicleControllerGetUnassignedVehiclesError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/vehicles/unassigned",
      operationId: "vehicleControllerGetUnassignedVehicles",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchVehicleControllerGetUnassignedVehicles(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type VehicleControllerGetVehicleByIdPathParams = {
  vehicleId: string;
};

export type VehicleControllerGetVehicleByIdError =
  Fetcher.ErrorWrapper<undefined>;

export type VehicleControllerGetVehicleByIdResponse = {
  data?: Schemas.AdminVehicleResDto;
  statusCode?: number;
};

export type VehicleControllerGetVehicleByIdVariables = {
  pathParams: VehicleControllerGetVehicleByIdPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchVehicleControllerGetVehicleById = (
  variables: VehicleControllerGetVehicleByIdVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    VehicleControllerGetVehicleByIdResponse,
    VehicleControllerGetVehicleByIdError,
    undefined,
    {},
    {},
    VehicleControllerGetVehicleByIdPathParams
  >({
    url: "/api/v1/admin/vehicles/{vehicleId}",
    method: "get",
    ...variables,
    signal,
  });

export const useVehicleControllerGetVehicleById = <
  TData = VehicleControllerGetVehicleByIdResponse,
>(
  variables: VehicleControllerGetVehicleByIdVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      VehicleControllerGetVehicleByIdResponse,
      VehicleControllerGetVehicleByIdError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    VehicleControllerGetVehicleByIdResponse,
    VehicleControllerGetVehicleByIdError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/vehicles/{vehicleId}",
      operationId: "vehicleControllerGetVehicleById",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchVehicleControllerGetVehicleById(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type VehicleControllerUpdatePathParams = {
  vehicleId: string;
};

export type VehicleControllerUpdateError = Fetcher.ErrorWrapper<undefined>;

export type VehicleControllerUpdateResponse = {
  data?: Schemas.AdminVehicleResDto;
  statusCode?: number;
};

export type VehicleControllerUpdateVariables = {
  body: Schemas.AdminPutVehicleReqDto;
  pathParams: VehicleControllerUpdatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchVehicleControllerUpdate = (
  variables: VehicleControllerUpdateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    VehicleControllerUpdateResponse,
    VehicleControllerUpdateError,
    Schemas.AdminPutVehicleReqDto,
    {},
    {},
    VehicleControllerUpdatePathParams
  >({
    url: "/api/v1/admin/vehicles/{vehicleId}",
    method: "put",
    ...variables,
    signal,
  });

export const useVehicleControllerUpdate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      VehicleControllerUpdateResponse,
      VehicleControllerUpdateError,
      VehicleControllerUpdateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    VehicleControllerUpdateResponse,
    VehicleControllerUpdateError,
    VehicleControllerUpdateVariables
  >({
    mutationFn: (variables: VehicleControllerUpdateVariables) =>
      fetchVehicleControllerUpdate({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type VehicleControllerDeletePathParams = {
  vehicleId: string;
};

export type VehicleControllerDeleteError = Fetcher.ErrorWrapper<undefined>;

export type VehicleControllerDeleteResponse = {
  data?: Schemas.AdminVehicleResDto;
  statusCode?: number;
};

export type VehicleControllerDeleteVariables = {
  pathParams: VehicleControllerDeletePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchVehicleControllerDelete = (
  variables: VehicleControllerDeleteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    VehicleControllerDeleteResponse,
    VehicleControllerDeleteError,
    undefined,
    {},
    {},
    VehicleControllerDeletePathParams
  >({
    url: "/api/v1/admin/vehicles/{vehicleId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useVehicleControllerDelete = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      VehicleControllerDeleteResponse,
      VehicleControllerDeleteError,
      VehicleControllerDeleteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    VehicleControllerDeleteResponse,
    VehicleControllerDeleteError,
    VehicleControllerDeleteVariables
  >({
    mutationFn: (variables: VehicleControllerDeleteVariables) =>
      fetchVehicleControllerDelete({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type VehicleTypeControllerFindManyQueryParams = {
  /**
   * @default 10
   */
  limit?: number;
  page?: number;
  /**
   * Search
   */
  search?: string;
  /**
   * Sort
   */
  sort?: string;
  /**
   * Order
   */
  order?: "asc" | "desc";
};

export type VehicleTypeControllerFindManyError =
  Fetcher.ErrorWrapper<undefined>;

export type VehicleTypeControllerFindManyResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.AdminVehicleTypeResDto[];
  };

export type VehicleTypeControllerFindManyVariables = {
  queryParams?: VehicleTypeControllerFindManyQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchVehicleTypeControllerFindMany = (
  variables: VehicleTypeControllerFindManyVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    VehicleTypeControllerFindManyResponse,
    VehicleTypeControllerFindManyError,
    undefined,
    {},
    VehicleTypeControllerFindManyQueryParams,
    {}
  >({
    url: "/api/v1/admin/vehicle-types",
    method: "get",
    ...variables,
    signal,
  });

export const useVehicleTypeControllerFindMany = <
  TData = VehicleTypeControllerFindManyResponse,
>(
  variables: VehicleTypeControllerFindManyVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      VehicleTypeControllerFindManyResponse,
      VehicleTypeControllerFindManyError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    VehicleTypeControllerFindManyResponse,
    VehicleTypeControllerFindManyError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/vehicle-types",
      operationId: "vehicleTypeControllerFindMany",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchVehicleTypeControllerFindMany(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type VehicleTypeControllerCreateVehicleTypeError =
  Fetcher.ErrorWrapper<undefined>;

export type VehicleTypeControllerCreateVehicleTypeResponse = {
  data?: Schemas.AdminVehicleTypeResDto;
  statusCode?: number;
};

export type VehicleTypeControllerCreateVehicleTypeVariables = {
  body: Schemas.AdminPostVehicleTypeReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchVehicleTypeControllerCreateVehicleType = (
  variables: VehicleTypeControllerCreateVehicleTypeVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    VehicleTypeControllerCreateVehicleTypeResponse,
    VehicleTypeControllerCreateVehicleTypeError,
    Schemas.AdminPostVehicleTypeReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/vehicle-types",
    method: "post",
    ...variables,
    signal,
  });

export const useVehicleTypeControllerCreateVehicleType = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      VehicleTypeControllerCreateVehicleTypeResponse,
      VehicleTypeControllerCreateVehicleTypeError,
      VehicleTypeControllerCreateVehicleTypeVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    VehicleTypeControllerCreateVehicleTypeResponse,
    VehicleTypeControllerCreateVehicleTypeError,
    VehicleTypeControllerCreateVehicleTypeVariables
  >({
    mutationFn: (variables: VehicleTypeControllerCreateVehicleTypeVariables) =>
      fetchVehicleTypeControllerCreateVehicleType({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type VehicleTypeControllerGetVehicleTypePathParams = {
  typeId: number;
};

export type VehicleTypeControllerGetVehicleTypeError =
  Fetcher.ErrorWrapper<undefined>;

export type VehicleTypeControllerGetVehicleTypeResponse = {
  data?: Schemas.AdminVehicleTypeResDto;
  statusCode?: number;
};

export type VehicleTypeControllerGetVehicleTypeVariables = {
  pathParams: VehicleTypeControllerGetVehicleTypePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchVehicleTypeControllerGetVehicleType = (
  variables: VehicleTypeControllerGetVehicleTypeVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    VehicleTypeControllerGetVehicleTypeResponse,
    VehicleTypeControllerGetVehicleTypeError,
    undefined,
    {},
    {},
    VehicleTypeControllerGetVehicleTypePathParams
  >({
    url: "/api/v1/admin/vehicle-types/{typeId}",
    method: "get",
    ...variables,
    signal,
  });

export const useVehicleTypeControllerGetVehicleType = <
  TData = VehicleTypeControllerGetVehicleTypeResponse,
>(
  variables: VehicleTypeControllerGetVehicleTypeVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      VehicleTypeControllerGetVehicleTypeResponse,
      VehicleTypeControllerGetVehicleTypeError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    VehicleTypeControllerGetVehicleTypeResponse,
    VehicleTypeControllerGetVehicleTypeError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/vehicle-types/{typeId}",
      operationId: "vehicleTypeControllerGetVehicleType",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchVehicleTypeControllerGetVehicleType(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type VehicleTypeControllerDeleteVehicleTypePathParams = {
  typeId: number;
};

export type VehicleTypeControllerDeleteVehicleTypeError =
  Fetcher.ErrorWrapper<undefined>;

export type VehicleTypeControllerDeleteVehicleTypeResponse = {
  data?: Schemas.AdminVehicleTypeResDto;
  statusCode?: number;
};

export type VehicleTypeControllerDeleteVehicleTypeVariables = {
  pathParams: VehicleTypeControllerDeleteVehicleTypePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchVehicleTypeControllerDeleteVehicleType = (
  variables: VehicleTypeControllerDeleteVehicleTypeVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    VehicleTypeControllerDeleteVehicleTypeResponse,
    VehicleTypeControllerDeleteVehicleTypeError,
    undefined,
    {},
    {},
    VehicleTypeControllerDeleteVehicleTypePathParams
  >({
    url: "/api/v1/admin/vehicle-types/{typeId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useVehicleTypeControllerDeleteVehicleType = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      VehicleTypeControllerDeleteVehicleTypeResponse,
      VehicleTypeControllerDeleteVehicleTypeError,
      VehicleTypeControllerDeleteVehicleTypeVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    VehicleTypeControllerDeleteVehicleTypeResponse,
    VehicleTypeControllerDeleteVehicleTypeError,
    VehicleTypeControllerDeleteVehicleTypeVariables
  >({
    mutationFn: (variables: VehicleTypeControllerDeleteVehicleTypeVariables) =>
      fetchVehicleTypeControllerDeleteVehicleType({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type VehicleTypeControllerUpdateVehicleTypePathParams = {
  typeId: number;
};

export type VehicleTypeControllerUpdateVehicleTypeError =
  Fetcher.ErrorWrapper<undefined>;

export type VehicleTypeControllerUpdateVehicleTypeResponse = {
  data?: Schemas.AdminVehicleTypeResDto;
  statusCode?: number;
};

export type VehicleTypeControllerUpdateVehicleTypeVariables = {
  body: Schemas.AdminPutVehicleTypeReqDto;
  pathParams: VehicleTypeControllerUpdateVehicleTypePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchVehicleTypeControllerUpdateVehicleType = (
  variables: VehicleTypeControllerUpdateVehicleTypeVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    VehicleTypeControllerUpdateVehicleTypeResponse,
    VehicleTypeControllerUpdateVehicleTypeError,
    Schemas.AdminPutVehicleTypeReqDto,
    {},
    {},
    VehicleTypeControllerUpdateVehicleTypePathParams
  >({
    url: "/api/v1/admin/vehicle-types/{typeId}",
    method: "put",
    ...variables,
    signal,
  });

export const useVehicleTypeControllerUpdateVehicleType = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      VehicleTypeControllerUpdateVehicleTypeResponse,
      VehicleTypeControllerUpdateVehicleTypeError,
      VehicleTypeControllerUpdateVehicleTypeVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    VehicleTypeControllerUpdateVehicleTypeResponse,
    VehicleTypeControllerUpdateVehicleTypeError,
    VehicleTypeControllerUpdateVehicleTypeVariables
  >({
    mutationFn: (variables: VehicleTypeControllerUpdateVehicleTypeVariables) =>
      fetchVehicleTypeControllerUpdateVehicleType({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerGetRoutesQueryParams = {
  /**
   * YYYY-MM-DD. Defaults to today.
   */
  date?: string;
  /**
   * Include locations with orders in response
   */
  includeLocations?: boolean;
  /**
   * Filter by order statuses. If not provided, all statuses are included.
   */
  orderStatuses?: ("skipped" | "regular" | "delayed" | "urgent")[];
  /**
   * Location type
   */
  locationTypes?: ("onDemand" | "annual")[];
};

export type AdminRouteV2ControllerGetRoutesError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerGetRoutesResponse = {
  data?: Schemas.AdminGetRoutesResDto[];
  statusCode?: number;
};

export type AdminRouteV2ControllerGetRoutesVariables = {
  queryParams?: AdminRouteV2ControllerGetRoutesQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerGetRoutes = (
  variables: AdminRouteV2ControllerGetRoutesVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerGetRoutesResponse,
    AdminRouteV2ControllerGetRoutesError,
    undefined,
    {},
    AdminRouteV2ControllerGetRoutesQueryParams,
    {}
  >({ url: "/api/v2/admin/routes", method: "get", ...variables, signal });

export const useAdminRouteV2ControllerGetRoutes = <
  TData = AdminRouteV2ControllerGetRoutesResponse,
>(
  variables: AdminRouteV2ControllerGetRoutesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminRouteV2ControllerGetRoutesResponse,
      AdminRouteV2ControllerGetRoutesError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminRouteV2ControllerGetRoutesResponse,
    AdminRouteV2ControllerGetRoutesError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/routes",
      operationId: "adminRouteV2ControllerGetRoutes",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminRouteV2ControllerGetRoutes(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminRouteV2ControllerGetRoutesPagedQueryParams = {
  /**
   * YYYY-MM-DD. Defaults to today.
   */
  date?: string;
  /**
   * Include locations with orders in response
   */
  includeLocations?: boolean;
  /**
   * Filter by order statuses. If not provided, all statuses are included.
   */
  orderStatuses?: ("skipped" | "regular" | "delayed" | "urgent")[];
  /**
   * Location type
   */
  locationTypes?: ("onDemand" | "annual")[];
  /**
   * Only these routes of the date, e.g. to refresh the routes a live update named.
   */
  ids?: string[];
  limit?: number;
  page?: number;
};

export type AdminRouteV2ControllerGetRoutesPagedError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerGetRoutesPagedResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.AdminGetRoutesResDto[];
  };

export type AdminRouteV2ControllerGetRoutesPagedVariables = {
  queryParams?: AdminRouteV2ControllerGetRoutesPagedQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerGetRoutesPaged = (
  variables: AdminRouteV2ControllerGetRoutesPagedVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerGetRoutesPagedResponse,
    AdminRouteV2ControllerGetRoutesPagedError,
    undefined,
    {},
    AdminRouteV2ControllerGetRoutesPagedQueryParams,
    {}
  >({ url: "/api/v2/admin/routes/paged", method: "get", ...variables, signal });

export const useAdminRouteV2ControllerGetRoutesPaged = <
  TData = AdminRouteV2ControllerGetRoutesPagedResponse,
>(
  variables: AdminRouteV2ControllerGetRoutesPagedVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminRouteV2ControllerGetRoutesPagedResponse,
      AdminRouteV2ControllerGetRoutesPagedError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminRouteV2ControllerGetRoutesPagedResponse,
    AdminRouteV2ControllerGetRoutesPagedError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/routes/paged",
      operationId: "adminRouteV2ControllerGetRoutesPaged",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminRouteV2ControllerGetRoutesPaged(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminRouteV2ControllerGetRoutesStatusesPerMonthQueryParams = {
  /**
   * The year
   *
   * @minimum 2024
   */
  year?: number;
  /**
   * The month
   *
   * @minimum 1
   * @maximum 12
   */
  month?: number;
};

export type AdminRouteV2ControllerGetRoutesStatusesPerMonthError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerGetRoutesStatusesPerMonthResponse = {
  data?: Schemas.AdminGetRouteStatusesPerMonthResDto[];
  statusCode?: number;
};

export type AdminRouteV2ControllerGetRoutesStatusesPerMonthVariables = {
  queryParams?: AdminRouteV2ControllerGetRoutesStatusesPerMonthQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerGetRoutesStatusesPerMonth = (
  variables: AdminRouteV2ControllerGetRoutesStatusesPerMonthVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerGetRoutesStatusesPerMonthResponse,
    AdminRouteV2ControllerGetRoutesStatusesPerMonthError,
    undefined,
    {},
    AdminRouteV2ControllerGetRoutesStatusesPerMonthQueryParams,
    {}
  >({
    url: "/api/v2/admin/routes/states",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerGetRoutesStatusesPerMonth = <
  TData = AdminRouteV2ControllerGetRoutesStatusesPerMonthResponse,
>(
  variables: AdminRouteV2ControllerGetRoutesStatusesPerMonthVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminRouteV2ControllerGetRoutesStatusesPerMonthResponse,
      AdminRouteV2ControllerGetRoutesStatusesPerMonthError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminRouteV2ControllerGetRoutesStatusesPerMonthResponse,
    AdminRouteV2ControllerGetRoutesStatusesPerMonthError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/routes/states",
      operationId: "adminRouteV2ControllerGetRoutesStatusesPerMonth",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminRouteV2ControllerGetRoutesStatusesPerMonth(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminRouteV2ControllerGetDateOfBuildRoutesError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerGetDateOfBuildRoutesResponse = {
  data?: Schemas.AdminGetDateOfRoutesByStateResDto;
  statusCode?: number;
};

export type AdminRouteV2ControllerGetDateOfBuildRoutesVariables =
  EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerGetDateOfBuildRoutes = (
  variables: AdminRouteV2ControllerGetDateOfBuildRoutesVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerGetDateOfBuildRoutesResponse,
    AdminRouteV2ControllerGetDateOfBuildRoutesError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/api/v2/admin/routes/build-date",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerGetDateOfBuildRoutes = <
  TData = AdminRouteV2ControllerGetDateOfBuildRoutesResponse,
>(
  variables: AdminRouteV2ControllerGetDateOfBuildRoutesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminRouteV2ControllerGetDateOfBuildRoutesResponse,
      AdminRouteV2ControllerGetDateOfBuildRoutesError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminRouteV2ControllerGetDateOfBuildRoutesResponse,
    AdminRouteV2ControllerGetDateOfBuildRoutesError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/routes/build-date",
      operationId: "adminRouteV2ControllerGetDateOfBuildRoutes",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminRouteV2ControllerGetDateOfBuildRoutes(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminRouteV2ControllerGetDateOfLiveRoutesError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerGetDateOfLiveRoutesResponse = {
  data?: Schemas.AdminGetDateOfRoutesByStateResDto;
  statusCode?: number;
};

export type AdminRouteV2ControllerGetDateOfLiveRoutesVariables =
  EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerGetDateOfLiveRoutes = (
  variables: AdminRouteV2ControllerGetDateOfLiveRoutesVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerGetDateOfLiveRoutesResponse,
    AdminRouteV2ControllerGetDateOfLiveRoutesError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/api/v2/admin/routes/live-date",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerGetDateOfLiveRoutes = <
  TData = AdminRouteV2ControllerGetDateOfLiveRoutesResponse,
>(
  variables: AdminRouteV2ControllerGetDateOfLiveRoutesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminRouteV2ControllerGetDateOfLiveRoutesResponse,
      AdminRouteV2ControllerGetDateOfLiveRoutesError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminRouteV2ControllerGetDateOfLiveRoutesResponse,
    AdminRouteV2ControllerGetDateOfLiveRoutesError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/routes/live-date",
      operationId: "adminRouteV2ControllerGetDateOfLiveRoutes",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminRouteV2ControllerGetDateOfLiveRoutes(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminRouteV2ControllerFindRouteLocationsPathParams = {
  date: string;
};

export type AdminRouteV2ControllerFindRouteLocationsQueryParams = {
  /**
   * Search term
   */
  searchTerm?: string;
};

export type AdminRouteV2ControllerFindRouteLocationsError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerFindRouteLocationsResponse = {
  data?: Schemas.AdminGetRoutesLocationsResDto[];
  statusCode?: number;
};

export type AdminRouteV2ControllerFindRouteLocationsVariables = {
  pathParams: AdminRouteV2ControllerFindRouteLocationsPathParams;
  queryParams?: AdminRouteV2ControllerFindRouteLocationsQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerFindRouteLocations = (
  variables: AdminRouteV2ControllerFindRouteLocationsVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerFindRouteLocationsResponse,
    AdminRouteV2ControllerFindRouteLocationsError,
    undefined,
    {},
    AdminRouteV2ControllerFindRouteLocationsQueryParams,
    AdminRouteV2ControllerFindRouteLocationsPathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/locations",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerFindRouteLocations = <
  TData = AdminRouteV2ControllerFindRouteLocationsResponse,
>(
  variables: AdminRouteV2ControllerFindRouteLocationsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminRouteV2ControllerFindRouteLocationsResponse,
      AdminRouteV2ControllerFindRouteLocationsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminRouteV2ControllerFindRouteLocationsResponse,
    AdminRouteV2ControllerFindRouteLocationsError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/routes/date/{date}/locations",
      operationId: "adminRouteV2ControllerFindRouteLocations",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminRouteV2ControllerFindRouteLocations(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminRouteV2ControllerGetRouteNotificationsPerDatePathParams = {
  date: string;
};

export type AdminRouteV2ControllerGetRouteNotificationsPerDateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerGetRouteNotificationsPerDateResponse = {
  data?: Schemas.AdminGetDateRouteNotificationsResDto;
  statusCode?: number;
};

export type AdminRouteV2ControllerGetRouteNotificationsPerDateVariables = {
  pathParams: AdminRouteV2ControllerGetRouteNotificationsPerDatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerGetRouteNotificationsPerDate = (
  variables: AdminRouteV2ControllerGetRouteNotificationsPerDateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerGetRouteNotificationsPerDateResponse,
    AdminRouteV2ControllerGetRouteNotificationsPerDateError,
    undefined,
    {},
    {},
    AdminRouteV2ControllerGetRouteNotificationsPerDatePathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/notifications",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerGetRouteNotificationsPerDate = <
  TData = AdminRouteV2ControllerGetRouteNotificationsPerDateResponse,
>(
  variables: AdminRouteV2ControllerGetRouteNotificationsPerDateVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminRouteV2ControllerGetRouteNotificationsPerDateResponse,
      AdminRouteV2ControllerGetRouteNotificationsPerDateError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminRouteV2ControllerGetRouteNotificationsPerDateResponse,
    AdminRouteV2ControllerGetRouteNotificationsPerDateError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/routes/date/{date}/notifications",
      operationId: "adminRouteV2ControllerGetRouteNotificationsPerDate",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminRouteV2ControllerGetRouteNotificationsPerDate(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminRouteV2ControllerUpdateRouteNotificationsPerDatePathParams = {
  date: string;
};

export type AdminRouteV2ControllerUpdateRouteNotificationsPerDateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerUpdateRouteNotificationsPerDateResponse = {
  data?: Schemas.AdminGetDateRouteNotificationsResDto;
  statusCode?: number;
};

export type AdminRouteV2ControllerUpdateRouteNotificationsPerDateVariables = {
  body: Schemas.AdminPutRouteNotificationsReqDto;
  pathParams: AdminRouteV2ControllerUpdateRouteNotificationsPerDatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerUpdateRouteNotificationsPerDate = (
  variables: AdminRouteV2ControllerUpdateRouteNotificationsPerDateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerUpdateRouteNotificationsPerDateResponse,
    AdminRouteV2ControllerUpdateRouteNotificationsPerDateError,
    Schemas.AdminPutRouteNotificationsReqDto,
    {},
    {},
    AdminRouteV2ControllerUpdateRouteNotificationsPerDatePathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/notifications",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerUpdateRouteNotificationsPerDate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerUpdateRouteNotificationsPerDateResponse,
      AdminRouteV2ControllerUpdateRouteNotificationsPerDateError,
      AdminRouteV2ControllerUpdateRouteNotificationsPerDateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerUpdateRouteNotificationsPerDateResponse,
    AdminRouteV2ControllerUpdateRouteNotificationsPerDateError,
    AdminRouteV2ControllerUpdateRouteNotificationsPerDateVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerUpdateRouteNotificationsPerDateVariables,
    ) =>
      fetchAdminRouteV2ControllerUpdateRouteNotificationsPerDate({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerCreateVirtualRouteError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerCreateVirtualRouteResponse = {
  data?: Schemas.AdminGetRoutesResDto;
  statusCode?: number;
};

export type AdminRouteV2ControllerCreateVirtualRouteVariables = {
  body: Schemas.AdminPostRouteReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerCreateVirtualRoute = (
  variables: AdminRouteV2ControllerCreateVirtualRouteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerCreateVirtualRouteResponse,
    AdminRouteV2ControllerCreateVirtualRouteError,
    Schemas.AdminPostRouteReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v2/admin/routes/virtual",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerCreateVirtualRoute = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerCreateVirtualRouteResponse,
      AdminRouteV2ControllerCreateVirtualRouteError,
      AdminRouteV2ControllerCreateVirtualRouteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerCreateVirtualRouteResponse,
    AdminRouteV2ControllerCreateVirtualRouteError,
    AdminRouteV2ControllerCreateVirtualRouteVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerCreateVirtualRouteVariables,
    ) =>
      fetchAdminRouteV2ControllerCreateVirtualRoute({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerDeleteVirtualRoutePathParams = {
  routeId: string;
};

export type AdminRouteV2ControllerDeleteVirtualRouteError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerDeleteVirtualRouteVariables = {
  pathParams: AdminRouteV2ControllerDeleteVirtualRoutePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerDeleteVirtualRoute = (
  variables: AdminRouteV2ControllerDeleteVirtualRouteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    undefined,
    AdminRouteV2ControllerDeleteVirtualRouteError,
    undefined,
    {},
    {},
    AdminRouteV2ControllerDeleteVirtualRoutePathParams
  >({
    url: "/api/v2/admin/routes/virtual/{routeId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerDeleteVirtualRoute = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      AdminRouteV2ControllerDeleteVirtualRouteError,
      AdminRouteV2ControllerDeleteVirtualRouteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    undefined,
    AdminRouteV2ControllerDeleteVirtualRouteError,
    AdminRouteV2ControllerDeleteVirtualRouteVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerDeleteVirtualRouteVariables,
    ) =>
      fetchAdminRouteV2ControllerDeleteVirtualRoute({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerTransferLocationToRoutePathParams = {
  date: string;
};

export type AdminRouteV2ControllerTransferLocationToRouteError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerTransferLocationToRouteResponse = {
  data?: Schemas.AdminGetRoutesResDto[];
  statusCode?: number;
};

export type AdminRouteV2ControllerTransferLocationToRouteVariables = {
  body: Schemas.AdminPostTransferLocationsToRouteReqDto;
  pathParams: AdminRouteV2ControllerTransferLocationToRoutePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerTransferLocationToRoute = (
  variables: AdminRouteV2ControllerTransferLocationToRouteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerTransferLocationToRouteResponse,
    AdminRouteV2ControllerTransferLocationToRouteError,
    Schemas.AdminPostTransferLocationsToRouteReqDto,
    {},
    {},
    AdminRouteV2ControllerTransferLocationToRoutePathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/locations/transfer",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerTransferLocationToRoute = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerTransferLocationToRouteResponse,
      AdminRouteV2ControllerTransferLocationToRouteError,
      AdminRouteV2ControllerTransferLocationToRouteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerTransferLocationToRouteResponse,
    AdminRouteV2ControllerTransferLocationToRouteError,
    AdminRouteV2ControllerTransferLocationToRouteVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerTransferLocationToRouteVariables,
    ) =>
      fetchAdminRouteV2ControllerTransferLocationToRoute({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerAddUnassignedLocationsToRoutePathParams = {
  date: string;
};

export type AdminRouteV2ControllerAddUnassignedLocationsToRouteError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerAddUnassignedLocationsToRouteResponse = {
  data?: Schemas.AdminGetRoutesResDto[];
  statusCode?: number;
};

export type AdminRouteV2ControllerAddUnassignedLocationsToRouteVariables = {
  body: Schemas.AdminPostTransferLocationsToRouteReqDto;
  pathParams: AdminRouteV2ControllerAddUnassignedLocationsToRoutePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerAddUnassignedLocationsToRoute = (
  variables: AdminRouteV2ControllerAddUnassignedLocationsToRouteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerAddUnassignedLocationsToRouteResponse,
    AdminRouteV2ControllerAddUnassignedLocationsToRouteError,
    Schemas.AdminPostTransferLocationsToRouteReqDto,
    {},
    {},
    AdminRouteV2ControllerAddUnassignedLocationsToRoutePathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/locations/unassigned",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerAddUnassignedLocationsToRoute = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerAddUnassignedLocationsToRouteResponse,
      AdminRouteV2ControllerAddUnassignedLocationsToRouteError,
      AdminRouteV2ControllerAddUnassignedLocationsToRouteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerAddUnassignedLocationsToRouteResponse,
    AdminRouteV2ControllerAddUnassignedLocationsToRouteError,
    AdminRouteV2ControllerAddUnassignedLocationsToRouteVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerAddUnassignedLocationsToRouteVariables,
    ) =>
      fetchAdminRouteV2ControllerAddUnassignedLocationsToRoute({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerReverseSelectedRouteLocationsSequencePathParams =
  {
    routeId: string;
  };

export type AdminRouteV2ControllerReverseSelectedRouteLocationsSequenceError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerReverseSelectedRouteLocationsSequenceResponse =
  {
    data?: Schemas.AdminGetRoutesResDto;
    statusCode?: number;
  };

export type AdminRouteV2ControllerReverseSelectedRouteLocationsSequenceVariables =
  {
    body: Schemas.AdminPatchRouteLocationSequenceReverseReqDto;
    pathParams: AdminRouteV2ControllerReverseSelectedRouteLocationsSequencePathParams;
  } & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerReverseSelectedRouteLocationsSequence =
  (
    variables: AdminRouteV2ControllerReverseSelectedRouteLocationsSequenceVariables,
    signal?: AbortSignal,
  ) =>
    estabrookFetch<
      AdminRouteV2ControllerReverseSelectedRouteLocationsSequenceResponse,
      AdminRouteV2ControllerReverseSelectedRouteLocationsSequenceError,
      Schemas.AdminPatchRouteLocationSequenceReverseReqDto,
      {},
      {},
      AdminRouteV2ControllerReverseSelectedRouteLocationsSequencePathParams
    >({
      url: "/api/v2/admin/routes/{routeId}/locations/reverse",
      method: "post",
      ...variables,
      signal,
    });

export const useAdminRouteV2ControllerReverseSelectedRouteLocationsSequence = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerReverseSelectedRouteLocationsSequenceResponse,
      AdminRouteV2ControllerReverseSelectedRouteLocationsSequenceError,
      AdminRouteV2ControllerReverseSelectedRouteLocationsSequenceVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerReverseSelectedRouteLocationsSequenceResponse,
    AdminRouteV2ControllerReverseSelectedRouteLocationsSequenceError,
    AdminRouteV2ControllerReverseSelectedRouteLocationsSequenceVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerReverseSelectedRouteLocationsSequenceVariables,
    ) =>
      fetchAdminRouteV2ControllerReverseSelectedRouteLocationsSequence({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerGetRouteSelectedLocationsQueryParams = {
  /**
   * YYYY-MM-DD. Date of the route. Default today!
   */
  date?: string;
};

export type AdminRouteV2ControllerGetRouteSelectedLocationsError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerGetRouteSelectedLocationsResponse = {
  data?: Schemas.AdminGetSelectedRouteLocationsResDto[];
  statusCode?: number;
};

export type AdminRouteV2ControllerGetRouteSelectedLocationsVariables = {
  body: Schemas.AdminGetSelectedRoutesLocationsInfoReqDto;
  queryParams?: AdminRouteV2ControllerGetRouteSelectedLocationsQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerGetRouteSelectedLocations = (
  variables: AdminRouteV2ControllerGetRouteSelectedLocationsVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerGetRouteSelectedLocationsResponse,
    AdminRouteV2ControllerGetRouteSelectedLocationsError,
    Schemas.AdminGetSelectedRoutesLocationsInfoReqDto,
    {},
    AdminRouteV2ControllerGetRouteSelectedLocationsQueryParams,
    {}
  >({
    url: "/api/v2/admin/routes/locations",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerGetRouteSelectedLocations = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerGetRouteSelectedLocationsResponse,
      AdminRouteV2ControllerGetRouteSelectedLocationsError,
      AdminRouteV2ControllerGetRouteSelectedLocationsVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerGetRouteSelectedLocationsResponse,
    AdminRouteV2ControllerGetRouteSelectedLocationsError,
    AdminRouteV2ControllerGetRouteSelectedLocationsVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerGetRouteSelectedLocationsVariables,
    ) =>
      fetchAdminRouteV2ControllerGetRouteSelectedLocations({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerUpdateRouteCustomerOrdersPathParams = {
  /**
   * Must be in the format YYYY-MM-DD.
   */
  date: string;
};

export type AdminRouteV2ControllerUpdateRouteCustomerOrdersError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerUpdateRouteCustomerOrdersResponse = {
  data?: Schemas.AdminGetRouteOrdersResDto[];
  statusCode?: number;
};

export type AdminRouteV2ControllerUpdateRouteCustomerOrdersVariables = {
  body: Schemas.AdminGetRoutesLocationsInfoReqDto;
  pathParams: AdminRouteV2ControllerUpdateRouteCustomerOrdersPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerUpdateRouteCustomerOrders = (
  variables: AdminRouteV2ControllerUpdateRouteCustomerOrdersVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerUpdateRouteCustomerOrdersResponse,
    AdminRouteV2ControllerUpdateRouteCustomerOrdersError,
    Schemas.AdminGetRoutesLocationsInfoReqDto,
    {},
    {},
    AdminRouteV2ControllerUpdateRouteCustomerOrdersPathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/customer-orders",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerUpdateRouteCustomerOrders = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerUpdateRouteCustomerOrdersResponse,
      AdminRouteV2ControllerUpdateRouteCustomerOrdersError,
      AdminRouteV2ControllerUpdateRouteCustomerOrdersVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerUpdateRouteCustomerOrdersResponse,
    AdminRouteV2ControllerUpdateRouteCustomerOrdersError,
    AdminRouteV2ControllerUpdateRouteCustomerOrdersVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerUpdateRouteCustomerOrdersVariables,
    ) =>
      fetchAdminRouteV2ControllerUpdateRouteCustomerOrders({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerUpdateRouteDriverStatusesPathParams = {
  /**
   * Must be in the format YYYY-MM-DD.
   */
  date: string;
};

export type AdminRouteV2ControllerUpdateRouteDriverStatusesError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerUpdateRouteDriverStatusesResponse = {
  data?: Schemas.AdminPostRouteLocationDriverStatusesResDto[];
  statusCode?: number;
};

export type AdminRouteV2ControllerUpdateRouteDriverStatusesVariables = {
  body: Schemas.AdminPostRouteLocationDriverStatusesReqDto;
  pathParams: AdminRouteV2ControllerUpdateRouteDriverStatusesPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerUpdateRouteDriverStatuses = (
  variables: AdminRouteV2ControllerUpdateRouteDriverStatusesVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerUpdateRouteDriverStatusesResponse,
    AdminRouteV2ControllerUpdateRouteDriverStatusesError,
    Schemas.AdminPostRouteLocationDriverStatusesReqDto,
    {},
    {},
    AdminRouteV2ControllerUpdateRouteDriverStatusesPathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/driver-statuses",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerUpdateRouteDriverStatuses = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerUpdateRouteDriverStatusesResponse,
      AdminRouteV2ControllerUpdateRouteDriverStatusesError,
      AdminRouteV2ControllerUpdateRouteDriverStatusesVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerUpdateRouteDriverStatusesResponse,
    AdminRouteV2ControllerUpdateRouteDriverStatusesError,
    AdminRouteV2ControllerUpdateRouteDriverStatusesVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerUpdateRouteDriverStatusesVariables,
    ) =>
      fetchAdminRouteV2ControllerUpdateRouteDriverStatuses({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerTriggerEstCalculationPathParams = {
  /**
   * Must be in the format YYYY-MM-DD.
   */
  date: string;
};

export type AdminRouteV2ControllerTriggerEstCalculationError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerTriggerEstCalculationVariables = {
  pathParams: AdminRouteV2ControllerTriggerEstCalculationPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerTriggerEstCalculation = (
  variables: AdminRouteV2ControllerTriggerEstCalculationVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    undefined,
    AdminRouteV2ControllerTriggerEstCalculationError,
    undefined,
    {},
    {},
    AdminRouteV2ControllerTriggerEstCalculationPathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/est",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerTriggerEstCalculation = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      AdminRouteV2ControllerTriggerEstCalculationError,
      AdminRouteV2ControllerTriggerEstCalculationVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    undefined,
    AdminRouteV2ControllerTriggerEstCalculationError,
    AdminRouteV2ControllerTriggerEstCalculationVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerTriggerEstCalculationVariables,
    ) =>
      fetchAdminRouteV2ControllerTriggerEstCalculation({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerUpdateRoutePriorityPathParams = {
  routeId: string;
};

export type AdminRouteV2ControllerUpdateRoutePriorityError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerUpdateRoutePriorityResponse = {
  data?: Schemas.AdminGetRoutesResDto[];
  statusCode?: number;
};

export type AdminRouteV2ControllerUpdateRoutePriorityVariables = {
  body: Schemas.AdminPutRoutePriorityReqDto;
  pathParams: AdminRouteV2ControllerUpdateRoutePriorityPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerUpdateRoutePriority = (
  variables: AdminRouteV2ControllerUpdateRoutePriorityVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerUpdateRoutePriorityResponse,
    AdminRouteV2ControllerUpdateRoutePriorityError,
    Schemas.AdminPutRoutePriorityReqDto,
    {},
    {},
    AdminRouteV2ControllerUpdateRoutePriorityPathParams
  >({
    url: "/api/v2/admin/routes/{routeId}/priority",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerUpdateRoutePriority = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerUpdateRoutePriorityResponse,
      AdminRouteV2ControllerUpdateRoutePriorityError,
      AdminRouteV2ControllerUpdateRoutePriorityVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerUpdateRoutePriorityResponse,
    AdminRouteV2ControllerUpdateRoutePriorityError,
    AdminRouteV2ControllerUpdateRoutePriorityVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerUpdateRoutePriorityVariables,
    ) =>
      fetchAdminRouteV2ControllerUpdateRoutePriority({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerUpdateRouteNotificationsPathParams = {
  routeId: string;
};

export type AdminRouteV2ControllerUpdateRouteNotificationsError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerUpdateRouteNotificationsResponse = {
  data?: Schemas.AdminGetRoutesResDto;
  statusCode?: number;
};

export type AdminRouteV2ControllerUpdateRouteNotificationsVariables = {
  body: Schemas.AdminPutRouteNotificationsReqDto;
  pathParams: AdminRouteV2ControllerUpdateRouteNotificationsPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerUpdateRouteNotifications = (
  variables: AdminRouteV2ControllerUpdateRouteNotificationsVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerUpdateRouteNotificationsResponse,
    AdminRouteV2ControllerUpdateRouteNotificationsError,
    Schemas.AdminPutRouteNotificationsReqDto,
    {},
    {},
    AdminRouteV2ControllerUpdateRouteNotificationsPathParams
  >({
    url: "/api/v2/admin/routes/{routeId}/notifications",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerUpdateRouteNotifications = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerUpdateRouteNotificationsResponse,
      AdminRouteV2ControllerUpdateRouteNotificationsError,
      AdminRouteV2ControllerUpdateRouteNotificationsVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerUpdateRouteNotificationsResponse,
    AdminRouteV2ControllerUpdateRouteNotificationsError,
    AdminRouteV2ControllerUpdateRouteNotificationsVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerUpdateRouteNotificationsVariables,
    ) =>
      fetchAdminRouteV2ControllerUpdateRouteNotifications({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerUpdateRouteStartTimePathParams = {
  routeId: string;
};

export type AdminRouteV2ControllerUpdateRouteStartTimeError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerUpdateRouteStartTimeResponse = {
  data?: Schemas.AdminRegionResDto;
  statusCode?: number;
};

export type AdminRouteV2ControllerUpdateRouteStartTimeVariables = {
  body: Schemas.AdminPatchRouteStartTimeReqDto;
  pathParams: AdminRouteV2ControllerUpdateRouteStartTimePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerUpdateRouteStartTime = (
  variables: AdminRouteV2ControllerUpdateRouteStartTimeVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerUpdateRouteStartTimeResponse,
    AdminRouteV2ControllerUpdateRouteStartTimeError,
    Schemas.AdminPatchRouteStartTimeReqDto,
    {},
    {},
    AdminRouteV2ControllerUpdateRouteStartTimePathParams
  >({
    url: "/api/v2/admin/routes/{routeId}/start-time",
    method: "patch",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerUpdateRouteStartTime = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerUpdateRouteStartTimeResponse,
      AdminRouteV2ControllerUpdateRouteStartTimeError,
      AdminRouteV2ControllerUpdateRouteStartTimeVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerUpdateRouteStartTimeResponse,
    AdminRouteV2ControllerUpdateRouteStartTimeError,
    AdminRouteV2ControllerUpdateRouteStartTimeVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerUpdateRouteStartTimeVariables,
    ) =>
      fetchAdminRouteV2ControllerUpdateRouteStartTime({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerUpdateManyRouteStartTimePathParams = {
  /**
   * Must be in the format YYYY-MM-DD.
   */
  date: string;
};

export type AdminRouteV2ControllerUpdateManyRouteStartTimeError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerUpdateManyRouteStartTimeResponse = {
  data?: Schemas.AdminRegionResDto;
  statusCode?: number;
};

export type AdminRouteV2ControllerUpdateManyRouteStartTimeVariables = {
  body: Schemas.AdminPatchRouteStartTimeReqDto;
  pathParams: AdminRouteV2ControllerUpdateManyRouteStartTimePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerUpdateManyRouteStartTime = (
  variables: AdminRouteV2ControllerUpdateManyRouteStartTimeVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerUpdateManyRouteStartTimeResponse,
    AdminRouteV2ControllerUpdateManyRouteStartTimeError,
    Schemas.AdminPatchRouteStartTimeReqDto,
    {},
    {},
    AdminRouteV2ControllerUpdateManyRouteStartTimePathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/start-time",
    method: "patch",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerUpdateManyRouteStartTime = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerUpdateManyRouteStartTimeResponse,
      AdminRouteV2ControllerUpdateManyRouteStartTimeError,
      AdminRouteV2ControllerUpdateManyRouteStartTimeVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerUpdateManyRouteStartTimeResponse,
    AdminRouteV2ControllerUpdateManyRouteStartTimeError,
    AdminRouteV2ControllerUpdateManyRouteStartTimeVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerUpdateManyRouteStartTimeVariables,
    ) =>
      fetchAdminRouteV2ControllerUpdateManyRouteStartTime({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerUpdateRoutePathParams = {
  routeId: string;
};

export type AdminRouteV2ControllerUpdateRouteError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerUpdateRouteResponse = {
  data?: Schemas.AdminPutRouteResDto;
  statusCode?: number;
};

export type AdminRouteV2ControllerUpdateRouteVariables = {
  body: Schemas.AdminPutRouteReqDto;
  pathParams: AdminRouteV2ControllerUpdateRoutePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerUpdateRoute = (
  variables: AdminRouteV2ControllerUpdateRouteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerUpdateRouteResponse,
    AdminRouteV2ControllerUpdateRouteError,
    Schemas.AdminPutRouteReqDto,
    {},
    {},
    AdminRouteV2ControllerUpdateRoutePathParams
  >({
    url: "/api/v2/admin/routes/{routeId}",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerUpdateRoute = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerUpdateRouteResponse,
      AdminRouteV2ControllerUpdateRouteError,
      AdminRouteV2ControllerUpdateRouteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerUpdateRouteResponse,
    AdminRouteV2ControllerUpdateRouteError,
    AdminRouteV2ControllerUpdateRouteVariables
  >({
    mutationFn: (variables: AdminRouteV2ControllerUpdateRouteVariables) =>
      fetchAdminRouteV2ControllerUpdateRoute({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerUpdateRouteLockStatusPathParams = {
  /**
   * Must be in the format YYYY-MM-DD.
   */
  date: string;
};

export type AdminRouteV2ControllerUpdateRouteLockStatusError =
  Fetcher.ErrorWrapper<{
    status: ClientErrorStatus | ServerErrorStatus;
    payload: Schemas.AdminPatchRouteLockResDto;
  }>;

export type AdminRouteV2ControllerUpdateRouteLockStatusVariables = {
  body: Schemas.AdminPatchRouteLockReqDto;
  pathParams: AdminRouteV2ControllerUpdateRouteLockStatusPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerUpdateRouteLockStatus = (
  variables: AdminRouteV2ControllerUpdateRouteLockStatusVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    undefined,
    AdminRouteV2ControllerUpdateRouteLockStatusError,
    Schemas.AdminPatchRouteLockReqDto,
    {},
    {},
    AdminRouteV2ControllerUpdateRouteLockStatusPathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/lock",
    method: "patch",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerUpdateRouteLockStatus = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      AdminRouteV2ControllerUpdateRouteLockStatusError,
      AdminRouteV2ControllerUpdateRouteLockStatusVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    undefined,
    AdminRouteV2ControllerUpdateRouteLockStatusError,
    AdminRouteV2ControllerUpdateRouteLockStatusVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerUpdateRouteLockStatusVariables,
    ) =>
      fetchAdminRouteV2ControllerUpdateRouteLockStatus({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerUpdateRoutesStatePathParams = {
  /**
   * Must be in the format YYYY-MM-DD.
   */
  date: string;
};

export type AdminRouteV2ControllerUpdateRoutesStateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerUpdateRoutesStateResponse = {
  data?: Schemas.AdminPatchRouteStateResDto;
  statusCode?: number;
};

export type AdminRouteV2ControllerUpdateRoutesStateVariables = {
  pathParams: AdminRouteV2ControllerUpdateRoutesStatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerUpdateRoutesState = (
  variables: AdminRouteV2ControllerUpdateRoutesStateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerUpdateRoutesStateResponse,
    AdminRouteV2ControllerUpdateRoutesStateError,
    undefined,
    {},
    {},
    AdminRouteV2ControllerUpdateRoutesStatePathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/build",
    method: "patch",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerUpdateRoutesState = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerUpdateRoutesStateResponse,
      AdminRouteV2ControllerUpdateRoutesStateError,
      AdminRouteV2ControllerUpdateRoutesStateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerUpdateRoutesStateResponse,
    AdminRouteV2ControllerUpdateRoutesStateError,
    AdminRouteV2ControllerUpdateRoutesStateVariables
  >({
    mutationFn: (variables: AdminRouteV2ControllerUpdateRoutesStateVariables) =>
      fetchAdminRouteV2ControllerUpdateRoutesState({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerUpdateRouteStateLivePathParams = {
  date: string;
};

export type AdminRouteV2ControllerUpdateRouteStateLiveQueryParams = {
  /**
   * The timezone of from the request
   */
  timezone?: string;
};

export type AdminRouteV2ControllerUpdateRouteStateLiveError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerUpdateRouteStateLiveResponse = {
  data?: Schemas.AdminPatchRouteStateResDto;
  statusCode?: number;
};

export type AdminRouteV2ControllerUpdateRouteStateLiveVariables = {
  pathParams: AdminRouteV2ControllerUpdateRouteStateLivePathParams;
  queryParams?: AdminRouteV2ControllerUpdateRouteStateLiveQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerUpdateRouteStateLive = (
  variables: AdminRouteV2ControllerUpdateRouteStateLiveVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerUpdateRouteStateLiveResponse,
    AdminRouteV2ControllerUpdateRouteStateLiveError,
    undefined,
    {},
    AdminRouteV2ControllerUpdateRouteStateLiveQueryParams,
    AdminRouteV2ControllerUpdateRouteStateLivePathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/live",
    method: "patch",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerUpdateRouteStateLive = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerUpdateRouteStateLiveResponse,
      AdminRouteV2ControllerUpdateRouteStateLiveError,
      AdminRouteV2ControllerUpdateRouteStateLiveVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerUpdateRouteStateLiveResponse,
    AdminRouteV2ControllerUpdateRouteStateLiveError,
    AdminRouteV2ControllerUpdateRouteStateLiveVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerUpdateRouteStateLiveVariables,
    ) =>
      fetchAdminRouteV2ControllerUpdateRouteStateLive({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerUpdateRouteStateEndPathParams = {
  date: string;
};

export type AdminRouteV2ControllerUpdateRouteStateEndError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerUpdateRouteStateEndResponse = {
  data?: Schemas.AdminPatchRouteStateResDto;
  statusCode?: number;
};

export type AdminRouteV2ControllerUpdateRouteStateEndVariables = {
  pathParams: AdminRouteV2ControllerUpdateRouteStateEndPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerUpdateRouteStateEnd = (
  variables: AdminRouteV2ControllerUpdateRouteStateEndVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerUpdateRouteStateEndResponse,
    AdminRouteV2ControllerUpdateRouteStateEndError,
    undefined,
    {},
    {},
    AdminRouteV2ControllerUpdateRouteStateEndPathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/end",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerUpdateRouteStateEnd = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminRouteV2ControllerUpdateRouteStateEndResponse,
      AdminRouteV2ControllerUpdateRouteStateEndError,
      AdminRouteV2ControllerUpdateRouteStateEndVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminRouteV2ControllerUpdateRouteStateEndResponse,
    AdminRouteV2ControllerUpdateRouteStateEndError,
    AdminRouteV2ControllerUpdateRouteStateEndVariables
  >({
    mutationFn: (
      variables: AdminRouteV2ControllerUpdateRouteStateEndVariables,
    ) =>
      fetchAdminRouteV2ControllerUpdateRouteStateEnd({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerUpdateRouteStatePathParams = {
  /**
   * Must be in the format YYYY-MM-DD.
   */
  date: string;
};

export type AdminRouteV2ControllerUpdateRouteStateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerUpdateRouteStateVariables = {
  body: Schemas.AdminPatchRevertStateReqDto;
  pathParams: AdminRouteV2ControllerUpdateRouteStatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerUpdateRouteState = (
  variables: AdminRouteV2ControllerUpdateRouteStateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    undefined,
    AdminRouteV2ControllerUpdateRouteStateError,
    Schemas.AdminPatchRevertStateReqDto,
    {},
    {},
    AdminRouteV2ControllerUpdateRouteStatePathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/state",
    method: "patch",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerUpdateRouteState = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      AdminRouteV2ControllerUpdateRouteStateError,
      AdminRouteV2ControllerUpdateRouteStateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    undefined,
    AdminRouteV2ControllerUpdateRouteStateError,
    AdminRouteV2ControllerUpdateRouteStateVariables
  >({
    mutationFn: (variables: AdminRouteV2ControllerUpdateRouteStateVariables) =>
      fetchAdminRouteV2ControllerUpdateRouteState({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteV2ControllerGetRouteStateDiffPathParams = {
  /**
   * Must be in the format YYYY-MM-DD.
   */
  date: string;
};

export type AdminRouteV2ControllerGetRouteStateDiffError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteV2ControllerGetRouteStateDiffResponse = {
  data?: Schemas.AdminGetRouteStateDiffResDto;
  statusCode?: number;
};

export type AdminRouteV2ControllerGetRouteStateDiffVariables = {
  pathParams: AdminRouteV2ControllerGetRouteStateDiffPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteV2ControllerGetRouteStateDiff = (
  variables: AdminRouteV2ControllerGetRouteStateDiffVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminRouteV2ControllerGetRouteStateDiffResponse,
    AdminRouteV2ControllerGetRouteStateDiffError,
    undefined,
    {},
    {},
    AdminRouteV2ControllerGetRouteStateDiffPathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/state/check",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminRouteV2ControllerGetRouteStateDiff = <
  TData = AdminRouteV2ControllerGetRouteStateDiffResponse,
>(
  variables: AdminRouteV2ControllerGetRouteStateDiffVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminRouteV2ControllerGetRouteStateDiffResponse,
      AdminRouteV2ControllerGetRouteStateDiffError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminRouteV2ControllerGetRouteStateDiffResponse,
    AdminRouteV2ControllerGetRouteStateDiffError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/routes/date/{date}/state/check",
      operationId: "adminRouteV2ControllerGetRouteStateDiff",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminRouteV2ControllerGetRouteStateDiff(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationControllerGetAllQueryParams = {
  limit?: number;
  page?: number;
  /**
   * Whether the location is enabled for reports
   */
  reportsEnabled?: boolean;
  /**
   * Location type
   */
  type?: "onDemand" | "annual";
  /**
   * Sort order
   */
  sort?: "name" | "address" | "emailConfirmed";
  /**
   * Sort order
   */
  order?: "asc" | "desc";
  /**
   * Search query
   */
  search?: string;
  /**
   * Comma-separated list of IDs
   */
  id?: string[];
  /**
   * Region ids
   */
  region__in?: string[];
  /**
   * Region ids
   */
  region__not_in?: string[];
};

export type AdminLocationControllerGetAllError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationControllerGetAllResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.AdminLocationWithCustomerResDto[];
  };

export type AdminLocationControllerGetAllVariables = {
  queryParams?: AdminLocationControllerGetAllQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationControllerGetAll = (
  variables: AdminLocationControllerGetAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationControllerGetAllResponse,
    AdminLocationControllerGetAllError,
    undefined,
    {},
    AdminLocationControllerGetAllQueryParams,
    {}
  >({ url: "/api/v1/admin/locations", method: "get", ...variables, signal });

export const useAdminLocationControllerGetAll = <
  TData = AdminLocationControllerGetAllResponse,
>(
  variables: AdminLocationControllerGetAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationControllerGetAllResponse,
      AdminLocationControllerGetAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationControllerGetAllResponse,
    AdminLocationControllerGetAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/locations",
      operationId: "adminLocationControllerGetAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationControllerGetAll(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationControllerCreateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationControllerCreateResponse = {
  data?: Schemas.AdminLocationResDto;
  statusCode?: number;
};

export type AdminLocationControllerCreateVariables = {
  body: Schemas.AdminPostLocationReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationControllerCreate = (
  variables: AdminLocationControllerCreateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationControllerCreateResponse,
    AdminLocationControllerCreateError,
    Schemas.AdminPostLocationReqDto,
    {},
    {},
    {}
  >({ url: "/api/v1/admin/locations", method: "post", ...variables, signal });

export const useAdminLocationControllerCreate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationControllerCreateResponse,
      AdminLocationControllerCreateError,
      AdminLocationControllerCreateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationControllerCreateResponse,
    AdminLocationControllerCreateError,
    AdminLocationControllerCreateVariables
  >({
    mutationFn: (variables: AdminLocationControllerCreateVariables) =>
      fetchAdminLocationControllerCreate({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type AdminLocationControllerDeleteManyError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationControllerDeleteManyResponse = {
  data?: Schemas.AdminDeleteManyLocationsResDto;
  statusCode?: number;
};

export type AdminLocationControllerDeleteManyRequestBody =
  Schemas.AdminDeleteManyLocationsReqDto[];

export type AdminLocationControllerDeleteManyVariables = {
  body?: AdminLocationControllerDeleteManyRequestBody;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationControllerDeleteMany = (
  variables: AdminLocationControllerDeleteManyVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationControllerDeleteManyResponse,
    AdminLocationControllerDeleteManyError,
    AdminLocationControllerDeleteManyRequestBody,
    {},
    {},
    {}
  >({ url: "/api/v1/admin/locations", method: "delete", ...variables, signal });

export const useAdminLocationControllerDeleteMany = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationControllerDeleteManyResponse,
      AdminLocationControllerDeleteManyError,
      AdminLocationControllerDeleteManyVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationControllerDeleteManyResponse,
    AdminLocationControllerDeleteManyError,
    AdminLocationControllerDeleteManyVariables
  >({
    mutationFn: (variables: AdminLocationControllerDeleteManyVariables) =>
      fetchAdminLocationControllerDeleteMany({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLocationControllerGetAllCSVQueryParams = {
  limit?: number;
  page?: number;
  /**
   * Whether the location is enabled for reports
   */
  reportsEnabled?: boolean;
  /**
   * Location type
   */
  type?: "onDemand" | "annual";
  /**
   * Sort order
   */
  sort?: "name" | "address" | "emailConfirmed";
  /**
   * Sort order
   */
  order?: "asc" | "desc";
  /**
   * Search query
   */
  search?: string;
  /**
   * Comma-separated list of IDs
   */
  id?: string[];
  /**
   * Region ids
   */
  region__in?: string[];
  /**
   * Region ids
   */
  region__not_in?: string[];
};

export type AdminLocationControllerGetAllCSVError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationControllerGetAllCSVResponse = {
  data?: Schemas.String;
  statusCode?: number;
};

export type AdminLocationControllerGetAllCSVVariables = {
  queryParams?: AdminLocationControllerGetAllCSVQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationControllerGetAllCSV = (
  variables: AdminLocationControllerGetAllCSVVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationControllerGetAllCSVResponse,
    AdminLocationControllerGetAllCSVError,
    undefined,
    {},
    AdminLocationControllerGetAllCSVQueryParams,
    {}
  >({
    url: "/api/v1/admin/locations/csv",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationControllerGetAllCSV = <
  TData = AdminLocationControllerGetAllCSVResponse,
>(
  variables: AdminLocationControllerGetAllCSVVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationControllerGetAllCSVResponse,
      AdminLocationControllerGetAllCSVError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationControllerGetAllCSVResponse,
    AdminLocationControllerGetAllCSVError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/locations/csv",
      operationId: "adminLocationControllerGetAllCSV",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationControllerGetAllCSV(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationControllerCreateFromCsvError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationControllerCreateFromCsvResponse = {
  data?: Schemas.CsvLocationResponseDto;
  statusCode?: number;
};

export type AdminLocationControllerCreateFromCsvRequestBody = {
  /**
   * @format binary
   */
  file?: Blob;
};

export type AdminLocationControllerCreateFromCsvVariables = {
  body?: AdminLocationControllerCreateFromCsvRequestBody;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationControllerCreateFromCsv = (
  variables: AdminLocationControllerCreateFromCsvVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationControllerCreateFromCsvResponse,
    AdminLocationControllerCreateFromCsvError,
    AdminLocationControllerCreateFromCsvRequestBody,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/locations/csv",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminLocationControllerCreateFromCsv = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationControllerCreateFromCsvResponse,
      AdminLocationControllerCreateFromCsvError,
      AdminLocationControllerCreateFromCsvVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationControllerCreateFromCsvResponse,
    AdminLocationControllerCreateFromCsvError,
    AdminLocationControllerCreateFromCsvVariables
  >({
    mutationFn: (variables: AdminLocationControllerCreateFromCsvVariables) =>
      fetchAdminLocationControllerCreateFromCsv({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLocationControllerGetCsvTemplateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationControllerGetCsvTemplateResponse = {
  data?: Schemas.String;
  statusCode?: number;
};

export type AdminLocationControllerGetCsvTemplateVariables =
  EstabrookContext["fetcherOptions"];

export const fetchAdminLocationControllerGetCsvTemplate = (
  variables: AdminLocationControllerGetCsvTemplateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationControllerGetCsvTemplateResponse,
    AdminLocationControllerGetCsvTemplateError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/locations/csv-template",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationControllerGetCsvTemplate = <
  TData = AdminLocationControllerGetCsvTemplateResponse,
>(
  variables: AdminLocationControllerGetCsvTemplateVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationControllerGetCsvTemplateResponse,
      AdminLocationControllerGetCsvTemplateError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationControllerGetCsvTemplateResponse,
    AdminLocationControllerGetCsvTemplateError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/locations/csv-template",
      operationId: "adminLocationControllerGetCsvTemplate",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationControllerGetCsvTemplate(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationControllerGetCsvUploadStateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationControllerGetCsvUploadStateResponse = {
  data?: Schemas.AdminCsvUploadStatResDto;
  statusCode?: number;
};

export type AdminLocationControllerGetCsvUploadStateVariables =
  EstabrookContext["fetcherOptions"];

export const fetchAdminLocationControllerGetCsvUploadState = (
  variables: AdminLocationControllerGetCsvUploadStateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationControllerGetCsvUploadStateResponse,
    AdminLocationControllerGetCsvUploadStateError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/locations/csv/state",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationControllerGetCsvUploadState = <
  TData = AdminLocationControllerGetCsvUploadStateResponse,
>(
  variables: AdminLocationControllerGetCsvUploadStateVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationControllerGetCsvUploadStateResponse,
      AdminLocationControllerGetCsvUploadStateError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationControllerGetCsvUploadStateResponse,
    AdminLocationControllerGetCsvUploadStateError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/locations/csv/state",
      operationId: "adminLocationControllerGetCsvUploadState",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationControllerGetCsvUploadState(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationControllerCancelCsvUploadError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationControllerCancelCsvUploadResponse = {
  data?: Schemas.String;
  statusCode?: number;
};

export type AdminLocationControllerCancelCsvUploadVariables =
  EstabrookContext["fetcherOptions"];

export const fetchAdminLocationControllerCancelCsvUpload = (
  variables: AdminLocationControllerCancelCsvUploadVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationControllerCancelCsvUploadResponse,
    AdminLocationControllerCancelCsvUploadError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/locations/csv/cancel",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminLocationControllerCancelCsvUpload = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationControllerCancelCsvUploadResponse,
      AdminLocationControllerCancelCsvUploadError,
      AdminLocationControllerCancelCsvUploadVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationControllerCancelCsvUploadResponse,
    AdminLocationControllerCancelCsvUploadError,
    AdminLocationControllerCancelCsvUploadVariables
  >({
    mutationFn: (variables: AdminLocationControllerCancelCsvUploadVariables) =>
      fetchAdminLocationControllerCancelCsvUpload({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLocationControllerAcceptUpdatesFromCsvError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationControllerAcceptUpdatesFromCsvResponse = {
  data?: Schemas.String;
  statusCode?: number;
};

export type AdminLocationControllerAcceptUpdatesFromCsvVariables = {
  body: Schemas.AcceptChangesFromCsvDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationControllerAcceptUpdatesFromCsv = (
  variables: AdminLocationControllerAcceptUpdatesFromCsvVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationControllerAcceptUpdatesFromCsvResponse,
    AdminLocationControllerAcceptUpdatesFromCsvError,
    Schemas.AcceptChangesFromCsvDto,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/locations/csv/accept",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminLocationControllerAcceptUpdatesFromCsv = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationControllerAcceptUpdatesFromCsvResponse,
      AdminLocationControllerAcceptUpdatesFromCsvError,
      AdminLocationControllerAcceptUpdatesFromCsvVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationControllerAcceptUpdatesFromCsvResponse,
    AdminLocationControllerAcceptUpdatesFromCsvError,
    AdminLocationControllerAcceptUpdatesFromCsvVariables
  >({
    mutationFn: (
      variables: AdminLocationControllerAcceptUpdatesFromCsvVariables,
    ) =>
      fetchAdminLocationControllerAcceptUpdatesFromCsv({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLocationControllerGetOnePathParams = {
  locationId: string;
};

export type AdminLocationControllerGetOneError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationControllerGetOneResponse = {
  data?: Schemas.AdminLocationResDto;
  statusCode?: number;
};

export type AdminLocationControllerGetOneVariables = {
  pathParams: AdminLocationControllerGetOnePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationControllerGetOne = (
  variables: AdminLocationControllerGetOneVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationControllerGetOneResponse,
    AdminLocationControllerGetOneError,
    undefined,
    {},
    {},
    AdminLocationControllerGetOnePathParams
  >({
    url: "/api/v1/admin/locations/{locationId}",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationControllerGetOne = <
  TData = AdminLocationControllerGetOneResponse,
>(
  variables: AdminLocationControllerGetOneVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationControllerGetOneResponse,
      AdminLocationControllerGetOneError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationControllerGetOneResponse,
    AdminLocationControllerGetOneError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/locations/{locationId}",
      operationId: "adminLocationControllerGetOne",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationControllerGetOne(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationControllerUpdatePathParams = {
  locationId: string;
};

export type AdminLocationControllerUpdateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationControllerUpdateResponse = {
  data?: Schemas.AdminLocationResDto;
  statusCode?: number;
};

export type AdminLocationControllerUpdateVariables = {
  body?: Schemas.AdminPutLocationReqDto;
  pathParams: AdminLocationControllerUpdatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationControllerUpdate = (
  variables: AdminLocationControllerUpdateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationControllerUpdateResponse,
    AdminLocationControllerUpdateError,
    Schemas.AdminPutLocationReqDto,
    {},
    {},
    AdminLocationControllerUpdatePathParams
  >({
    url: "/api/v1/admin/locations/{locationId}",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminLocationControllerUpdate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationControllerUpdateResponse,
      AdminLocationControllerUpdateError,
      AdminLocationControllerUpdateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationControllerUpdateResponse,
    AdminLocationControllerUpdateError,
    AdminLocationControllerUpdateVariables
  >({
    mutationFn: (variables: AdminLocationControllerUpdateVariables) =>
      fetchAdminLocationControllerUpdate({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type AdminLocationControllerDeletePathParams = {
  locationId: string;
};

export type AdminLocationControllerDeleteError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationControllerDeleteResponse = {
  data?: Schemas.AdminLocationResDto;
  statusCode?: number;
};

export type AdminLocationControllerDeleteVariables = {
  pathParams: AdminLocationControllerDeletePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationControllerDelete = (
  variables: AdminLocationControllerDeleteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationControllerDeleteResponse,
    AdminLocationControllerDeleteError,
    undefined,
    {},
    {},
    AdminLocationControllerDeletePathParams
  >({
    url: "/api/v1/admin/locations/{locationId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminLocationControllerDelete = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationControllerDeleteResponse,
      AdminLocationControllerDeleteError,
      AdminLocationControllerDeleteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationControllerDeleteResponse,
    AdminLocationControllerDeleteError,
    AdminLocationControllerDeleteVariables
  >({
    mutationFn: (variables: AdminLocationControllerDeleteVariables) =>
      fetchAdminLocationControllerDelete({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type AdminLocationControllerUpdateServiceTimePathParams = {
  locationId: string;
};

export type AdminLocationControllerUpdateServiceTimeError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationControllerUpdateServiceTimeResponse = {
  data?: Schemas.AdminLocationServiceTimeResDto;
  statusCode?: number;
};

export type AdminLocationControllerUpdateServiceTimeVariables = {
  body: Schemas.AdminPutLocationServiceTimeReqDto;
  pathParams: AdminLocationControllerUpdateServiceTimePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationControllerUpdateServiceTime = (
  variables: AdminLocationControllerUpdateServiceTimeVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationControllerUpdateServiceTimeResponse,
    AdminLocationControllerUpdateServiceTimeError,
    Schemas.AdminPutLocationServiceTimeReqDto,
    {},
    {},
    AdminLocationControllerUpdateServiceTimePathParams
  >({
    url: "/api/v1/admin/locations/{locationId}/service-time",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminLocationControllerUpdateServiceTime = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationControllerUpdateServiceTimeResponse,
      AdminLocationControllerUpdateServiceTimeError,
      AdminLocationControllerUpdateServiceTimeVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationControllerUpdateServiceTimeResponse,
    AdminLocationControllerUpdateServiceTimeError,
    AdminLocationControllerUpdateServiceTimeVariables
  >({
    mutationFn: (
      variables: AdminLocationControllerUpdateServiceTimeVariables,
    ) =>
      fetchAdminLocationControllerUpdateServiceTime({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLocationControllerUpdateManyServiceAccessError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationControllerUpdateManyServiceAccessResponse = {
  data?: Schemas.AdminLocationResDto[];
  statusCode?: number;
};

export type AdminLocationControllerUpdateManyServiceAccessRequestBody =
  Schemas.AdminPatchManyLocationServiceAccessReqDto[];

export type AdminLocationControllerUpdateManyServiceAccessVariables = {
  body?: AdminLocationControllerUpdateManyServiceAccessRequestBody;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationControllerUpdateManyServiceAccess = (
  variables: AdminLocationControllerUpdateManyServiceAccessVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationControllerUpdateManyServiceAccessResponse,
    AdminLocationControllerUpdateManyServiceAccessError,
    AdminLocationControllerUpdateManyServiceAccessRequestBody,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/locations/service-access",
    method: "patch",
    ...variables,
    signal,
  });

export const useAdminLocationControllerUpdateManyServiceAccess = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationControllerUpdateManyServiceAccessResponse,
      AdminLocationControllerUpdateManyServiceAccessError,
      AdminLocationControllerUpdateManyServiceAccessVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationControllerUpdateManyServiceAccessResponse,
    AdminLocationControllerUpdateManyServiceAccessError,
    AdminLocationControllerUpdateManyServiceAccessVariables
  >({
    mutationFn: (
      variables: AdminLocationControllerUpdateManyServiceAccessVariables,
    ) =>
      fetchAdminLocationControllerUpdateManyServiceAccess({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLocationControllerUpdateLocationServiceAccessPathParams = {
  locationId: string;
};

export type AdminLocationControllerUpdateLocationServiceAccessError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationControllerUpdateLocationServiceAccessResponse = {
  data?: Schemas.AdminLocationResDto;
  statusCode?: number;
};

export type AdminLocationControllerUpdateLocationServiceAccessVariables = {
  body: Schemas.AdminPutLocationServiceAccessReqDto;
  pathParams: AdminLocationControllerUpdateLocationServiceAccessPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationControllerUpdateLocationServiceAccess = (
  variables: AdminLocationControllerUpdateLocationServiceAccessVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationControllerUpdateLocationServiceAccessResponse,
    AdminLocationControllerUpdateLocationServiceAccessError,
    Schemas.AdminPutLocationServiceAccessReqDto,
    {},
    {},
    AdminLocationControllerUpdateLocationServiceAccessPathParams
  >({
    url: "/api/v1/admin/locations/{locationId}/service-access",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminLocationControllerUpdateLocationServiceAccess = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationControllerUpdateLocationServiceAccessResponse,
      AdminLocationControllerUpdateLocationServiceAccessError,
      AdminLocationControllerUpdateLocationServiceAccessVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationControllerUpdateLocationServiceAccessResponse,
    AdminLocationControllerUpdateLocationServiceAccessError,
    AdminLocationControllerUpdateLocationServiceAccessVariables
  >({
    mutationFn: (
      variables: AdminLocationControllerUpdateLocationServiceAccessVariables,
    ) =>
      fetchAdminLocationControllerUpdateLocationServiceAccess({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLocationCustomerControllerGetAllPathParams = {
  customerId: string;
};

export type AdminLocationCustomerControllerGetAllError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationCustomerControllerGetAllResponse = {
  data?: Schemas.AdminShortLocationResDto[];
  statusCode?: number;
};

export type AdminLocationCustomerControllerGetAllVariables = {
  pathParams: AdminLocationCustomerControllerGetAllPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationCustomerControllerGetAll = (
  variables: AdminLocationCustomerControllerGetAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationCustomerControllerGetAllResponse,
    AdminLocationCustomerControllerGetAllError,
    undefined,
    {},
    {},
    AdminLocationCustomerControllerGetAllPathParams
  >({
    url: "/api/v1/admin/customers/{customerId}/locations",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationCustomerControllerGetAll = <
  TData = AdminLocationCustomerControllerGetAllResponse,
>(
  variables: AdminLocationCustomerControllerGetAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationCustomerControllerGetAllResponse,
      AdminLocationCustomerControllerGetAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationCustomerControllerGetAllResponse,
    AdminLocationCustomerControllerGetAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/customers/{customerId}/locations",
      operationId: "adminLocationCustomerControllerGetAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationCustomerControllerGetAll(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationEligibilityControllerGetLocationsOnDatePathParams = {
  date: string;
};

export type AdminLocationEligibilityControllerGetLocationsOnDateQueryParams = {
  limit?: number;
  page?: number;
  eligibleLocationStatus?:
    | "home"
    | "away"
    | "delayed"
    | "annualUrgent"
    | "earlyPlow"
    | "latePlow"
    | "onDemandUrgent";
};

export type AdminLocationEligibilityControllerGetLocationsOnDateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationEligibilityControllerGetLocationsOnDateResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.AdminLocationEligibilityResDto[];
  };

export type AdminLocationEligibilityControllerGetLocationsOnDateVariables = {
  pathParams: AdminLocationEligibilityControllerGetLocationsOnDatePathParams;
  queryParams?: AdminLocationEligibilityControllerGetLocationsOnDateQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationEligibilityControllerGetLocationsOnDate = (
  variables: AdminLocationEligibilityControllerGetLocationsOnDateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationEligibilityControllerGetLocationsOnDateResponse,
    AdminLocationEligibilityControllerGetLocationsOnDateError,
    undefined,
    {},
    AdminLocationEligibilityControllerGetLocationsOnDateQueryParams,
    AdminLocationEligibilityControllerGetLocationsOnDatePathParams
  >({
    url: "/api/v1/admin/locations/eligibility/{date}",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationEligibilityControllerGetLocationsOnDate = <
  TData = AdminLocationEligibilityControllerGetLocationsOnDateResponse,
>(
  variables: AdminLocationEligibilityControllerGetLocationsOnDateVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationEligibilityControllerGetLocationsOnDateResponse,
      AdminLocationEligibilityControllerGetLocationsOnDateError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationEligibilityControllerGetLocationsOnDateResponse,
    AdminLocationEligibilityControllerGetLocationsOnDateError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/locations/eligibility/{date}",
      operationId: "adminLocationEligibilityControllerGetLocationsOnDate",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationEligibilityControllerGetLocationsOnDate(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationEligibilityControllerGetStatisticsOnDatePathParams = {
  date: string;
};

export type AdminLocationEligibilityControllerGetStatisticsOnDateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationEligibilityControllerGetStatisticsOnDateResponse = {
  data?: Schemas.AdminLocationEligibilityStatisticsResDto;
  statusCode?: number;
};

export type AdminLocationEligibilityControllerGetStatisticsOnDateVariables = {
  pathParams: AdminLocationEligibilityControllerGetStatisticsOnDatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationEligibilityControllerGetStatisticsOnDate = (
  variables: AdminLocationEligibilityControllerGetStatisticsOnDateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationEligibilityControllerGetStatisticsOnDateResponse,
    AdminLocationEligibilityControllerGetStatisticsOnDateError,
    undefined,
    {},
    {},
    AdminLocationEligibilityControllerGetStatisticsOnDatePathParams
  >({
    url: "/api/v1/admin/locations/eligibility/{date}/statistics",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationEligibilityControllerGetStatisticsOnDate = <
  TData = AdminLocationEligibilityControllerGetStatisticsOnDateResponse,
>(
  variables: AdminLocationEligibilityControllerGetStatisticsOnDateVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationEligibilityControllerGetStatisticsOnDateResponse,
      AdminLocationEligibilityControllerGetStatisticsOnDateError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationEligibilityControllerGetStatisticsOnDateResponse,
    AdminLocationEligibilityControllerGetStatisticsOnDateError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/locations/eligibility/{date}/statistics",
      operationId: "adminLocationEligibilityControllerGetStatisticsOnDate",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationEligibilityControllerGetStatisticsOnDate(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationV2ControllerGetAllLocationsQueryParams = {
  /**
   * Limit
   *
   * @default 10
   */
  limit?: number;
  page?: number;
  /**
   * Whether the location is assigned to a region
   */
  assigned?: boolean;
  /**
   * Search query
   */
  search?: string;
  /**
   * Sort order
   */
  sort?: string;
  /**
   * Sort order
   */
  order?: "asc" | "desc";
  /**
   * Whether the location is enabled for reports
   */
  reportsEnabled?: boolean;
  /**
   * Location type
   */
  type?: ("onDemand" | "annual")[];
  /**
   * Region ids
   */
  region__in?: string[];
};

export type AdminLocationV2ControllerGetAllLocationsError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationV2ControllerGetAllLocationsResponse = {
  data?: Schemas.AdminAllLocationsResDto[];
  statusCode?: number;
};

export type AdminLocationV2ControllerGetAllLocationsVariables = {
  queryParams?: AdminLocationV2ControllerGetAllLocationsQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationV2ControllerGetAllLocations = (
  variables: AdminLocationV2ControllerGetAllLocationsVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationV2ControllerGetAllLocationsResponse,
    AdminLocationV2ControllerGetAllLocationsError,
    undefined,
    {},
    AdminLocationV2ControllerGetAllLocationsQueryParams,
    {}
  >({ url: "/api/v2/admin/locations", method: "get", ...variables, signal });

export const useAdminLocationV2ControllerGetAllLocations = <
  TData = AdminLocationV2ControllerGetAllLocationsResponse,
>(
  variables: AdminLocationV2ControllerGetAllLocationsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationV2ControllerGetAllLocationsResponse,
      AdminLocationV2ControllerGetAllLocationsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationV2ControllerGetAllLocationsResponse,
    AdminLocationV2ControllerGetAllLocationsError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/locations",
      operationId: "adminLocationV2ControllerGetAllLocations",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationV2ControllerGetAllLocations(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationV2ControllerFindManyQueryParams = {
  /**
   * Limit
   *
   * @default 10
   */
  limit?: number;
  page?: number;
  /**
   * Whether the location is assigned to a region
   */
  assigned?: boolean;
  /**
   * Search query
   */
  search?: string;
  /**
   * Sort order
   */
  sort?: string;
  /**
   * Sort order
   */
  order?: "asc" | "desc";
  /**
   * Whether the location is enabled for reports
   */
  reportsEnabled?: boolean;
  /**
   * Location type
   */
  type?: ("onDemand" | "annual")[];
  /**
   * Region ids
   */
  region__in?: string[];
};

export type AdminLocationV2ControllerFindManyError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationV2ControllerFindManyResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.AdminAllLocationsResDto[];
  };

export type AdminLocationV2ControllerFindManyVariables = {
  queryParams?: AdminLocationV2ControllerFindManyQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationV2ControllerFindMany = (
  variables: AdminLocationV2ControllerFindManyVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationV2ControllerFindManyResponse,
    AdminLocationV2ControllerFindManyError,
    undefined,
    {},
    AdminLocationV2ControllerFindManyQueryParams,
    {}
  >({
    url: "/api/v2/admin/locations/paged",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationV2ControllerFindMany = <
  TData = AdminLocationV2ControllerFindManyResponse,
>(
  variables: AdminLocationV2ControllerFindManyVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationV2ControllerFindManyResponse,
      AdminLocationV2ControllerFindManyError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationV2ControllerFindManyResponse,
    AdminLocationV2ControllerFindManyError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/locations/paged",
      operationId: "adminLocationV2ControllerFindMany",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationV2ControllerFindMany(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationV2ControllerGetSelectedLocationsInfoError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationV2ControllerGetSelectedLocationsInfoResponse = {
  data?: Schemas.AdminLocationsInfoResDto[];
  statusCode?: number;
};

export type AdminLocationV2ControllerGetSelectedLocationsInfoVariables = {
  body: Schemas.AdminLocationsInfoReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationV2ControllerGetSelectedLocationsInfo = (
  variables: AdminLocationV2ControllerGetSelectedLocationsInfoVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationV2ControllerGetSelectedLocationsInfoResponse,
    AdminLocationV2ControllerGetSelectedLocationsInfoError,
    Schemas.AdminLocationsInfoReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v2/admin/locations/info",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminLocationV2ControllerGetSelectedLocationsInfo = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationV2ControllerGetSelectedLocationsInfoResponse,
      AdminLocationV2ControllerGetSelectedLocationsInfoError,
      AdminLocationV2ControllerGetSelectedLocationsInfoVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationV2ControllerGetSelectedLocationsInfoResponse,
    AdminLocationV2ControllerGetSelectedLocationsInfoError,
    AdminLocationV2ControllerGetSelectedLocationsInfoVariables
  >({
    mutationFn: (
      variables: AdminLocationV2ControllerGetSelectedLocationsInfoVariables,
    ) =>
      fetchAdminLocationV2ControllerGetSelectedLocationsInfo({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLocationV2ControllerGetUnassignedRouteLocationsForDatePathParams =
  {
    date: string;
  };

export type AdminLocationV2ControllerGetUnassignedRouteLocationsForDateQueryParams =
  {
    limit?: number;
    page?: number;
    /**
     * Get unnassigned acctive locations
     */
    active?: boolean;
    /**
     * Location type
     */
    type?: "onDemand" | "annual";
  };

export type AdminLocationV2ControllerGetUnassignedRouteLocationsForDateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationV2ControllerGetUnassignedRouteLocationsForDateResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.AdminAllLocationsResDto[];
  };

export type AdminLocationV2ControllerGetUnassignedRouteLocationsForDateVariables =
  {
    pathParams: AdminLocationV2ControllerGetUnassignedRouteLocationsForDatePathParams;
    queryParams?: AdminLocationV2ControllerGetUnassignedRouteLocationsForDateQueryParams;
  } & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationV2ControllerGetUnassignedRouteLocationsForDate =
  (
    variables: AdminLocationV2ControllerGetUnassignedRouteLocationsForDateVariables,
    signal?: AbortSignal,
  ) =>
    estabrookFetch<
      AdminLocationV2ControllerGetUnassignedRouteLocationsForDateResponse,
      AdminLocationV2ControllerGetUnassignedRouteLocationsForDateError,
      undefined,
      {},
      AdminLocationV2ControllerGetUnassignedRouteLocationsForDateQueryParams,
      AdminLocationV2ControllerGetUnassignedRouteLocationsForDatePathParams
    >({
      url: "/api/v2/admin/locations/date/{date}/unassigned",
      method: "get",
      ...variables,
      signal,
    });

export const useAdminLocationV2ControllerGetUnassignedRouteLocationsForDate = <
  TData = AdminLocationV2ControllerGetUnassignedRouteLocationsForDateResponse,
>(
  variables: AdminLocationV2ControllerGetUnassignedRouteLocationsForDateVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationV2ControllerGetUnassignedRouteLocationsForDateResponse,
      AdminLocationV2ControllerGetUnassignedRouteLocationsForDateError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationV2ControllerGetUnassignedRouteLocationsForDateResponse,
    AdminLocationV2ControllerGetUnassignedRouteLocationsForDateError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/locations/date/{date}/unassigned",
      operationId:
        "adminLocationV2ControllerGetUnassignedRouteLocationsForDate",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationV2ControllerGetUnassignedRouteLocationsForDate(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationV2ControllerCreateLocationWithCustomerError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationV2ControllerCreateLocationWithCustomerResponse = {
  data?: Schemas.AdminLocationResV2Dto;
  statusCode?: number;
};

export type AdminLocationV2ControllerCreateLocationWithCustomerVariables = {
  body: Schemas.AdminCreateLocationWithCustomerReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationV2ControllerCreateLocationWithCustomer = (
  variables: AdminLocationV2ControllerCreateLocationWithCustomerVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationV2ControllerCreateLocationWithCustomerResponse,
    AdminLocationV2ControllerCreateLocationWithCustomerError,
    Schemas.AdminCreateLocationWithCustomerReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v2/admin/locations/customer",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminLocationV2ControllerCreateLocationWithCustomer = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationV2ControllerCreateLocationWithCustomerResponse,
      AdminLocationV2ControllerCreateLocationWithCustomerError,
      AdminLocationV2ControllerCreateLocationWithCustomerVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationV2ControllerCreateLocationWithCustomerResponse,
    AdminLocationV2ControllerCreateLocationWithCustomerError,
    AdminLocationV2ControllerCreateLocationWithCustomerVariables
  >({
    mutationFn: (
      variables: AdminLocationV2ControllerCreateLocationWithCustomerVariables,
    ) =>
      fetchAdminLocationV2ControllerCreateLocationWithCustomer({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminCustomerControllerGetAllCSVQueryParams = {
  limit?: number;
  page?: number;
  /**
   * The sort
   */
  sort?: "fullName" | "email";
  /**
   * The order
   */
  order?: "asc" | "desc";
  /**
   * The search
   */
  search?: string;
  /**
   * The email confirmed
   */
  emailConfirmed?: boolean;
  /**
   * IANA timezone used to format CSV datetime columns. Defaults to UTC when omitted.
   */
  timezone?: string;
};

export type AdminCustomerControllerGetAllCSVError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminCustomerControllerGetAllCSVResponse = {
  data?: Schemas.String;
  statusCode?: number;
};

export type AdminCustomerControllerGetAllCSVVariables = {
  queryParams?: AdminCustomerControllerGetAllCSVQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminCustomerControllerGetAllCSV = (
  variables: AdminCustomerControllerGetAllCSVVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminCustomerControllerGetAllCSVResponse,
    AdminCustomerControllerGetAllCSVError,
    undefined,
    {},
    AdminCustomerControllerGetAllCSVQueryParams,
    {}
  >({
    url: "/api/v1/admin/customers/csv",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminCustomerControllerGetAllCSV = <
  TData = AdminCustomerControllerGetAllCSVResponse,
>(
  variables: AdminCustomerControllerGetAllCSVVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminCustomerControllerGetAllCSVResponse,
      AdminCustomerControllerGetAllCSVError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminCustomerControllerGetAllCSVResponse,
    AdminCustomerControllerGetAllCSVError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/customers/csv",
      operationId: "adminCustomerControllerGetAllCSV",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminCustomerControllerGetAllCSV(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminCustomerControllerSendInvitePathParams = {
  customerId: string;
};

export type AdminCustomerControllerSendInviteError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminCustomerControllerSendInviteResponse = {
  data?: Schemas.AdminCustomerResDto;
  statusCode?: number;
};

export type AdminCustomerControllerSendInviteVariables = {
  pathParams: AdminCustomerControllerSendInvitePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminCustomerControllerSendInvite = (
  variables: AdminCustomerControllerSendInviteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminCustomerControllerSendInviteResponse,
    AdminCustomerControllerSendInviteError,
    undefined,
    {},
    {},
    AdminCustomerControllerSendInvitePathParams
  >({
    url: "/api/v1/admin/customers/{customerId}/send-invite",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminCustomerControllerSendInvite = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminCustomerControllerSendInviteResponse,
      AdminCustomerControllerSendInviteError,
      AdminCustomerControllerSendInviteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminCustomerControllerSendInviteResponse,
    AdminCustomerControllerSendInviteError,
    AdminCustomerControllerSendInviteVariables
  >({
    mutationFn: (variables: AdminCustomerControllerSendInviteVariables) =>
      fetchAdminCustomerControllerSendInvite({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminCustomerControllerUpdateManyAppAccessError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminCustomerControllerUpdateManyAppAccessResponse = {
  data?: Schemas.AdminCustomerResDto[];
  statusCode?: number;
};

export type AdminCustomerControllerUpdateManyAppAccessRequestBody =
  Schemas.AdminPatchManyCustomerAppAccessReqDto[];

export type AdminCustomerControllerUpdateManyAppAccessVariables = {
  body?: AdminCustomerControllerUpdateManyAppAccessRequestBody;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminCustomerControllerUpdateManyAppAccess = (
  variables: AdminCustomerControllerUpdateManyAppAccessVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminCustomerControllerUpdateManyAppAccessResponse,
    AdminCustomerControllerUpdateManyAppAccessError,
    AdminCustomerControllerUpdateManyAppAccessRequestBody,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/customers/app-access",
    method: "patch",
    ...variables,
    signal,
  });

export const useAdminCustomerControllerUpdateManyAppAccess = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminCustomerControllerUpdateManyAppAccessResponse,
      AdminCustomerControllerUpdateManyAppAccessError,
      AdminCustomerControllerUpdateManyAppAccessVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminCustomerControllerUpdateManyAppAccessResponse,
    AdminCustomerControllerUpdateManyAppAccessError,
    AdminCustomerControllerUpdateManyAppAccessVariables
  >({
    mutationFn: (
      variables: AdminCustomerControllerUpdateManyAppAccessVariables,
    ) =>
      fetchAdminCustomerControllerUpdateManyAppAccess({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminCustomerControllerUpdateCustomerAppAccessPathParams = {
  customerId: string;
};

export type AdminCustomerControllerUpdateCustomerAppAccessError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminCustomerControllerUpdateCustomerAppAccessResponse = {
  data?: Schemas.AdminCustomerResDto;
  statusCode?: number;
};

export type AdminCustomerControllerUpdateCustomerAppAccessVariables = {
  body: Schemas.AdminPutCustomerAppAccessReqDto;
  pathParams: AdminCustomerControllerUpdateCustomerAppAccessPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminCustomerControllerUpdateCustomerAppAccess = (
  variables: AdminCustomerControllerUpdateCustomerAppAccessVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminCustomerControllerUpdateCustomerAppAccessResponse,
    AdminCustomerControllerUpdateCustomerAppAccessError,
    Schemas.AdminPutCustomerAppAccessReqDto,
    {},
    {},
    AdminCustomerControllerUpdateCustomerAppAccessPathParams
  >({
    url: "/api/v1/admin/customers/{customerId}/app-access",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminCustomerControllerUpdateCustomerAppAccess = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminCustomerControllerUpdateCustomerAppAccessResponse,
      AdminCustomerControllerUpdateCustomerAppAccessError,
      AdminCustomerControllerUpdateCustomerAppAccessVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminCustomerControllerUpdateCustomerAppAccessResponse,
    AdminCustomerControllerUpdateCustomerAppAccessError,
    AdminCustomerControllerUpdateCustomerAppAccessVariables
  >({
    mutationFn: (
      variables: AdminCustomerControllerUpdateCustomerAppAccessVariables,
    ) =>
      fetchAdminCustomerControllerUpdateCustomerAppAccess({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminAccountControllerGetAllQueryParams = {
  limit?: number;
  page?: number;
  sort?: "lastName" | "address" | "email" | "isActive";
  order?: "asc" | "desc";
  search?: string;
  /**
   * Super admins only — when set, lists organization admins for that organization. Omit to list platform super admins.
   */
  organizationId?: string;
  /**
   * Filter by active status
   */
  isActive?: boolean;
};

export type AdminAccountControllerGetAllError = Fetcher.ErrorWrapper<undefined>;

export type AdminAccountControllerGetAllResponse = Schemas.PaginatedResponse & {
  data?: Schemas.AdminAccountResDto[];
};

export type AdminAccountControllerGetAllVariables = {
  queryParams?: AdminAccountControllerGetAllQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminAccountControllerGetAll = (
  variables: AdminAccountControllerGetAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminAccountControllerGetAllResponse,
    AdminAccountControllerGetAllError,
    undefined,
    {},
    AdminAccountControllerGetAllQueryParams,
    {}
  >({ url: "/api/v1/admin/accounts", method: "get", ...variables, signal });

export const useAdminAccountControllerGetAll = <
  TData = AdminAccountControllerGetAllResponse,
>(
  variables: AdminAccountControllerGetAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminAccountControllerGetAllResponse,
      AdminAccountControllerGetAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminAccountControllerGetAllResponse,
    AdminAccountControllerGetAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/accounts",
      operationId: "adminAccountControllerGetAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminAccountControllerGetAll(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminAccountControllerResetPasswordError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminAccountControllerResetPasswordResponse = {
  data?: Schemas.AdminAccountResDto;
  statusCode?: number;
};

export type AdminAccountControllerResetPasswordVariables = {
  body: Schemas.AdminPostAccountPasswordResetReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminAccountControllerResetPassword = (
  variables: AdminAccountControllerResetPasswordVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminAccountControllerResetPasswordResponse,
    AdminAccountControllerResetPasswordError,
    Schemas.AdminPostAccountPasswordResetReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/accounts/password/reset",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminAccountControllerResetPassword = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminAccountControllerResetPasswordResponse,
      AdminAccountControllerResetPasswordError,
      AdminAccountControllerResetPasswordVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminAccountControllerResetPasswordResponse,
    AdminAccountControllerResetPasswordError,
    AdminAccountControllerResetPasswordVariables
  >({
    mutationFn: (variables: AdminAccountControllerResetPasswordVariables) =>
      fetchAdminAccountControllerResetPassword({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminAccountControllerUpdateStatusPathParams = {
  id: string;
};

export type AdminAccountControllerUpdateStatusError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminAccountControllerUpdateStatusResponse = {
  data?: Schemas.AdminAccountResDto;
  statusCode?: number;
};

export type AdminAccountControllerUpdateStatusVariables = {
  body: Schemas.AdminPatchAccountStatusReqDto;
  pathParams: AdminAccountControllerUpdateStatusPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminAccountControllerUpdateStatus = (
  variables: AdminAccountControllerUpdateStatusVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminAccountControllerUpdateStatusResponse,
    AdminAccountControllerUpdateStatusError,
    Schemas.AdminPatchAccountStatusReqDto,
    {},
    {},
    AdminAccountControllerUpdateStatusPathParams
  >({
    url: "/api/v1/admin/accounts/{id}/status",
    method: "patch",
    ...variables,
    signal,
  });

export const useAdminAccountControllerUpdateStatus = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminAccountControllerUpdateStatusResponse,
      AdminAccountControllerUpdateStatusError,
      AdminAccountControllerUpdateStatusVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminAccountControllerUpdateStatusResponse,
    AdminAccountControllerUpdateStatusError,
    AdminAccountControllerUpdateStatusVariables
  >({
    mutationFn: (variables: AdminAccountControllerUpdateStatusVariables) =>
      fetchAdminAccountControllerUpdateStatus({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminAccountControllerDeleteAccountPathParams = {
  id: string;
};

export type AdminAccountControllerDeleteAccountError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminAccountControllerDeleteAccountResponse = {
  data?: Schemas.AdminAccountResDto;
  statusCode?: number;
};

export type AdminAccountControllerDeleteAccountVariables = {
  pathParams: AdminAccountControllerDeleteAccountPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminAccountControllerDeleteAccount = (
  variables: AdminAccountControllerDeleteAccountVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminAccountControllerDeleteAccountResponse,
    AdminAccountControllerDeleteAccountError,
    undefined,
    {},
    {},
    AdminAccountControllerDeleteAccountPathParams
  >({
    url: "/api/v1/admin/accounts/{id}",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminAccountControllerDeleteAccount = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminAccountControllerDeleteAccountResponse,
      AdminAccountControllerDeleteAccountError,
      AdminAccountControllerDeleteAccountVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminAccountControllerDeleteAccountResponse,
    AdminAccountControllerDeleteAccountError,
    AdminAccountControllerDeleteAccountVariables
  >({
    mutationFn: (variables: AdminAccountControllerDeleteAccountVariables) =>
      fetchAdminAccountControllerDeleteAccount({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLocationNotesControllerCreatePathParams = {
  locationId: string;
};

export type AdminLocationNotesControllerCreateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationNotesControllerCreateResponse = {
  data?: Schemas.LocationNoteDto;
  statusCode?: number;
};

export type AdminLocationNotesControllerCreateVariables = {
  body: Schemas.CreateLocationNoteDto;
  pathParams: AdminLocationNotesControllerCreatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationNotesControllerCreate = (
  variables: AdminLocationNotesControllerCreateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationNotesControllerCreateResponse,
    AdminLocationNotesControllerCreateError,
    Schemas.CreateLocationNoteDto,
    {},
    {},
    AdminLocationNotesControllerCreatePathParams
  >({
    url: "/api/v2/admin/locations/{locationId}/location-notes",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminLocationNotesControllerCreate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationNotesControllerCreateResponse,
      AdminLocationNotesControllerCreateError,
      AdminLocationNotesControllerCreateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationNotesControllerCreateResponse,
    AdminLocationNotesControllerCreateError,
    AdminLocationNotesControllerCreateVariables
  >({
    mutationFn: (variables: AdminLocationNotesControllerCreateVariables) =>
      fetchAdminLocationNotesControllerCreate({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLocationNotesControllerFindAllPathParams = {
  locationId: string;
};

export type AdminLocationNotesControllerFindAllError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationNotesControllerFindAllResponse = {
  data?: Schemas.LocationNoteDto[];
  statusCode?: number;
};

export type AdminLocationNotesControllerFindAllVariables = {
  pathParams: AdminLocationNotesControllerFindAllPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationNotesControllerFindAll = (
  variables: AdminLocationNotesControllerFindAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationNotesControllerFindAllResponse,
    AdminLocationNotesControllerFindAllError,
    undefined,
    {},
    {},
    AdminLocationNotesControllerFindAllPathParams
  >({
    url: "/api/v2/admin/locations/{locationId}/location-notes",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationNotesControllerFindAll = <
  TData = AdminLocationNotesControllerFindAllResponse,
>(
  variables: AdminLocationNotesControllerFindAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationNotesControllerFindAllResponse,
      AdminLocationNotesControllerFindAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationNotesControllerFindAllResponse,
    AdminLocationNotesControllerFindAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/locations/{locationId}/location-notes",
      operationId: "adminLocationNotesControllerFindAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationNotesControllerFindAll(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationNotesControllerUpdatePathParams = {
  locationId: string;
  locationNoteId: string;
};

export type AdminLocationNotesControllerUpdateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationNotesControllerUpdateResponse = {
  data?: Schemas.LocationNoteDto;
  statusCode?: number;
};

export type AdminLocationNotesControllerUpdateVariables = {
  body?: Schemas.UpdateLocationNoteDto;
  pathParams: AdminLocationNotesControllerUpdatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationNotesControllerUpdate = (
  variables: AdminLocationNotesControllerUpdateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationNotesControllerUpdateResponse,
    AdminLocationNotesControllerUpdateError,
    Schemas.UpdateLocationNoteDto,
    {},
    {},
    AdminLocationNotesControllerUpdatePathParams
  >({
    url: "/api/v2/admin/locations/{locationId}/location-notes/{locationNoteId}",
    method: "patch",
    ...variables,
    signal,
  });

export const useAdminLocationNotesControllerUpdate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationNotesControllerUpdateResponse,
      AdminLocationNotesControllerUpdateError,
      AdminLocationNotesControllerUpdateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationNotesControllerUpdateResponse,
    AdminLocationNotesControllerUpdateError,
    AdminLocationNotesControllerUpdateVariables
  >({
    mutationFn: (variables: AdminLocationNotesControllerUpdateVariables) =>
      fetchAdminLocationNotesControllerUpdate({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLocationNotesControllerDeletePathParams = {
  locationNoteId: string;
};

export type AdminLocationNotesControllerDeleteError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationNotesControllerDeleteResponse = {
  data?: Schemas.LocationNoteDto[];
  statusCode?: number;
};

export type AdminLocationNotesControllerDeleteVariables = {
  pathParams: AdminLocationNotesControllerDeletePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationNotesControllerDelete = (
  variables: AdminLocationNotesControllerDeleteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationNotesControllerDeleteResponse,
    AdminLocationNotesControllerDeleteError,
    undefined,
    {},
    {},
    AdminLocationNotesControllerDeletePathParams
  >({
    url: "/api/v2/admin/locations/{locationId}/location-notes/{locationNoteId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminLocationNotesControllerDelete = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationNotesControllerDeleteResponse,
      AdminLocationNotesControllerDeleteError,
      AdminLocationNotesControllerDeleteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationNotesControllerDeleteResponse,
    AdminLocationNotesControllerDeleteError,
    AdminLocationNotesControllerDeleteVariables
  >({
    mutationFn: (variables: AdminLocationNotesControllerDeleteVariables) =>
      fetchAdminLocationNotesControllerDelete({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminPlowReportControllerDownloadPlowReportError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminPlowReportControllerDownloadPlowReportVariables = {
  body: Schemas.AdminPostPlowReportReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminPlowReportControllerDownloadPlowReport = (
  variables: AdminPlowReportControllerDownloadPlowReportVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    undefined,
    AdminPlowReportControllerDownloadPlowReportError,
    Schemas.AdminPostPlowReportReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v2/admin/locations/plow-report",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminPlowReportControllerDownloadPlowReport = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      AdminPlowReportControllerDownloadPlowReportError,
      AdminPlowReportControllerDownloadPlowReportVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    undefined,
    AdminPlowReportControllerDownloadPlowReportError,
    AdminPlowReportControllerDownloadPlowReportVariables
  >({
    mutationFn: (
      variables: AdminPlowReportControllerDownloadPlowReportVariables,
    ) =>
      fetchAdminPlowReportControllerDownloadPlowReport({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminPlowReportControllerDownloadDailyRoutePlowReportError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminPlowReportControllerDownloadDailyRoutePlowReportVariables = {
  body: Schemas.AdminPostDailyRoutePlowReportReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminPlowReportControllerDownloadDailyRoutePlowReport = (
  variables: AdminPlowReportControllerDownloadDailyRoutePlowReportVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    undefined,
    AdminPlowReportControllerDownloadDailyRoutePlowReportError,
    Schemas.AdminPostDailyRoutePlowReportReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v2/admin/routes/plow-report",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminPlowReportControllerDownloadDailyRoutePlowReport = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      AdminPlowReportControllerDownloadDailyRoutePlowReportError,
      AdminPlowReportControllerDownloadDailyRoutePlowReportVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    undefined,
    AdminPlowReportControllerDownloadDailyRoutePlowReportError,
    AdminPlowReportControllerDownloadDailyRoutePlowReportVariables
  >({
    mutationFn: (
      variables: AdminPlowReportControllerDownloadDailyRoutePlowReportVariables,
    ) =>
      fetchAdminPlowReportControllerDownloadDailyRoutePlowReport({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type DriverControllerGetAllQueryParams = {
  /**
   * The limit
   *
   * @default 10
   */
  limit?: number;
  page?: number;
  /**
   * The search query
   */
  search?: string;
  /**
   * The vehicle ID
   */
  vehicleId?: string;
  /**
   * The sort order
   */
  sort?: string;
  /**
   * The sort order
   */
  order?: "asc" | "desc";
  /**
   * The has vehicle
   */
  hasVehicle?: boolean;
  /**
   * The is available
   */
  isAvailable?: boolean;
  /**
   * IANA timezone used to format CSV datetime columns. Defaults to UTC when omitted.
   */
  timezone?: string;
};

export type DriverControllerGetAllError = Fetcher.ErrorWrapper<undefined>;

export type DriverControllerGetAllResponse = Schemas.PaginatedResponse & {
  data?: Schemas.AdminDriverResViewDto[];
};

export type DriverControllerGetAllVariables = {
  queryParams?: DriverControllerGetAllQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchDriverControllerGetAll = (
  variables: DriverControllerGetAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    DriverControllerGetAllResponse,
    DriverControllerGetAllError,
    undefined,
    {},
    DriverControllerGetAllQueryParams,
    {}
  >({ url: "/api/v1/admin/drivers", method: "get", ...variables, signal });

export const useDriverControllerGetAll = <
  TData = DriverControllerGetAllResponse,
>(
  variables: DriverControllerGetAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      DriverControllerGetAllResponse,
      DriverControllerGetAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    DriverControllerGetAllResponse,
    DriverControllerGetAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/drivers",
      operationId: "driverControllerGetAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchDriverControllerGetAll({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type DriverControllerCreateError = Fetcher.ErrorWrapper<undefined>;

export type DriverControllerCreateResponse = {
  data?: Schemas.AdminDriverResDto;
  statusCode?: number;
};

export type DriverControllerCreateVariables = {
  body?: Schemas.AdminPostDriverReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchDriverControllerCreate = (
  variables: DriverControllerCreateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    DriverControllerCreateResponse,
    DriverControllerCreateError,
    Schemas.AdminPostDriverReqDto,
    {},
    {},
    {}
  >({ url: "/api/v1/admin/drivers", method: "post", ...variables, signal });

export const useDriverControllerCreate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      DriverControllerCreateResponse,
      DriverControllerCreateError,
      DriverControllerCreateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    DriverControllerCreateResponse,
    DriverControllerCreateError,
    DriverControllerCreateVariables
  >({
    mutationFn: (variables: DriverControllerCreateVariables) =>
      fetchDriverControllerCreate({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type DriverControllerGetAllCsvQueryParams = {
  /**
   * The limit
   *
   * @default 10
   */
  limit?: number;
  page?: number;
  /**
   * The search query
   */
  search?: string;
  /**
   * The vehicle ID
   */
  vehicleId?: string;
  /**
   * The sort order
   */
  sort?: string;
  /**
   * The sort order
   */
  order?: "asc" | "desc";
  /**
   * The has vehicle
   */
  hasVehicle?: boolean;
  /**
   * The is available
   */
  isAvailable?: boolean;
  /**
   * IANA timezone used to format CSV datetime columns. Defaults to UTC when omitted.
   */
  timezone?: string;
};

export type DriverControllerGetAllCsvError = Fetcher.ErrorWrapper<undefined>;

export type DriverControllerGetAllCsvResponse = {
  data?: Schemas.String;
  statusCode?: number;
};

export type DriverControllerGetAllCsvVariables = {
  queryParams?: DriverControllerGetAllCsvQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchDriverControllerGetAllCsv = (
  variables: DriverControllerGetAllCsvVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    DriverControllerGetAllCsvResponse,
    DriverControllerGetAllCsvError,
    undefined,
    {},
    DriverControllerGetAllCsvQueryParams,
    {}
  >({ url: "/api/v1/admin/drivers/csv", method: "get", ...variables, signal });

export const useDriverControllerGetAllCsv = <
  TData = DriverControllerGetAllCsvResponse,
>(
  variables: DriverControllerGetAllCsvVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      DriverControllerGetAllCsvResponse,
      DriverControllerGetAllCsvError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    DriverControllerGetAllCsvResponse,
    DriverControllerGetAllCsvError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/drivers/csv",
      operationId: "driverControllerGetAllCsv",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchDriverControllerGetAllCsv(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type DriverControllerUpdateByIdPathParams = {
  driverId: string;
};

export type DriverControllerUpdateByIdError = Fetcher.ErrorWrapper<undefined>;

export type DriverControllerUpdateByIdResponse = {
  data?: Schemas.AdminDriverResDto;
  statusCode?: number;
};

export type DriverControllerUpdateByIdVariables = {
  body: Schemas.AdminPutDriverReqDto;
  pathParams: DriverControllerUpdateByIdPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchDriverControllerUpdateById = (
  variables: DriverControllerUpdateByIdVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    DriverControllerUpdateByIdResponse,
    DriverControllerUpdateByIdError,
    Schemas.AdminPutDriverReqDto,
    {},
    {},
    DriverControllerUpdateByIdPathParams
  >({
    url: "/api/v1/admin/drivers/{driverId}",
    method: "put",
    ...variables,
    signal,
  });

export const useDriverControllerUpdateById = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      DriverControllerUpdateByIdResponse,
      DriverControllerUpdateByIdError,
      DriverControllerUpdateByIdVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    DriverControllerUpdateByIdResponse,
    DriverControllerUpdateByIdError,
    DriverControllerUpdateByIdVariables
  >({
    mutationFn: (variables: DriverControllerUpdateByIdVariables) =>
      fetchDriverControllerUpdateById({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type DriverControllerGetDriverByIdPathParams = {
  driverId: string;
};

export type DriverControllerGetDriverByIdError =
  Fetcher.ErrorWrapper<undefined>;

export type DriverControllerGetDriverByIdResponse = {
  data?: Schemas.AdminDriverResDto;
  statusCode?: number;
};

export type DriverControllerGetDriverByIdVariables = {
  pathParams: DriverControllerGetDriverByIdPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchDriverControllerGetDriverById = (
  variables: DriverControllerGetDriverByIdVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    DriverControllerGetDriverByIdResponse,
    DriverControllerGetDriverByIdError,
    undefined,
    {},
    {},
    DriverControllerGetDriverByIdPathParams
  >({
    url: "/api/v1/admin/drivers/{driverId}",
    method: "get",
    ...variables,
    signal,
  });

export const useDriverControllerGetDriverById = <
  TData = DriverControllerGetDriverByIdResponse,
>(
  variables: DriverControllerGetDriverByIdVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      DriverControllerGetDriverByIdResponse,
      DriverControllerGetDriverByIdError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    DriverControllerGetDriverByIdResponse,
    DriverControllerGetDriverByIdError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/drivers/{driverId}",
      operationId: "driverControllerGetDriverById",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchDriverControllerGetDriverById(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type DriverControllerDeletePathParams = {
  driverId: string;
};

export type DriverControllerDeleteError = Fetcher.ErrorWrapper<undefined>;

export type DriverControllerDeleteResponse = {
  data?: Schemas.AdminDeleteDriverResDto;
  statusCode?: number;
};

export type DriverControllerDeleteVariables = {
  pathParams: DriverControllerDeletePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchDriverControllerDelete = (
  variables: DriverControllerDeleteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    DriverControllerDeleteResponse,
    DriverControllerDeleteError,
    undefined,
    {},
    {},
    DriverControllerDeletePathParams
  >({
    url: "/api/v1/admin/drivers/{driverId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useDriverControllerDelete = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      DriverControllerDeleteResponse,
      DriverControllerDeleteError,
      DriverControllerDeleteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    DriverControllerDeleteResponse,
    DriverControllerDeleteError,
    DriverControllerDeleteVariables
  >({
    mutationFn: (variables: DriverControllerDeleteVariables) =>
      fetchDriverControllerDelete({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type DriverControllerSendInvitePathParams = {
  driverId: string;
};

export type DriverControllerSendInviteError = Fetcher.ErrorWrapper<undefined>;

export type DriverControllerSendInviteResponse = {
  data?: Schemas.AdminDriverResDto;
  statusCode?: number;
};

export type DriverControllerSendInviteVariables = {
  pathParams: DriverControllerSendInvitePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchDriverControllerSendInvite = (
  variables: DriverControllerSendInviteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    DriverControllerSendInviteResponse,
    DriverControllerSendInviteError,
    undefined,
    {},
    {},
    DriverControllerSendInvitePathParams
  >({
    url: "/api/v1/admin/drivers/{driverId}/send-invite",
    method: "post",
    ...variables,
    signal,
  });

export const useDriverControllerSendInvite = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      DriverControllerSendInviteResponse,
      DriverControllerSendInviteError,
      DriverControllerSendInviteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    DriverControllerSendInviteResponse,
    DriverControllerSendInviteError,
    DriverControllerSendInviteVariables
  >({
    mutationFn: (variables: DriverControllerSendInviteVariables) =>
      fetchDriverControllerSendInvite({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type DriverControllerUpdateDriverAppAccessPathParams = {
  driverId: string;
};

export type DriverControllerUpdateDriverAppAccessError =
  Fetcher.ErrorWrapper<undefined>;

export type DriverControllerUpdateDriverAppAccessResponse = {
  data?: Schemas.AdminDriverResDto;
  statusCode?: number;
};

export type DriverControllerUpdateDriverAppAccessVariables = {
  body: Schemas.AdminPutDriverAppAccessReqDto;
  pathParams: DriverControllerUpdateDriverAppAccessPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchDriverControllerUpdateDriverAppAccess = (
  variables: DriverControllerUpdateDriverAppAccessVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    DriverControllerUpdateDriverAppAccessResponse,
    DriverControllerUpdateDriverAppAccessError,
    Schemas.AdminPutDriverAppAccessReqDto,
    {},
    {},
    DriverControllerUpdateDriverAppAccessPathParams
  >({
    url: "/api/v1/admin/drivers/{driverId}/app-access",
    method: "patch",
    ...variables,
    signal,
  });

export const useDriverControllerUpdateDriverAppAccess = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      DriverControllerUpdateDriverAppAccessResponse,
      DriverControllerUpdateDriverAppAccessError,
      DriverControllerUpdateDriverAppAccessVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    DriverControllerUpdateDriverAppAccessResponse,
    DriverControllerUpdateDriverAppAccessError,
    DriverControllerUpdateDriverAppAccessVariables
  >({
    mutationFn: (variables: DriverControllerUpdateDriverAppAccessVariables) =>
      fetchDriverControllerUpdateDriverAppAccess({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type GuestSentNotificationRecipientControllerGetAllQueryParams = {
  limit?: number;
  page?: number;
  /**
   * Subscription type
   */
  subscriptionType?: "workwave" | "noaa" | "notification";
};

export type GuestSentNotificationRecipientControllerGetAllError =
  Fetcher.ErrorWrapper<undefined>;

export type GuestSentNotificationRecipientControllerGetAllResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.GuestSentNotificationRecipientResDto[];
  };

export type GuestSentNotificationRecipientControllerGetAllVariables = {
  queryParams?: GuestSentNotificationRecipientControllerGetAllQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchGuestSentNotificationRecipientControllerGetAll = (
  variables: GuestSentNotificationRecipientControllerGetAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    GuestSentNotificationRecipientControllerGetAllResponse,
    GuestSentNotificationRecipientControllerGetAllError,
    undefined,
    {},
    GuestSentNotificationRecipientControllerGetAllQueryParams,
    {}
  >({
    url: "/api/v1/guest/sent-notification-recipients",
    method: "get",
    ...variables,
    signal,
  });

export const useGuestSentNotificationRecipientControllerGetAll = <
  TData = GuestSentNotificationRecipientControllerGetAllResponse,
>(
  variables: GuestSentNotificationRecipientControllerGetAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GuestSentNotificationRecipientControllerGetAllResponse,
      GuestSentNotificationRecipientControllerGetAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    GuestSentNotificationRecipientControllerGetAllResponse,
    GuestSentNotificationRecipientControllerGetAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/guest/sent-notification-recipients",
      operationId: "guestSentNotificationRecipientControllerGetAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGuestSentNotificationRecipientControllerGetAll(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminAuthControllerLoginError = Fetcher.ErrorWrapper<undefined>;

export type AdminAuthControllerLoginResponse = {
  data?: Schemas.AdminLoginResDto;
  statusCode?: number;
};

export type AdminAuthControllerLoginVariables = {
  body: Schemas.AdminLoginReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminAuthControllerLogin = (
  variables: AdminAuthControllerLoginVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminAuthControllerLoginResponse,
    AdminAuthControllerLoginError,
    Schemas.AdminLoginReqDto,
    {},
    {},
    {}
  >({ url: "/api/v1/admin/auth/login", method: "post", ...variables, signal });

export const useAdminAuthControllerLogin = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminAuthControllerLoginResponse,
      AdminAuthControllerLoginError,
      AdminAuthControllerLoginVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminAuthControllerLoginResponse,
    AdminAuthControllerLoginError,
    AdminAuthControllerLoginVariables
  >({
    mutationFn: (variables: AdminAuthControllerLoginVariables) =>
      fetchAdminAuthControllerLogin({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type AdminAuthControllerRefreshError = Fetcher.ErrorWrapper<undefined>;

export type AdminAuthControllerRefreshResponse = {
  data?: Schemas.AdminLoginResDto;
  statusCode?: number;
};

export type AdminAuthControllerRefreshVariables = {
  body: Schemas.AdminRefreshReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminAuthControllerRefresh = (
  variables: AdminAuthControllerRefreshVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminAuthControllerRefreshResponse,
    AdminAuthControllerRefreshError,
    Schemas.AdminRefreshReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/auth/refresh",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminAuthControllerRefresh = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminAuthControllerRefreshResponse,
      AdminAuthControllerRefreshError,
      AdminAuthControllerRefreshVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminAuthControllerRefreshResponse,
    AdminAuthControllerRefreshError,
    AdminAuthControllerRefreshVariables
  >({
    mutationFn: (variables: AdminAuthControllerRefreshVariables) =>
      fetchAdminAuthControllerRefresh({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type AdminAuthControllerGetSessionError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminAuthControllerGetSessionResponse = {
  data?: Schemas.AdminSessionResDto;
  statusCode?: number;
};

export type AdminAuthControllerGetSessionVariables =
  EstabrookContext["fetcherOptions"];

export const fetchAdminAuthControllerGetSession = (
  variables: AdminAuthControllerGetSessionVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminAuthControllerGetSessionResponse,
    AdminAuthControllerGetSessionError,
    undefined,
    {},
    {},
    {}
  >({ url: "/api/v1/admin/auth/session", method: "get", ...variables, signal });

export const useAdminAuthControllerGetSession = <
  TData = AdminAuthControllerGetSessionResponse,
>(
  variables: AdminAuthControllerGetSessionVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminAuthControllerGetSessionResponse,
      AdminAuthControllerGetSessionError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminAuthControllerGetSessionResponse,
    AdminAuthControllerGetSessionError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/auth/session",
      operationId: "adminAuthControllerGetSession",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminAuthControllerGetSession(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminAuthControllerForgotPasswordError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminAuthControllerForgotPasswordResponse = {
  data?: Schemas.AdminEmailResDto;
  statusCode?: number;
};

export type AdminAuthControllerForgotPasswordVariables = {
  body: Schemas.AdminForgotPasswordReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminAuthControllerForgotPassword = (
  variables: AdminAuthControllerForgotPasswordVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminAuthControllerForgotPasswordResponse,
    AdminAuthControllerForgotPasswordError,
    Schemas.AdminForgotPasswordReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/auth/password/forgot",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminAuthControllerForgotPassword = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminAuthControllerForgotPasswordResponse,
      AdminAuthControllerForgotPasswordError,
      AdminAuthControllerForgotPasswordVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminAuthControllerForgotPasswordResponse,
    AdminAuthControllerForgotPasswordError,
    AdminAuthControllerForgotPasswordVariables
  >({
    mutationFn: (variables: AdminAuthControllerForgotPasswordVariables) =>
      fetchAdminAuthControllerForgotPassword({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminAuthControllerResetPasswordError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminAuthControllerResetPasswordResponse = {
  data?: Schemas.AdminLoginResDto;
  statusCode?: number;
};

export type AdminAuthControllerResetPasswordVariables = {
  body: Schemas.AdminResetPasswordReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminAuthControllerResetPassword = (
  variables: AdminAuthControllerResetPasswordVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminAuthControllerResetPasswordResponse,
    AdminAuthControllerResetPasswordError,
    Schemas.AdminResetPasswordReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/auth/password/reset",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminAuthControllerResetPassword = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminAuthControllerResetPasswordResponse,
      AdminAuthControllerResetPasswordError,
      AdminAuthControllerResetPasswordVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminAuthControllerResetPasswordResponse,
    AdminAuthControllerResetPasswordError,
    AdminAuthControllerResetPasswordVariables
  >({
    mutationFn: (variables: AdminAuthControllerResetPasswordVariables) =>
      fetchAdminAuthControllerResetPassword({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminAuthControllerRegisterInviteError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminAuthControllerRegisterInviteResponse = {
  data?: Schemas.AdminLoginResDto;
  statusCode?: number;
};

export type AdminAuthControllerRegisterInviteVariables = {
  body: Schemas.AdminRegisterInviteReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminAuthControllerRegisterInvite = (
  variables: AdminAuthControllerRegisterInviteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminAuthControllerRegisterInviteResponse,
    AdminAuthControllerRegisterInviteError,
    Schemas.AdminRegisterInviteReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/auth/register-invite",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminAuthControllerRegisterInvite = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminAuthControllerRegisterInviteResponse,
      AdminAuthControllerRegisterInviteError,
      AdminAuthControllerRegisterInviteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminAuthControllerRegisterInviteResponse,
    AdminAuthControllerRegisterInviteError,
    AdminAuthControllerRegisterInviteVariables
  >({
    mutationFn: (variables: AdminAuthControllerRegisterInviteVariables) =>
      fetchAdminAuthControllerRegisterInvite({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminAuthControllerRegisterAdminAccountError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminAuthControllerRegisterAdminAccountResponse = {
  data?: Schemas.AdminLoginResDto;
  statusCode?: number;
};

export type AdminAuthControllerRegisterAdminAccountVariables = {
  body: Schemas.AdminRegisterAdminReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminAuthControllerRegisterAdminAccount = (
  variables: AdminAuthControllerRegisterAdminAccountVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminAuthControllerRegisterAdminAccountResponse,
    AdminAuthControllerRegisterAdminAccountError,
    Schemas.AdminRegisterAdminReqDto,
    {},
    {},
    {}
  >({ url: "/api/v1/admin/auth", method: "post", ...variables, signal });

export const useAdminAuthControllerRegisterAdminAccount = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminAuthControllerRegisterAdminAccountResponse,
      AdminAuthControllerRegisterAdminAccountError,
      AdminAuthControllerRegisterAdminAccountVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminAuthControllerRegisterAdminAccountResponse,
    AdminAuthControllerRegisterAdminAccountError,
    AdminAuthControllerRegisterAdminAccountVariables
  >({
    mutationFn: (variables: AdminAuthControllerRegisterAdminAccountVariables) =>
      fetchAdminAuthControllerRegisterAdminAccount({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminImpersonationControllerStartPathParams = {
  orgId: string;
};

export type AdminImpersonationControllerStartError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminImpersonationControllerStartResponse = {
  data?: Schemas.AdminLoginResDto;
  statusCode?: number;
};

export type AdminImpersonationControllerStartVariables = {
  pathParams: AdminImpersonationControllerStartPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminImpersonationControllerStart = (
  variables: AdminImpersonationControllerStartVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminImpersonationControllerStartResponse,
    AdminImpersonationControllerStartError,
    undefined,
    {},
    {},
    AdminImpersonationControllerStartPathParams
  >({
    url: "/api/v1/admin/organizations/{orgId}/impersonate",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminImpersonationControllerStart = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminImpersonationControllerStartResponse,
      AdminImpersonationControllerStartError,
      AdminImpersonationControllerStartVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminImpersonationControllerStartResponse,
    AdminImpersonationControllerStartError,
    AdminImpersonationControllerStartVariables
  >({
    mutationFn: (variables: AdminImpersonationControllerStartVariables) =>
      fetchAdminImpersonationControllerStart({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminImpersonationControllerStopError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminImpersonationControllerStopResponse = {
  data?: Schemas.AdminLoginResDto;
  statusCode?: number;
};

export type AdminImpersonationControllerStopVariables =
  EstabrookContext["fetcherOptions"];

export const fetchAdminImpersonationControllerStop = (
  variables: AdminImpersonationControllerStopVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminImpersonationControllerStopResponse,
    AdminImpersonationControllerStopError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/impersonate/stop",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminImpersonationControllerStop = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminImpersonationControllerStopResponse,
      AdminImpersonationControllerStopError,
      AdminImpersonationControllerStopVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminImpersonationControllerStopResponse,
    AdminImpersonationControllerStopError,
    AdminImpersonationControllerStopVariables
  >({
    mutationFn: (variables: AdminImpersonationControllerStopVariables) =>
      fetchAdminImpersonationControllerStop({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AppAuthControllerResetPasswordError =
  Fetcher.ErrorWrapper<undefined>;

export type AppAuthControllerResetPasswordResponse = {
  data?: Schemas.AppLoginResDto;
  statusCode?: number;
};

export type AppAuthControllerResetPasswordVariables = {
  body: Schemas.AppResetPasswordReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAppAuthControllerResetPassword = (
  variables: AppAuthControllerResetPasswordVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AppAuthControllerResetPasswordResponse,
    AppAuthControllerResetPasswordError,
    Schemas.AppResetPasswordReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v1/app/auth/password/reset",
    method: "post",
    ...variables,
    signal,
  });

export const useAppAuthControllerResetPassword = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AppAuthControllerResetPasswordResponse,
      AppAuthControllerResetPasswordError,
      AppAuthControllerResetPasswordVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AppAuthControllerResetPasswordResponse,
    AppAuthControllerResetPasswordError,
    AppAuthControllerResetPasswordVariables
  >({
    mutationFn: (variables: AppAuthControllerResetPasswordVariables) =>
      fetchAppAuthControllerResetPassword({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type DriverAuthControllerRegisterError = Fetcher.ErrorWrapper<undefined>;

export type DriverAuthControllerRegisterResponse = {
  data?: Schemas.DriverLoginResDto;
  statusCode?: number;
};

export type DriverAuthControllerRegisterVariables = {
  body: Schemas.DriverRegisterReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchDriverAuthControllerRegister = (
  variables: DriverAuthControllerRegisterVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    DriverAuthControllerRegisterResponse,
    DriverAuthControllerRegisterError,
    Schemas.DriverRegisterReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v2/driver/auth/register",
    method: "post",
    ...variables,
    signal,
  });

export const useDriverAuthControllerRegister = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      DriverAuthControllerRegisterResponse,
      DriverAuthControllerRegisterError,
      DriverAuthControllerRegisterVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    DriverAuthControllerRegisterResponse,
    DriverAuthControllerRegisterError,
    DriverAuthControllerRegisterVariables
  >({
    mutationFn: (variables: DriverAuthControllerRegisterVariables) =>
      fetchDriverAuthControllerRegister({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type DriverAuthControllerResetPasswordError =
  Fetcher.ErrorWrapper<undefined>;

export type DriverAuthControllerResetPasswordResponse = {
  data?: Schemas.DriverLoginResDto;
  statusCode?: number;
};

export type DriverAuthControllerResetPasswordVariables = {
  body: Schemas.DriverResetPasswordReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchDriverAuthControllerResetPassword = (
  variables: DriverAuthControllerResetPasswordVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    DriverAuthControllerResetPasswordResponse,
    DriverAuthControllerResetPasswordError,
    Schemas.DriverResetPasswordReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v2/driver/auth/password/reset",
    method: "post",
    ...variables,
    signal,
  });

export const useDriverAuthControllerResetPassword = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      DriverAuthControllerResetPasswordResponse,
      DriverAuthControllerResetPasswordError,
      DriverAuthControllerResetPasswordVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    DriverAuthControllerResetPasswordResponse,
    DriverAuthControllerResetPasswordError,
    DriverAuthControllerResetPasswordVariables
  >({
    mutationFn: (variables: DriverAuthControllerResetPasswordVariables) =>
      fetchDriverAuthControllerResetPassword({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminInviteControllerInviteQueryParams = {
  /**
   * Super admins: required when inviting an organization admin without an assigned organization; optional when inviting to another organization or omit to use your assigned organization. For super_admin invites, omit for platform-wide access or set to your organization to affiliate the invitee with your org.
   */
  organizationId?: string;
};

export type AdminInviteControllerInviteError = Fetcher.ErrorWrapper<undefined>;

export type AdminInviteControllerInviteResponse = {
  data?: Schemas.AdminAccountResDto;
  statusCode?: number;
};

export type AdminInviteControllerInviteVariables = {
  body: Schemas.AdminPostAdminInviteReqDto;
  queryParams?: AdminInviteControllerInviteQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminInviteControllerInvite = (
  variables: AdminInviteControllerInviteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminInviteControllerInviteResponse,
    AdminInviteControllerInviteError,
    Schemas.AdminPostAdminInviteReqDto,
    {},
    AdminInviteControllerInviteQueryParams,
    {}
  >({ url: "/api/v1/admin/invites", method: "post", ...variables, signal });

export const useAdminInviteControllerInvite = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminInviteControllerInviteResponse,
      AdminInviteControllerInviteError,
      AdminInviteControllerInviteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminInviteControllerInviteResponse,
    AdminInviteControllerInviteError,
    AdminInviteControllerInviteVariables
  >({
    mutationFn: (variables: AdminInviteControllerInviteVariables) =>
      fetchAdminInviteControllerInvite({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type AdminInviteControllerResendInvitePathParams = {
  id: string;
};

export type AdminInviteControllerResendInviteError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminInviteControllerResendInviteResponse = {
  data?: Schemas.AdminAccountResDto;
  statusCode?: number;
};

export type AdminInviteControllerResendInviteVariables = {
  pathParams: AdminInviteControllerResendInvitePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminInviteControllerResendInvite = (
  variables: AdminInviteControllerResendInviteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminInviteControllerResendInviteResponse,
    AdminInviteControllerResendInviteError,
    undefined,
    {},
    {},
    AdminInviteControllerResendInvitePathParams
  >({
    url: "/api/v1/admin/invites/{id}/resend",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminInviteControllerResendInvite = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminInviteControllerResendInviteResponse,
      AdminInviteControllerResendInviteError,
      AdminInviteControllerResendInviteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminInviteControllerResendInviteResponse,
    AdminInviteControllerResendInviteError,
    AdminInviteControllerResendInviteVariables
  >({
    mutationFn: (variables: AdminInviteControllerResendInviteVariables) =>
      fetchAdminInviteControllerResendInvite({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminInviteControllerRemoveAccessPathParams = {
  id: string;
};

export type AdminInviteControllerRemoveAccessError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminInviteControllerRemoveAccessResponse = {
  data?: Schemas.AdminAccountResDto;
  statusCode?: number;
};

export type AdminInviteControllerRemoveAccessVariables = {
  pathParams: AdminInviteControllerRemoveAccessPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminInviteControllerRemoveAccess = (
  variables: AdminInviteControllerRemoveAccessVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminInviteControllerRemoveAccessResponse,
    AdminInviteControllerRemoveAccessError,
    undefined,
    {},
    {},
    AdminInviteControllerRemoveAccessPathParams
  >({
    url: "/api/v1/admin/invites/{id}",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminInviteControllerRemoveAccess = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminInviteControllerRemoveAccessResponse,
      AdminInviteControllerRemoveAccessError,
      AdminInviteControllerRemoveAccessVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminInviteControllerRemoveAccessResponse,
    AdminInviteControllerRemoveAccessError,
    AdminInviteControllerRemoveAccessVariables
  >({
    mutationFn: (variables: AdminInviteControllerRemoveAccessVariables) =>
      fetchAdminInviteControllerRemoveAccess({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLeadControllerGetAllQueryParams = {
  limit?: number;
  page?: number;
  /**
   * Lead list filter
   *
   * @default allContacts
   */
  filter?: "allContacts" | "potentialCustomers" | "currentCustomers";
  /**
   * Sort field
   */
  sort?: "fullName" | "address" | "phoneNumber" | "createdAt";
  /**
   * Sort order
   */
  order?: "asc" | "desc";
  /**
   * Search query
   */
  search?: string;
  /**
   * Filter leads created on or after this date (YYYY-MM-DD)
   *
   * @format date
   */
  fromDate?: string;
  /**
   * Filter leads created on or before this date (YYYY-MM-DD)
   *
   * @format date
   */
  toDate?: string;
};

export type AdminLeadControllerGetAllError = Fetcher.ErrorWrapper<undefined>;

export type AdminLeadControllerGetAllResponse = {
  metaData?: Schemas.AdminLeadsPaginatedMetaDataDto;
  data?: Schemas.AdminLeadListResDto[];
};

export type AdminLeadControllerGetAllVariables = {
  queryParams?: AdminLeadControllerGetAllQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLeadControllerGetAll = (
  variables: AdminLeadControllerGetAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLeadControllerGetAllResponse,
    AdminLeadControllerGetAllError,
    undefined,
    {},
    AdminLeadControllerGetAllQueryParams,
    {}
  >({ url: "/api/v1/admin/leads", method: "get", ...variables, signal });

export const useAdminLeadControllerGetAll = <
  TData = AdminLeadControllerGetAllResponse,
>(
  variables: AdminLeadControllerGetAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLeadControllerGetAllResponse,
      AdminLeadControllerGetAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLeadControllerGetAllResponse,
    AdminLeadControllerGetAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/leads",
      operationId: "adminLeadControllerGetAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLeadControllerGetAll(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLeadControllerCreateError = Fetcher.ErrorWrapper<undefined>;

export type AdminLeadControllerCreateResponse = {
  data?: Schemas.AdminLeadResDto;
  statusCode?: number;
};

export type AdminLeadControllerCreateVariables = {
  body: Schemas.AdminPostLeadReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLeadControllerCreate = (
  variables: AdminLeadControllerCreateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLeadControllerCreateResponse,
    AdminLeadControllerCreateError,
    Schemas.AdminPostLeadReqDto,
    {},
    {},
    {}
  >({ url: "/api/v1/admin/leads", method: "post", ...variables, signal });

export const useAdminLeadControllerCreate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLeadControllerCreateResponse,
      AdminLeadControllerCreateError,
      AdminLeadControllerCreateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLeadControllerCreateResponse,
    AdminLeadControllerCreateError,
    AdminLeadControllerCreateVariables
  >({
    mutationFn: (variables: AdminLeadControllerCreateVariables) =>
      fetchAdminLeadControllerCreate({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type AdminLeadControllerDeleteManyError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLeadControllerDeleteManyResponse = {
  data?: Schemas.AdminDeleteManyLeadsResDto;
  statusCode?: number;
};

export type AdminLeadControllerDeleteManyVariables = {
  body: Schemas.AdminDeleteManyLeadsReqDto;
} & EstabrookContext["fetcherOptions"];

/**
 * Soft-deletes unconverted leads. Leads linked to a customer, missing, or already deleted are returned in skippedIds.
 */
export const fetchAdminLeadControllerDeleteMany = (
  variables: AdminLeadControllerDeleteManyVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLeadControllerDeleteManyResponse,
    AdminLeadControllerDeleteManyError,
    Schemas.AdminDeleteManyLeadsReqDto,
    {},
    {},
    {}
  >({ url: "/api/v1/admin/leads", method: "delete", ...variables, signal });

/**
 * Soft-deletes unconverted leads. Leads linked to a customer, missing, or already deleted are returned in skippedIds.
 */
export const useAdminLeadControllerDeleteMany = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLeadControllerDeleteManyResponse,
      AdminLeadControllerDeleteManyError,
      AdminLeadControllerDeleteManyVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLeadControllerDeleteManyResponse,
    AdminLeadControllerDeleteManyError,
    AdminLeadControllerDeleteManyVariables
  >({
    mutationFn: (variables: AdminLeadControllerDeleteManyVariables) =>
      fetchAdminLeadControllerDeleteMany({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type AdminLeadControllerGetAllCSVQueryParams = {
  limit?: number;
  page?: number;
  /**
   * Lead list filter
   *
   * @default allContacts
   */
  filter?: "allContacts" | "potentialCustomers" | "currentCustomers";
  /**
   * Sort field
   */
  sort?: "fullName" | "address" | "phoneNumber" | "createdAt";
  /**
   * Sort order
   */
  order?: "asc" | "desc";
  /**
   * Search query
   */
  search?: string;
  /**
   * Filter leads created on or after this date (YYYY-MM-DD)
   *
   * @format date
   */
  fromDate?: string;
  /**
   * Filter leads created on or before this date (YYYY-MM-DD)
   *
   * @format date
   */
  toDate?: string;
};

export type AdminLeadControllerGetAllCSVError = Fetcher.ErrorWrapper<undefined>;

export type AdminLeadControllerGetAllCSVResponse = {
  data?: Schemas.String;
  statusCode?: number;
};

export type AdminLeadControllerGetAllCSVVariables = {
  queryParams?: AdminLeadControllerGetAllCSVQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLeadControllerGetAllCSV = (
  variables: AdminLeadControllerGetAllCSVVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLeadControllerGetAllCSVResponse,
    AdminLeadControllerGetAllCSVError,
    undefined,
    {},
    AdminLeadControllerGetAllCSVQueryParams,
    {}
  >({ url: "/api/v1/admin/leads/csv", method: "get", ...variables, signal });

export const useAdminLeadControllerGetAllCSV = <
  TData = AdminLeadControllerGetAllCSVResponse,
>(
  variables: AdminLeadControllerGetAllCSVVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLeadControllerGetAllCSVResponse,
      AdminLeadControllerGetAllCSVError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLeadControllerGetAllCSVResponse,
    AdminLeadControllerGetAllCSVError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/leads/csv",
      operationId: "adminLeadControllerGetAllCSV",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLeadControllerGetAllCSV(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLeadControllerImportFromCsvError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLeadControllerImportFromCsvResponse = {
  data?: Schemas.AdminImportLeadsCsvResDto;
  statusCode?: number;
};

export type AdminLeadControllerImportFromCsvRequestBody = {
  /**
   * @format binary
   */
  file?: Blob;
};

export type AdminLeadControllerImportFromCsvVariables = {
  body?: AdminLeadControllerImportFromCsvRequestBody;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLeadControllerImportFromCsv = (
  variables: AdminLeadControllerImportFromCsvVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLeadControllerImportFromCsvResponse,
    AdminLeadControllerImportFromCsvError,
    AdminLeadControllerImportFromCsvRequestBody,
    {},
    {},
    {}
  >({ url: "/api/v1/admin/leads/csv", method: "post", ...variables, signal });

export const useAdminLeadControllerImportFromCsv = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLeadControllerImportFromCsvResponse,
      AdminLeadControllerImportFromCsvError,
      AdminLeadControllerImportFromCsvVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLeadControllerImportFromCsvResponse,
    AdminLeadControllerImportFromCsvError,
    AdminLeadControllerImportFromCsvVariables
  >({
    mutationFn: (variables: AdminLeadControllerImportFromCsvVariables) =>
      fetchAdminLeadControllerImportFromCsv({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLeadControllerGetCsvTemplateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLeadControllerGetCsvTemplateResponse = {
  data?: Schemas.String;
  statusCode?: number;
};

export type AdminLeadControllerGetCsvTemplateVariables =
  EstabrookContext["fetcherOptions"];

export const fetchAdminLeadControllerGetCsvTemplate = (
  variables: AdminLeadControllerGetCsvTemplateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLeadControllerGetCsvTemplateResponse,
    AdminLeadControllerGetCsvTemplateError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/leads/csv-template",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLeadControllerGetCsvTemplate = <
  TData = AdminLeadControllerGetCsvTemplateResponse,
>(
  variables: AdminLeadControllerGetCsvTemplateVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLeadControllerGetCsvTemplateResponse,
      AdminLeadControllerGetCsvTemplateError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLeadControllerGetCsvTemplateResponse,
    AdminLeadControllerGetCsvTemplateError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/leads/csv-template",
      operationId: "adminLeadControllerGetCsvTemplate",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLeadControllerGetCsvTemplate(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLeadControllerGetOnePathParams = {
  leadId: string;
};

export type AdminLeadControllerGetOneError = Fetcher.ErrorWrapper<undefined>;

export type AdminLeadControllerGetOneResponse = {
  data?: Schemas.AdminLeadResDto;
  statusCode?: number;
};

export type AdminLeadControllerGetOneVariables = {
  pathParams: AdminLeadControllerGetOnePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLeadControllerGetOne = (
  variables: AdminLeadControllerGetOneVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLeadControllerGetOneResponse,
    AdminLeadControllerGetOneError,
    undefined,
    {},
    {},
    AdminLeadControllerGetOnePathParams
  >({
    url: "/api/v1/admin/leads/{leadId}",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLeadControllerGetOne = <
  TData = AdminLeadControllerGetOneResponse,
>(
  variables: AdminLeadControllerGetOneVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLeadControllerGetOneResponse,
      AdminLeadControllerGetOneError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLeadControllerGetOneResponse,
    AdminLeadControllerGetOneError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/leads/{leadId}",
      operationId: "adminLeadControllerGetOne",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLeadControllerGetOne(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLeadControllerUpdatePathParams = {
  leadId: string;
};

export type AdminLeadControllerUpdateError = Fetcher.ErrorWrapper<undefined>;

export type AdminLeadControllerUpdateResponse = {
  data?: Schemas.AdminLeadResDto;
  statusCode?: number;
};

export type AdminLeadControllerUpdateVariables = {
  body?: Schemas.AdminPutLeadReqDto;
  pathParams: AdminLeadControllerUpdatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLeadControllerUpdate = (
  variables: AdminLeadControllerUpdateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLeadControllerUpdateResponse,
    AdminLeadControllerUpdateError,
    Schemas.AdminPutLeadReqDto,
    {},
    {},
    AdminLeadControllerUpdatePathParams
  >({
    url: "/api/v1/admin/leads/{leadId}",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminLeadControllerUpdate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLeadControllerUpdateResponse,
      AdminLeadControllerUpdateError,
      AdminLeadControllerUpdateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLeadControllerUpdateResponse,
    AdminLeadControllerUpdateError,
    AdminLeadControllerUpdateVariables
  >({
    mutationFn: (variables: AdminLeadControllerUpdateVariables) =>
      fetchAdminLeadControllerUpdate({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type AdminLeadControllerDeletePathParams = {
  leadId: string;
};

export type AdminLeadControllerDeleteError = Fetcher.ErrorWrapper<undefined>;

export type AdminLeadControllerDeleteResponse = {
  data?: Schemas.AdminLeadResDto;
  statusCode?: number;
};

export type AdminLeadControllerDeleteVariables = {
  pathParams: AdminLeadControllerDeletePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLeadControllerDelete = (
  variables: AdminLeadControllerDeleteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLeadControllerDeleteResponse,
    AdminLeadControllerDeleteError,
    undefined,
    {},
    {},
    AdminLeadControllerDeletePathParams
  >({
    url: "/api/v1/admin/leads/{leadId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminLeadControllerDelete = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLeadControllerDeleteResponse,
      AdminLeadControllerDeleteError,
      AdminLeadControllerDeleteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLeadControllerDeleteResponse,
    AdminLeadControllerDeleteError,
    AdminLeadControllerDeleteVariables
  >({
    mutationFn: (variables: AdminLeadControllerDeleteVariables) =>
      fetchAdminLeadControllerDelete({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type AdminLocationActionControllerGetAllQueryParams = {
  /**
   * From date
   *
   * @format date-time
   */
  from?: string;
  /**
   * To date
   *
   * @format date-time
   */
  to?: string;
};

export type AdminLocationActionControllerGetAllError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationActionControllerGetAllResponse = {
  data?: Schemas.AdminLocationActionResDto[];
  statusCode?: number;
};

export type AdminLocationActionControllerGetAllVariables = {
  queryParams?: AdminLocationActionControllerGetAllQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationActionControllerGetAll = (
  variables: AdminLocationActionControllerGetAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationActionControllerGetAllResponse,
    AdminLocationActionControllerGetAllError,
    undefined,
    {},
    AdminLocationActionControllerGetAllQueryParams,
    {}
  >({
    url: "/api/v1/admin/location-actions",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationActionControllerGetAll = <
  TData = AdminLocationActionControllerGetAllResponse,
>(
  variables: AdminLocationActionControllerGetAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationActionControllerGetAllResponse,
      AdminLocationActionControllerGetAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationActionControllerGetAllResponse,
    AdminLocationActionControllerGetAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/location-actions",
      operationId: "adminLocationActionControllerGetAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationActionControllerGetAll(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationActionControllerUpsertLocationActionError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationActionControllerUpsertLocationActionResponse = {
  data?: Schemas.AdminLocationActionResDto;
  statusCode?: number;
};

export type AdminLocationActionControllerUpsertLocationActionVariables = {
  body: Schemas.AdminPostLocationActionReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationActionControllerUpsertLocationAction = (
  variables: AdminLocationActionControllerUpsertLocationActionVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationActionControllerUpsertLocationActionResponse,
    AdminLocationActionControllerUpsertLocationActionError,
    Schemas.AdminPostLocationActionReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/location-actions",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminLocationActionControllerUpsertLocationAction = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationActionControllerUpsertLocationActionResponse,
      AdminLocationActionControllerUpsertLocationActionError,
      AdminLocationActionControllerUpsertLocationActionVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationActionControllerUpsertLocationActionResponse,
    AdminLocationActionControllerUpsertLocationActionError,
    AdminLocationActionControllerUpsertLocationActionVariables
  >({
    mutationFn: (
      variables: AdminLocationActionControllerUpsertLocationActionVariables,
    ) =>
      fetchAdminLocationActionControllerUpsertLocationAction({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLocationActionControllerDeleteLocationActionPathParams = {
  locationActionId: string;
};

export type AdminLocationActionControllerDeleteLocationActionError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationActionControllerDeleteLocationActionResponse = {
  data?: Schemas.AdminLocationActionResDto;
  statusCode?: number;
};

export type AdminLocationActionControllerDeleteLocationActionVariables = {
  body: Schemas.AdminPutLocationActionReqDto;
  pathParams: AdminLocationActionControllerDeleteLocationActionPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationActionControllerDeleteLocationAction = (
  variables: AdminLocationActionControllerDeleteLocationActionVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationActionControllerDeleteLocationActionResponse,
    AdminLocationActionControllerDeleteLocationActionError,
    Schemas.AdminPutLocationActionReqDto,
    {},
    {},
    AdminLocationActionControllerDeleteLocationActionPathParams
  >({
    url: "/api/v1/admin/location-actions/{locationActionId}",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminLocationActionControllerDeleteLocationAction = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminLocationActionControllerDeleteLocationActionResponse,
      AdminLocationActionControllerDeleteLocationActionError,
      AdminLocationActionControllerDeleteLocationActionVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminLocationActionControllerDeleteLocationActionResponse,
    AdminLocationActionControllerDeleteLocationActionError,
    AdminLocationActionControllerDeleteLocationActionVariables
  >({
    mutationFn: (
      variables: AdminLocationActionControllerDeleteLocationActionVariables,
    ) =>
      fetchAdminLocationActionControllerDeleteLocationAction({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLocationLocationActionControllerGetAllForLocationPathParams = {
  locationId: string;
};

export type AdminLocationLocationActionControllerGetAllForLocationQueryParams =
  {
    /**
     * From date
     *
     * @format date-time
     */
    from?: string;
    /**
     * To date
     *
     * @format date-time
     */
    to?: string;
  };

export type AdminLocationLocationActionControllerGetAllForLocationError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationLocationActionControllerGetAllForLocationResponse = {
  data?: Schemas.AdminLocationActionResDto[];
  statusCode?: number;
};

export type AdminLocationLocationActionControllerGetAllForLocationVariables = {
  pathParams: AdminLocationLocationActionControllerGetAllForLocationPathParams;
  queryParams?: AdminLocationLocationActionControllerGetAllForLocationQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationLocationActionControllerGetAllForLocation = (
  variables: AdminLocationLocationActionControllerGetAllForLocationVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationLocationActionControllerGetAllForLocationResponse,
    AdminLocationLocationActionControllerGetAllForLocationError,
    undefined,
    {},
    AdminLocationLocationActionControllerGetAllForLocationQueryParams,
    AdminLocationLocationActionControllerGetAllForLocationPathParams
  >({
    url: "/api/v1/admin/locations/{locationId}/location-actions",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationLocationActionControllerGetAllForLocation = <
  TData = AdminLocationLocationActionControllerGetAllForLocationResponse,
>(
  variables: AdminLocationLocationActionControllerGetAllForLocationVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationLocationActionControllerGetAllForLocationResponse,
      AdminLocationLocationActionControllerGetAllForLocationError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationLocationActionControllerGetAllForLocationResponse,
    AdminLocationLocationActionControllerGetAllForLocationError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/locations/{locationId}/location-actions",
      operationId: "adminLocationLocationActionControllerGetAllForLocation",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationLocationActionControllerGetAllForLocation(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminWeatherReportControllerGetAllQueryParams = {
  limit?: number;
  page?: number;
  /**
   * Search
   */
  search?: string;
  sort?:
    | "snowAmount"
    | "createdAt"
    | "updatedAt"
    | "address"
    | "elevation"
    | "reporter";
  order?: "asc" | "desc";
  severity?:
    | "rain"
    | "mixedPrecipitation"
    | "lightSnow"
    | "moderateSnow"
    | "heavySnow"
    | "noPrecipitation";
  snowAmount?: number;
};

export type AdminWeatherReportControllerGetAllError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminWeatherReportControllerGetAllResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.AdminWeatherReportResDto[];
  };

export type AdminWeatherReportControllerGetAllVariables = {
  queryParams?: AdminWeatherReportControllerGetAllQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminWeatherReportControllerGetAll = (
  variables: AdminWeatherReportControllerGetAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminWeatherReportControllerGetAllResponse,
    AdminWeatherReportControllerGetAllError,
    undefined,
    {},
    AdminWeatherReportControllerGetAllQueryParams,
    {}
  >({
    url: "/api/v1/admin/weather-reports",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminWeatherReportControllerGetAll = <
  TData = AdminWeatherReportControllerGetAllResponse,
>(
  variables: AdminWeatherReportControllerGetAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminWeatherReportControllerGetAllResponse,
      AdminWeatherReportControllerGetAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminWeatherReportControllerGetAllResponse,
    AdminWeatherReportControllerGetAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/weather-reports",
      operationId: "adminWeatherReportControllerGetAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminWeatherReportControllerGetAll(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminWeatherReportControllerCreateWeatherReportError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminWeatherReportControllerCreateWeatherReportResponse = {
  data?: Schemas.AdminWeatherReportResDto;
  statusCode?: number;
};

export type AdminWeatherReportControllerCreateWeatherReportVariables = {
  body: Schemas.AdminPostWeatherReportReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminWeatherReportControllerCreateWeatherReport = (
  variables: AdminWeatherReportControllerCreateWeatherReportVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminWeatherReportControllerCreateWeatherReportResponse,
    AdminWeatherReportControllerCreateWeatherReportError,
    Schemas.AdminPostWeatherReportReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/weather-reports",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminWeatherReportControllerCreateWeatherReport = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminWeatherReportControllerCreateWeatherReportResponse,
      AdminWeatherReportControllerCreateWeatherReportError,
      AdminWeatherReportControllerCreateWeatherReportVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminWeatherReportControllerCreateWeatherReportResponse,
    AdminWeatherReportControllerCreateWeatherReportError,
    AdminWeatherReportControllerCreateWeatherReportVariables
  >({
    mutationFn: (
      variables: AdminWeatherReportControllerCreateWeatherReportVariables,
    ) =>
      fetchAdminWeatherReportControllerCreateWeatherReport({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminWeatherReportControllerFindOnePathParams = {
  reportId: string;
};

export type AdminWeatherReportControllerFindOneError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminWeatherReportControllerFindOneResponse = {
  data?: Schemas.AdminWeatherReportResDto;
  statusCode?: number;
};

export type AdminWeatherReportControllerFindOneVariables = {
  pathParams: AdminWeatherReportControllerFindOnePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminWeatherReportControllerFindOne = (
  variables: AdminWeatherReportControllerFindOneVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminWeatherReportControllerFindOneResponse,
    AdminWeatherReportControllerFindOneError,
    undefined,
    {},
    {},
    AdminWeatherReportControllerFindOnePathParams
  >({
    url: "/api/v1/admin/weather-reports/{reportId}",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminWeatherReportControllerFindOne = <
  TData = AdminWeatherReportControllerFindOneResponse,
>(
  variables: AdminWeatherReportControllerFindOneVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminWeatherReportControllerFindOneResponse,
      AdminWeatherReportControllerFindOneError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminWeatherReportControllerFindOneResponse,
    AdminWeatherReportControllerFindOneError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/weather-reports/{reportId}",
      operationId: "adminWeatherReportControllerFindOne",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminWeatherReportControllerFindOne(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminWeatherReportControllerDeleteWeatherReportPathParams = {
  reportId: string;
};

export type AdminWeatherReportControllerDeleteWeatherReportError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminWeatherReportControllerDeleteWeatherReportResponse = {
  data?: Schemas.AdminWeatherReportResDto;
  statusCode?: number;
};

export type AdminWeatherReportControllerDeleteWeatherReportVariables = {
  pathParams: AdminWeatherReportControllerDeleteWeatherReportPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminWeatherReportControllerDeleteWeatherReport = (
  variables: AdminWeatherReportControllerDeleteWeatherReportVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminWeatherReportControllerDeleteWeatherReportResponse,
    AdminWeatherReportControllerDeleteWeatherReportError,
    undefined,
    {},
    {},
    AdminWeatherReportControllerDeleteWeatherReportPathParams
  >({
    url: "/api/v1/admin/weather-reports/{reportId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminWeatherReportControllerDeleteWeatherReport = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminWeatherReportControllerDeleteWeatherReportResponse,
      AdminWeatherReportControllerDeleteWeatherReportError,
      AdminWeatherReportControllerDeleteWeatherReportVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminWeatherReportControllerDeleteWeatherReportResponse,
    AdminWeatherReportControllerDeleteWeatherReportError,
    AdminWeatherReportControllerDeleteWeatherReportVariables
  >({
    mutationFn: (
      variables: AdminWeatherReportControllerDeleteWeatherReportVariables,
    ) =>
      fetchAdminWeatherReportControllerDeleteWeatherReport({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminWeatherReportControllerUpdateWeatherReportPathParams = {
  reportId: string;
};

export type AdminWeatherReportControllerUpdateWeatherReportError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminWeatherReportControllerUpdateWeatherReportResponse = {
  data?: Schemas.AdminWeatherReportResDto;
  statusCode?: number;
};

export type AdminWeatherReportControllerUpdateWeatherReportVariables = {
  body: Schemas.AdminPutWeatherReportReqDto;
  pathParams: AdminWeatherReportControllerUpdateWeatherReportPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminWeatherReportControllerUpdateWeatherReport = (
  variables: AdminWeatherReportControllerUpdateWeatherReportVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminWeatherReportControllerUpdateWeatherReportResponse,
    AdminWeatherReportControllerUpdateWeatherReportError,
    Schemas.AdminPutWeatherReportReqDto,
    {},
    {},
    AdminWeatherReportControllerUpdateWeatherReportPathParams
  >({
    url: "/api/v1/admin/weather-reports/{reportId}",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminWeatherReportControllerUpdateWeatherReport = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminWeatherReportControllerUpdateWeatherReportResponse,
      AdminWeatherReportControllerUpdateWeatherReportError,
      AdminWeatherReportControllerUpdateWeatherReportVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminWeatherReportControllerUpdateWeatherReportResponse,
    AdminWeatherReportControllerUpdateWeatherReportError,
    AdminWeatherReportControllerUpdateWeatherReportVariables
  >({
    mutationFn: (
      variables: AdminWeatherReportControllerUpdateWeatherReportVariables,
    ) =>
      fetchAdminWeatherReportControllerUpdateWeatherReport({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLocationWeatherReportControllerGetAllPathParams = {
  locationId: string;
};

export type AdminLocationWeatherReportControllerGetAllQueryParams = {
  limit?: number;
  page?: number;
  /**
   * Search
   */
  search?: string;
  sort?:
    | "snowAmount"
    | "createdAt"
    | "updatedAt"
    | "address"
    | "elevation"
    | "reporter";
  order?: "asc" | "desc";
  severity?:
    | "rain"
    | "mixedPrecipitation"
    | "lightSnow"
    | "moderateSnow"
    | "heavySnow"
    | "noPrecipitation";
  snowAmount?: number;
};

export type AdminLocationWeatherReportControllerGetAllError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationWeatherReportControllerGetAllResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.AdminWeatherReportResDto[];
  };

export type AdminLocationWeatherReportControllerGetAllVariables = {
  pathParams: AdminLocationWeatherReportControllerGetAllPathParams;
  queryParams?: AdminLocationWeatherReportControllerGetAllQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationWeatherReportControllerGetAll = (
  variables: AdminLocationWeatherReportControllerGetAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationWeatherReportControllerGetAllResponse,
    AdminLocationWeatherReportControllerGetAllError,
    undefined,
    {},
    AdminLocationWeatherReportControllerGetAllQueryParams,
    AdminLocationWeatherReportControllerGetAllPathParams
  >({
    url: "/api/v1/admin/locations/{locationId}/weather-reports",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationWeatherReportControllerGetAll = <
  TData = AdminLocationWeatherReportControllerGetAllResponse,
>(
  variables: AdminLocationWeatherReportControllerGetAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationWeatherReportControllerGetAllResponse,
      AdminLocationWeatherReportControllerGetAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationWeatherReportControllerGetAllResponse,
    AdminLocationWeatherReportControllerGetAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/locations/{locationId}/weather-reports",
      operationId: "adminLocationWeatherReportControllerGetAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationWeatherReportControllerGetAll(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationWeatherReportControllerGetLatestPathParams = {
  locationId: string;
};

export type AdminLocationWeatherReportControllerGetLatestError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationWeatherReportControllerGetLatestResponse = {
  data?: Schemas.AdminWeatherReportResDto;
  statusCode?: number;
};

export type AdminLocationWeatherReportControllerGetLatestVariables = {
  pathParams: AdminLocationWeatherReportControllerGetLatestPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationWeatherReportControllerGetLatest = (
  variables: AdminLocationWeatherReportControllerGetLatestVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationWeatherReportControllerGetLatestResponse,
    AdminLocationWeatherReportControllerGetLatestError,
    undefined,
    {},
    {},
    AdminLocationWeatherReportControllerGetLatestPathParams
  >({
    url: "/api/v1/admin/locations/{locationId}/weather-reports/latest",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationWeatherReportControllerGetLatest = <
  TData = AdminLocationWeatherReportControllerGetLatestResponse,
>(
  variables: AdminLocationWeatherReportControllerGetLatestVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationWeatherReportControllerGetLatestResponse,
      AdminLocationWeatherReportControllerGetLatestError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationWeatherReportControllerGetLatestResponse,
    AdminLocationWeatherReportControllerGetLatestError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/locations/{locationId}/weather-reports/latest",
      operationId: "adminLocationWeatherReportControllerGetLatest",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationWeatherReportControllerGetLatest(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminElevationLevelControllerFindManyQueryParams = {
  /**
   * @default 10
   */
  limit?: number;
  page?: number;
  /**
   * Sort
   */
  sort?: string;
  /**
   * Order
   */
  order?: "asc" | "desc";
};

export type AdminElevationLevelControllerFindManyError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminElevationLevelControllerFindManyResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.AdminElevationLevelResDto[];
  };

export type AdminElevationLevelControllerFindManyVariables = {
  queryParams?: AdminElevationLevelControllerFindManyQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminElevationLevelControllerFindMany = (
  variables: AdminElevationLevelControllerFindManyVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminElevationLevelControllerFindManyResponse,
    AdminElevationLevelControllerFindManyError,
    undefined,
    {},
    AdminElevationLevelControllerFindManyQueryParams,
    {}
  >({
    url: "/api/v1/admin/elevation-levels",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminElevationLevelControllerFindMany = <
  TData = AdminElevationLevelControllerFindManyResponse,
>(
  variables: AdminElevationLevelControllerFindManyVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminElevationLevelControllerFindManyResponse,
      AdminElevationLevelControllerFindManyError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminElevationLevelControllerFindManyResponse,
    AdminElevationLevelControllerFindManyError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/elevation-levels",
      operationId: "adminElevationLevelControllerFindMany",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminElevationLevelControllerFindMany(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminElevationLevelControllerCreateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminElevationLevelControllerCreateResponse = {
  data?: Schemas.AdminElevationLevelResDto;
  statusCode?: number;
};

export type AdminElevationLevelControllerCreateVariables = {
  body: Schemas.AdminPostElevationLevelReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminElevationLevelControllerCreate = (
  variables: AdminElevationLevelControllerCreateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminElevationLevelControllerCreateResponse,
    AdminElevationLevelControllerCreateError,
    Schemas.AdminPostElevationLevelReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/elevation-levels",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminElevationLevelControllerCreate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminElevationLevelControllerCreateResponse,
      AdminElevationLevelControllerCreateError,
      AdminElevationLevelControllerCreateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminElevationLevelControllerCreateResponse,
    AdminElevationLevelControllerCreateError,
    AdminElevationLevelControllerCreateVariables
  >({
    mutationFn: (variables: AdminElevationLevelControllerCreateVariables) =>
      fetchAdminElevationLevelControllerCreate({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminElevationLevelControllerFindByIdPathParams = {
  id: string;
};

export type AdminElevationLevelControllerFindByIdError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminElevationLevelControllerFindByIdResponse = {
  data?: Schemas.AdminElevationLevelResDto;
  statusCode?: number;
};

export type AdminElevationLevelControllerFindByIdVariables = {
  pathParams: AdminElevationLevelControllerFindByIdPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminElevationLevelControllerFindById = (
  variables: AdminElevationLevelControllerFindByIdVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminElevationLevelControllerFindByIdResponse,
    AdminElevationLevelControllerFindByIdError,
    undefined,
    {},
    {},
    AdminElevationLevelControllerFindByIdPathParams
  >({
    url: "/api/v1/admin/elevation-levels/{id}",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminElevationLevelControllerFindById = <
  TData = AdminElevationLevelControllerFindByIdResponse,
>(
  variables: AdminElevationLevelControllerFindByIdVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminElevationLevelControllerFindByIdResponse,
      AdminElevationLevelControllerFindByIdError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminElevationLevelControllerFindByIdResponse,
    AdminElevationLevelControllerFindByIdError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/elevation-levels/{id}",
      operationId: "adminElevationLevelControllerFindById",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminElevationLevelControllerFindById(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminElevationLevelControllerUpdatePathParams = {
  id: string;
};

export type AdminElevationLevelControllerUpdateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminElevationLevelControllerUpdateResponse = {
  data?: Schemas.AdminElevationLevelResDto;
  statusCode?: number;
};

export type AdminElevationLevelControllerUpdateVariables = {
  body: Schemas.AdminPutElevationLevelReqDto;
  pathParams: AdminElevationLevelControllerUpdatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminElevationLevelControllerUpdate = (
  variables: AdminElevationLevelControllerUpdateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminElevationLevelControllerUpdateResponse,
    AdminElevationLevelControllerUpdateError,
    Schemas.AdminPutElevationLevelReqDto,
    {},
    {},
    AdminElevationLevelControllerUpdatePathParams
  >({
    url: "/api/v1/admin/elevation-levels/{id}",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminElevationLevelControllerUpdate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminElevationLevelControllerUpdateResponse,
      AdminElevationLevelControllerUpdateError,
      AdminElevationLevelControllerUpdateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminElevationLevelControllerUpdateResponse,
    AdminElevationLevelControllerUpdateError,
    AdminElevationLevelControllerUpdateVariables
  >({
    mutationFn: (variables: AdminElevationLevelControllerUpdateVariables) =>
      fetchAdminElevationLevelControllerUpdate({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminElevationLevelControllerDeletePathParams = {
  id: string;
};

export type AdminElevationLevelControllerDeleteError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminElevationLevelControllerDeleteResponse = {
  data?: Schemas.AdminElevationLevelResDto;
  statusCode?: number;
};

export type AdminElevationLevelControllerDeleteVariables = {
  pathParams: AdminElevationLevelControllerDeletePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminElevationLevelControllerDelete = (
  variables: AdminElevationLevelControllerDeleteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminElevationLevelControllerDeleteResponse,
    AdminElevationLevelControllerDeleteError,
    undefined,
    {},
    {},
    AdminElevationLevelControllerDeletePathParams
  >({
    url: "/api/v1/admin/elevation-levels/{id}",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminElevationLevelControllerDelete = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminElevationLevelControllerDeleteResponse,
      AdminElevationLevelControllerDeleteError,
      AdminElevationLevelControllerDeleteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminElevationLevelControllerDeleteResponse,
    AdminElevationLevelControllerDeleteError,
    AdminElevationLevelControllerDeleteVariables
  >({
    mutationFn: (variables: AdminElevationLevelControllerDeleteVariables) =>
      fetchAdminElevationLevelControllerDelete({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminLocationWeatherForecastControllerGetDailyPathParams = {
  locationId: string;
};

export type AdminLocationWeatherForecastControllerGetDailyQueryParams = {
  /**
   * From date
   *
   * @format date
   */
  from?: string;
  /**
   * To date
   *
   * @format date
   */
  to?: string;
};

export type AdminLocationWeatherForecastControllerGetDailyError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationWeatherForecastControllerGetDailyResponse = {
  data?: Schemas.AdminDailyWeatherForecastResDto[];
  statusCode?: number;
};

export type AdminLocationWeatherForecastControllerGetDailyVariables = {
  pathParams: AdminLocationWeatherForecastControllerGetDailyPathParams;
  queryParams?: AdminLocationWeatherForecastControllerGetDailyQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationWeatherForecastControllerGetDaily = (
  variables: AdminLocationWeatherForecastControllerGetDailyVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationWeatherForecastControllerGetDailyResponse,
    AdminLocationWeatherForecastControllerGetDailyError,
    undefined,
    {},
    AdminLocationWeatherForecastControllerGetDailyQueryParams,
    AdminLocationWeatherForecastControllerGetDailyPathParams
  >({
    url: "/api/v1/admin/locations/{locationId}/weather-forecast/daily",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationWeatherForecastControllerGetDaily = <
  TData = AdminLocationWeatherForecastControllerGetDailyResponse,
>(
  variables: AdminLocationWeatherForecastControllerGetDailyVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationWeatherForecastControllerGetDailyResponse,
      AdminLocationWeatherForecastControllerGetDailyError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationWeatherForecastControllerGetDailyResponse,
    AdminLocationWeatherForecastControllerGetDailyError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/locations/{locationId}/weather-forecast/daily",
      operationId: "adminLocationWeatherForecastControllerGetDaily",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationWeatherForecastControllerGetDaily(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationWeatherForecastControllerGetHourlyPathParams = {
  locationId: string;
};

export type AdminLocationWeatherForecastControllerGetHourlyQueryParams = {
  /**
   * From date
   *
   * @format date
   */
  from?: string;
  /**
   * To date
   *
   * @format date
   */
  to?: string;
};

export type AdminLocationWeatherForecastControllerGetHourlyError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationWeatherForecastControllerGetHourlyResponse = {
  data?: Schemas.AdminHourlyWeatherForecastResDto[];
  statusCode?: number;
};

export type AdminLocationWeatherForecastControllerGetHourlyVariables = {
  pathParams: AdminLocationWeatherForecastControllerGetHourlyPathParams;
  queryParams?: AdminLocationWeatherForecastControllerGetHourlyQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationWeatherForecastControllerGetHourly = (
  variables: AdminLocationWeatherForecastControllerGetHourlyVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationWeatherForecastControllerGetHourlyResponse,
    AdminLocationWeatherForecastControllerGetHourlyError,
    undefined,
    {},
    AdminLocationWeatherForecastControllerGetHourlyQueryParams,
    AdminLocationWeatherForecastControllerGetHourlyPathParams
  >({
    url: "/api/v1/admin/locations/{locationId}/weather-forecast/hourly",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationWeatherForecastControllerGetHourly = <
  TData = AdminLocationWeatherForecastControllerGetHourlyResponse,
>(
  variables: AdminLocationWeatherForecastControllerGetHourlyVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationWeatherForecastControllerGetHourlyResponse,
      AdminLocationWeatherForecastControllerGetHourlyError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationWeatherForecastControllerGetHourlyResponse,
    AdminLocationWeatherForecastControllerGetHourlyError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/locations/{locationId}/weather-forecast/hourly",
      operationId: "adminLocationWeatherForecastControllerGetHourly",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationWeatherForecastControllerGetHourly(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminLocationWeatherForecastControllerGetSnowfallAmountPathParams =
  {
    locationId: string;
  };

export type AdminLocationWeatherForecastControllerGetSnowfallAmountQueryParams =
  {
    /**
     * From date
     *
     * @format date
     */
    from?: string;
    /**
     * To date
     *
     * @format date
     */
    to?: string;
  };

export type AdminLocationWeatherForecastControllerGetSnowfallAmountError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminLocationWeatherForecastControllerGetSnowfallAmountResponse = {
  data?: Schemas.AdminDailySnowfallAmountResDto[];
  statusCode?: number;
};

export type AdminLocationWeatherForecastControllerGetSnowfallAmountVariables = {
  pathParams: AdminLocationWeatherForecastControllerGetSnowfallAmountPathParams;
  queryParams?: AdminLocationWeatherForecastControllerGetSnowfallAmountQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminLocationWeatherForecastControllerGetSnowfallAmount = (
  variables: AdminLocationWeatherForecastControllerGetSnowfallAmountVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminLocationWeatherForecastControllerGetSnowfallAmountResponse,
    AdminLocationWeatherForecastControllerGetSnowfallAmountError,
    undefined,
    {},
    AdminLocationWeatherForecastControllerGetSnowfallAmountQueryParams,
    AdminLocationWeatherForecastControllerGetSnowfallAmountPathParams
  >({
    url: "/api/v1/admin/locations/{locationId}/weather-forecast/snowfall-amount",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminLocationWeatherForecastControllerGetSnowfallAmount = <
  TData = AdminLocationWeatherForecastControllerGetSnowfallAmountResponse,
>(
  variables: AdminLocationWeatherForecastControllerGetSnowfallAmountVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminLocationWeatherForecastControllerGetSnowfallAmountResponse,
      AdminLocationWeatherForecastControllerGetSnowfallAmountError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminLocationWeatherForecastControllerGetSnowfallAmountResponse,
    AdminLocationWeatherForecastControllerGetSnowfallAmountError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/locations/{locationId}/weather-forecast/snowfall-amount",
      operationId: "adminLocationWeatherForecastControllerGetSnowfallAmount",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminLocationWeatherForecastControllerGetSnowfallAmount(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type GuestWeatherForecastControllerGetDailyQueryParams = {
  /**
   * @format date
   */
  from?: string;
  /**
   * @format date
   */
  to?: string;
};

export type GuestWeatherForecastControllerGetDailyError =
  Fetcher.ErrorWrapper<undefined>;

export type GuestWeatherForecastControllerGetDailyResponse = {
  data?: Schemas.GuestDailyWeatherForecastResDto[];
  statusCode?: number;
};

export type GuestWeatherForecastControllerGetDailyVariables = {
  queryParams?: GuestWeatherForecastControllerGetDailyQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchGuestWeatherForecastControllerGetDaily = (
  variables: GuestWeatherForecastControllerGetDailyVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    GuestWeatherForecastControllerGetDailyResponse,
    GuestWeatherForecastControllerGetDailyError,
    undefined,
    {},
    GuestWeatherForecastControllerGetDailyQueryParams,
    {}
  >({
    url: "/api/v1/guest/weather-forecast/daily",
    method: "get",
    ...variables,
    signal,
  });

export const useGuestWeatherForecastControllerGetDaily = <
  TData = GuestWeatherForecastControllerGetDailyResponse,
>(
  variables: GuestWeatherForecastControllerGetDailyVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GuestWeatherForecastControllerGetDailyResponse,
      GuestWeatherForecastControllerGetDailyError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    GuestWeatherForecastControllerGetDailyResponse,
    GuestWeatherForecastControllerGetDailyError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/guest/weather-forecast/daily",
      operationId: "guestWeatherForecastControllerGetDaily",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGuestWeatherForecastControllerGetDaily(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminNotificationControllerGetAllQueryParams = {
  limit?: number;
  page?: number;
  /**
   * Search
   */
  search?: string;
  /**
   * Notification type
   */
  type?: "push" | "email";
  /**
   * Notification delivery type
   */
  deliveryType?: ("instant" | "scheduled" | "recurring")[];
  /**
   * Notification sort
   */
  sort?: "updatedAt" | "lastSent" | "date";
  /**
   * Notification order
   */
  order?: "asc" | "desc";
};

export type AdminNotificationControllerGetAllError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminNotificationControllerGetAllResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.AdminNotificationResDto[];
  };

export type AdminNotificationControllerGetAllVariables = {
  queryParams?: AdminNotificationControllerGetAllQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminNotificationControllerGetAll = (
  variables: AdminNotificationControllerGetAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminNotificationControllerGetAllResponse,
    AdminNotificationControllerGetAllError,
    undefined,
    {},
    AdminNotificationControllerGetAllQueryParams,
    {}
  >({
    url: "/api/v1/admin/notifications",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminNotificationControllerGetAll = <
  TData = AdminNotificationControllerGetAllResponse,
>(
  variables: AdminNotificationControllerGetAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminNotificationControllerGetAllResponse,
      AdminNotificationControllerGetAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminNotificationControllerGetAllResponse,
    AdminNotificationControllerGetAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/notifications",
      operationId: "adminNotificationControllerGetAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminNotificationControllerGetAll(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminNotificationControllerCreateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminNotificationControllerCreateResponse = {
  data?: Schemas.AdminNotificationResDto;
  statusCode?: number;
};

export type AdminNotificationControllerCreateVariables = {
  body: Schemas.AdminPostNotificationReqDto;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminNotificationControllerCreate = (
  variables: AdminNotificationControllerCreateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminNotificationControllerCreateResponse,
    AdminNotificationControllerCreateError,
    Schemas.AdminPostNotificationReqDto,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/notifications",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminNotificationControllerCreate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminNotificationControllerCreateResponse,
      AdminNotificationControllerCreateError,
      AdminNotificationControllerCreateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminNotificationControllerCreateResponse,
    AdminNotificationControllerCreateError,
    AdminNotificationControllerCreateVariables
  >({
    mutationFn: (variables: AdminNotificationControllerCreateVariables) =>
      fetchAdminNotificationControllerCreate({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminNotificationControllerGetOnePathParams = {
  notificationId: string;
};

export type AdminNotificationControllerGetOneError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminNotificationControllerGetOneResponse = {
  data?: Schemas.AdminNotificationResDto;
  statusCode?: number;
};

export type AdminNotificationControllerGetOneVariables = {
  pathParams: AdminNotificationControllerGetOnePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminNotificationControllerGetOne = (
  variables: AdminNotificationControllerGetOneVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminNotificationControllerGetOneResponse,
    AdminNotificationControllerGetOneError,
    undefined,
    {},
    {},
    AdminNotificationControllerGetOnePathParams
  >({
    url: "/api/v1/admin/notifications/{notificationId}",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminNotificationControllerGetOne = <
  TData = AdminNotificationControllerGetOneResponse,
>(
  variables: AdminNotificationControllerGetOneVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminNotificationControllerGetOneResponse,
      AdminNotificationControllerGetOneError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminNotificationControllerGetOneResponse,
    AdminNotificationControllerGetOneError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/notifications/{notificationId}",
      operationId: "adminNotificationControllerGetOne",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminNotificationControllerGetOne(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminNotificationControllerUpdatePathParams = {
  notificationId: string;
};

export type AdminNotificationControllerUpdateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminNotificationControllerUpdateResponse = {
  data?: Schemas.AdminNotificationResDto;
  statusCode?: number;
};

export type AdminNotificationControllerUpdateVariables = {
  body?: Schemas.AdminPutNotificationReqDto;
  pathParams: AdminNotificationControllerUpdatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminNotificationControllerUpdate = (
  variables: AdminNotificationControllerUpdateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminNotificationControllerUpdateResponse,
    AdminNotificationControllerUpdateError,
    Schemas.AdminPutNotificationReqDto,
    {},
    {},
    AdminNotificationControllerUpdatePathParams
  >({
    url: "/api/v1/admin/notifications/{notificationId}",
    method: "put",
    ...variables,
    signal,
  });

export const useAdminNotificationControllerUpdate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminNotificationControllerUpdateResponse,
      AdminNotificationControllerUpdateError,
      AdminNotificationControllerUpdateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminNotificationControllerUpdateResponse,
    AdminNotificationControllerUpdateError,
    AdminNotificationControllerUpdateVariables
  >({
    mutationFn: (variables: AdminNotificationControllerUpdateVariables) =>
      fetchAdminNotificationControllerUpdate({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminNotificationControllerDeletePathParams = {
  notificationId: string;
};

export type AdminNotificationControllerDeleteError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminNotificationControllerDeleteResponse = {
  data?: Schemas.AdminNotificationResDto;
  statusCode?: number;
};

export type AdminNotificationControllerDeleteVariables = {
  pathParams: AdminNotificationControllerDeletePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminNotificationControllerDelete = (
  variables: AdminNotificationControllerDeleteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminNotificationControllerDeleteResponse,
    AdminNotificationControllerDeleteError,
    undefined,
    {},
    {},
    AdminNotificationControllerDeletePathParams
  >({
    url: "/api/v1/admin/notifications/{notificationId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminNotificationControllerDelete = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminNotificationControllerDeleteResponse,
      AdminNotificationControllerDeleteError,
      AdminNotificationControllerDeleteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminNotificationControllerDeleteResponse,
    AdminNotificationControllerDeleteError,
    AdminNotificationControllerDeleteVariables
  >({
    mutationFn: (variables: AdminNotificationControllerDeleteVariables) =>
      fetchAdminNotificationControllerDelete({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminNotificationControllerSendPathParams = {
  notificationId: string;
};

export type AdminNotificationControllerSendError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminNotificationControllerSendResponse = {
  data?: Schemas.AdminPostSendNotificationsResDto;
  statusCode?: number;
};

export type AdminNotificationControllerSendVariables = {
  pathParams: AdminNotificationControllerSendPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminNotificationControllerSend = (
  variables: AdminNotificationControllerSendVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminNotificationControllerSendResponse,
    AdminNotificationControllerSendError,
    undefined,
    {},
    {},
    AdminNotificationControllerSendPathParams
  >({
    url: "/api/v1/admin/notifications/{notificationId}/send",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminNotificationControllerSend = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminNotificationControllerSendResponse,
      AdminNotificationControllerSendError,
      AdminNotificationControllerSendVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminNotificationControllerSendResponse,
    AdminNotificationControllerSendError,
    AdminNotificationControllerSendVariables
  >({
    mutationFn: (variables: AdminNotificationControllerSendVariables) =>
      fetchAdminNotificationControllerSend({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type AdminNotificationNotificationRecipientControllerGetAllPathParams = {
  notificationId: string;
};

export type AdminNotificationNotificationRecipientControllerGetAllError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminNotificationNotificationRecipientControllerGetAllResponse = {
  data?: Schemas.AdminNotificationRecipientResDto[];
  statusCode?: number;
};

export type AdminNotificationNotificationRecipientControllerGetAllVariables = {
  pathParams: AdminNotificationNotificationRecipientControllerGetAllPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminNotificationNotificationRecipientControllerGetAll = (
  variables: AdminNotificationNotificationRecipientControllerGetAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminNotificationNotificationRecipientControllerGetAllResponse,
    AdminNotificationNotificationRecipientControllerGetAllError,
    undefined,
    {},
    {},
    AdminNotificationNotificationRecipientControllerGetAllPathParams
  >({
    url: "/api/v1/admin/notifications/{notificationId}/notification-recipients",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminNotificationNotificationRecipientControllerGetAll = <
  TData = AdminNotificationNotificationRecipientControllerGetAllResponse,
>(
  variables: AdminNotificationNotificationRecipientControllerGetAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminNotificationNotificationRecipientControllerGetAllResponse,
      AdminNotificationNotificationRecipientControllerGetAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminNotificationNotificationRecipientControllerGetAllResponse,
    AdminNotificationNotificationRecipientControllerGetAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/notifications/{notificationId}/notification-recipients",
      operationId: "adminNotificationNotificationRecipientControllerGetAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminNotificationNotificationRecipientControllerGetAll(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminNotificationNotificationRecipientControllerCreatePathParams = {
  notificationId: string;
};

export type AdminNotificationNotificationRecipientControllerCreateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminNotificationNotificationRecipientControllerCreateResponse = {
  data?: Schemas.AdminNotificationRecipientResDto[];
  statusCode?: number;
};

export type AdminNotificationNotificationRecipientControllerCreateVariables = {
  body: Schemas.AdminPostNotificationNotificationRecipientReqDtoList;
  pathParams: AdminNotificationNotificationRecipientControllerCreatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminNotificationNotificationRecipientControllerCreate = (
  variables: AdminNotificationNotificationRecipientControllerCreateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminNotificationNotificationRecipientControllerCreateResponse,
    AdminNotificationNotificationRecipientControllerCreateError,
    Schemas.AdminPostNotificationNotificationRecipientReqDtoList,
    {},
    {},
    AdminNotificationNotificationRecipientControllerCreatePathParams
  >({
    url: "/api/v1/admin/notifications/{notificationId}/notification-recipients",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminNotificationNotificationRecipientControllerCreate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminNotificationNotificationRecipientControllerCreateResponse,
      AdminNotificationNotificationRecipientControllerCreateError,
      AdminNotificationNotificationRecipientControllerCreateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminNotificationNotificationRecipientControllerCreateResponse,
    AdminNotificationNotificationRecipientControllerCreateError,
    AdminNotificationNotificationRecipientControllerCreateVariables
  >({
    mutationFn: (
      variables: AdminNotificationNotificationRecipientControllerCreateVariables,
    ) =>
      fetchAdminNotificationNotificationRecipientControllerCreate({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminNotificationNotificationRecipientControllerInitialGroupPathParams =
  {
    notificationId: string;
  };

export type AdminNotificationNotificationRecipientControllerInitialGroupError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminNotificationNotificationRecipientControllerInitialGroupVariables =
  {
    body: Schemas.AdminPostNotificationNotificationRecipientInitialListReqDto;
    pathParams: AdminNotificationNotificationRecipientControllerInitialGroupPathParams;
  } & EstabrookContext["fetcherOptions"];

export const fetchAdminNotificationNotificationRecipientControllerInitialGroup =
  (
    variables: AdminNotificationNotificationRecipientControllerInitialGroupVariables,
    signal?: AbortSignal,
  ) =>
    estabrookFetch<
      undefined,
      AdminNotificationNotificationRecipientControllerInitialGroupError,
      Schemas.AdminPostNotificationNotificationRecipientInitialListReqDto,
      {},
      {},
      AdminNotificationNotificationRecipientControllerInitialGroupPathParams
    >({
      url: "/api/v1/admin/notifications/{notificationId}/notification-recipients/initial-list",
      method: "post",
      ...variables,
      signal,
    });

export const useAdminNotificationNotificationRecipientControllerInitialGroup = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      AdminNotificationNotificationRecipientControllerInitialGroupError,
      AdminNotificationNotificationRecipientControllerInitialGroupVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    undefined,
    AdminNotificationNotificationRecipientControllerInitialGroupError,
    AdminNotificationNotificationRecipientControllerInitialGroupVariables
  >({
    mutationFn: (
      variables: AdminNotificationNotificationRecipientControllerInitialGroupVariables,
    ) =>
      fetchAdminNotificationNotificationRecipientControllerInitialGroup({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminNotificationNotificationRecipientControllerDeletePathParams = {
  customerId: string;
  notificationId: string;
};

export type AdminNotificationNotificationRecipientControllerDeleteQueryParams =
  {
    /**
     * Location id
     */
    locationId?: string;
  };

export type AdminNotificationNotificationRecipientControllerDeleteError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminNotificationNotificationRecipientControllerDeleteResponse = {
  data?: Schemas.AdminNotificationRecipientResDto;
  statusCode?: number;
};

export type AdminNotificationNotificationRecipientControllerDeleteVariables = {
  pathParams: AdminNotificationNotificationRecipientControllerDeletePathParams;
  queryParams?: AdminNotificationNotificationRecipientControllerDeleteQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminNotificationNotificationRecipientControllerDelete = (
  variables: AdminNotificationNotificationRecipientControllerDeleteVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminNotificationNotificationRecipientControllerDeleteResponse,
    AdminNotificationNotificationRecipientControllerDeleteError,
    undefined,
    {},
    AdminNotificationNotificationRecipientControllerDeleteQueryParams,
    AdminNotificationNotificationRecipientControllerDeletePathParams
  >({
    url: "/api/v1/admin/notifications/{notificationId}/notification-recipients/{customerId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminNotificationNotificationRecipientControllerDelete = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminNotificationNotificationRecipientControllerDeleteResponse,
      AdminNotificationNotificationRecipientControllerDeleteError,
      AdminNotificationNotificationRecipientControllerDeleteVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminNotificationNotificationRecipientControllerDeleteResponse,
    AdminNotificationNotificationRecipientControllerDeleteError,
    AdminNotificationNotificationRecipientControllerDeleteVariables
  >({
    mutationFn: (
      variables: AdminNotificationNotificationRecipientControllerDeleteVariables,
    ) =>
      fetchAdminNotificationNotificationRecipientControllerDelete({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type LocationNoaaSyncControllerRunError =
  Fetcher.ErrorWrapper<undefined>;

export type LocationNoaaSyncControllerRunResponse = {
  data?: Schemas.String;
  statusCode?: number;
};

export type LocationNoaaSyncControllerRunVariables =
  EstabrookContext["fetcherOptions"];

export const fetchLocationNoaaSyncControllerRun = (
  variables: LocationNoaaSyncControllerRunVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    LocationNoaaSyncControllerRunResponse,
    LocationNoaaSyncControllerRunError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/location-noaa-sync",
    method: "post",
    ...variables,
    signal,
  });

export const useLocationNoaaSyncControllerRun = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      LocationNoaaSyncControllerRunResponse,
      LocationNoaaSyncControllerRunError,
      LocationNoaaSyncControllerRunVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    LocationNoaaSyncControllerRunResponse,
    LocationNoaaSyncControllerRunError,
    LocationNoaaSyncControllerRunVariables
  >({
    mutationFn: (variables: LocationNoaaSyncControllerRunVariables) =>
      fetchLocationNoaaSyncControllerRun({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type LocationNoaaSyncControllerGetFailedJobsError =
  Fetcher.ErrorWrapper<undefined>;

export type LocationNoaaSyncControllerGetFailedJobsVariables =
  EstabrookContext["fetcherOptions"];

export const fetchLocationNoaaSyncControllerGetFailedJobs = (
  variables: LocationNoaaSyncControllerGetFailedJobsVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    undefined,
    LocationNoaaSyncControllerGetFailedJobsError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/api/v1/admin/location-noaa-sync",
    method: "get",
    ...variables,
    signal,
  });

export const useLocationNoaaSyncControllerGetFailedJobs = <TData = undefined>(
  variables: LocationNoaaSyncControllerGetFailedJobsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      undefined,
      LocationNoaaSyncControllerGetFailedJobsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    undefined,
    LocationNoaaSyncControllerGetFailedJobsError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/location-noaa-sync",
      operationId: "locationNoaaSyncControllerGetFailedJobs",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchLocationNoaaSyncControllerGetFailedJobs(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminDriverStatusControllerFindAllError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminDriverStatusControllerFindAllResponse = {
  data?: Schemas.DriverStatusDto[];
  statusCode?: number;
};

export type AdminDriverStatusControllerFindAllVariables =
  EstabrookContext["fetcherOptions"];

export const fetchAdminDriverStatusControllerFindAll = (
  variables: AdminDriverStatusControllerFindAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminDriverStatusControllerFindAllResponse,
    AdminDriverStatusControllerFindAllError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/api/v2/admin/driver-status",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminDriverStatusControllerFindAll = <
  TData = AdminDriverStatusControllerFindAllResponse,
>(
  variables: AdminDriverStatusControllerFindAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminDriverStatusControllerFindAllResponse,
      AdminDriverStatusControllerFindAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminDriverStatusControllerFindAllResponse,
    AdminDriverStatusControllerFindAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/driver-status",
      operationId: "adminDriverStatusControllerFindAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminDriverStatusControllerFindAll(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminServiceAlertsControllerSendServiceAlertPathParams = {
  serviceAlertId: string;
};

export type AdminServiceAlertsControllerSendServiceAlertError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminServiceAlertsControllerSendServiceAlertResponse = {
  data?: Schemas.ServiceAlertDto;
  statusCode?: number;
};

export type AdminServiceAlertsControllerSendServiceAlertVariables = {
  pathParams: AdminServiceAlertsControllerSendServiceAlertPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminServiceAlertsControllerSendServiceAlert = (
  variables: AdminServiceAlertsControllerSendServiceAlertVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminServiceAlertsControllerSendServiceAlertResponse,
    AdminServiceAlertsControllerSendServiceAlertError,
    undefined,
    {},
    {},
    AdminServiceAlertsControllerSendServiceAlertPathParams
  >({
    url: "/api/v2/admin/service-alerts/{serviceAlertId}/send",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminServiceAlertsControllerSendServiceAlert = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminServiceAlertsControllerSendServiceAlertResponse,
      AdminServiceAlertsControllerSendServiceAlertError,
      AdminServiceAlertsControllerSendServiceAlertVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminServiceAlertsControllerSendServiceAlertResponse,
    AdminServiceAlertsControllerSendServiceAlertError,
    AdminServiceAlertsControllerSendServiceAlertVariables
  >({
    mutationFn: (
      variables: AdminServiceAlertsControllerSendServiceAlertVariables,
    ) =>
      fetchAdminServiceAlertsControllerSendServiceAlert({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminServiceAlertsControllerFindManyQueryParams = {
  /**
   * Limit
   *
   * @default 10
   */
  limit?: number;
  page?: number;
  /**
   * Search
   */
  search?: string;
  /**
   * Status
   */
  status?: "sent" | "notSentYet";
  /**
   * Sort
   */
  sort?: string;
  /**
   * Order
   */
  order?: "asc" | "desc";
};

export type AdminServiceAlertsControllerFindManyError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminServiceAlertsControllerFindManyResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.ServiceAlertDto[];
  };

export type AdminServiceAlertsControllerFindManyVariables = {
  queryParams?: AdminServiceAlertsControllerFindManyQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminServiceAlertsControllerFindMany = (
  variables: AdminServiceAlertsControllerFindManyVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminServiceAlertsControllerFindManyResponse,
    AdminServiceAlertsControllerFindManyError,
    undefined,
    {},
    AdminServiceAlertsControllerFindManyQueryParams,
    {}
  >({
    url: "/api/v2/admin/service-alerts",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminServiceAlertsControllerFindMany = <
  TData = AdminServiceAlertsControllerFindManyResponse,
>(
  variables: AdminServiceAlertsControllerFindManyVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminServiceAlertsControllerFindManyResponse,
      AdminServiceAlertsControllerFindManyError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminServiceAlertsControllerFindManyResponse,
    AdminServiceAlertsControllerFindManyError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/service-alerts",
      operationId: "adminServiceAlertsControllerFindMany",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminServiceAlertsControllerFindMany(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminServiceAlertsControllerFindByIdPathParams = {
  serviceAlertId: string;
};

export type AdminServiceAlertsControllerFindByIdError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminServiceAlertsControllerFindByIdResponse = {
  data?: Schemas.ServiceAlertDto;
  statusCode?: number;
};

export type AdminServiceAlertsControllerFindByIdVariables = {
  pathParams: AdminServiceAlertsControllerFindByIdPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminServiceAlertsControllerFindById = (
  variables: AdminServiceAlertsControllerFindByIdVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminServiceAlertsControllerFindByIdResponse,
    AdminServiceAlertsControllerFindByIdError,
    undefined,
    {},
    {},
    AdminServiceAlertsControllerFindByIdPathParams
  >({
    url: "/api/v2/admin/service-alerts/{serviceAlertId}",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminServiceAlertsControllerFindById = <
  TData = AdminServiceAlertsControllerFindByIdResponse,
>(
  variables: AdminServiceAlertsControllerFindByIdVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminServiceAlertsControllerFindByIdResponse,
      AdminServiceAlertsControllerFindByIdError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminServiceAlertsControllerFindByIdResponse,
    AdminServiceAlertsControllerFindByIdError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v2/admin/service-alerts/{serviceAlertId}",
      operationId: "adminServiceAlertsControllerFindById",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminServiceAlertsControllerFindById(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type AdminServiceAlertsControllerUpdatePathParams = {
  serviceAlertId: string;
};

export type AdminServiceAlertsControllerUpdateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminServiceAlertsControllerUpdateResponse = {
  data?: Schemas.ServiceAlertDto;
  statusCode?: number;
};

export type AdminServiceAlertsControllerUpdateVariables = {
  body?: Schemas.PatchServiceAlertReqDto;
  pathParams: AdminServiceAlertsControllerUpdatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminServiceAlertsControllerUpdate = (
  variables: AdminServiceAlertsControllerUpdateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminServiceAlertsControllerUpdateResponse,
    AdminServiceAlertsControllerUpdateError,
    Schemas.PatchServiceAlertReqDto,
    {},
    {},
    AdminServiceAlertsControllerUpdatePathParams
  >({
    url: "/api/v2/admin/service-alerts/{serviceAlertId}",
    method: "patch",
    ...variables,
    signal,
  });

export const useAdminServiceAlertsControllerUpdate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      AdminServiceAlertsControllerUpdateResponse,
      AdminServiceAlertsControllerUpdateError,
      AdminServiceAlertsControllerUpdateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    AdminServiceAlertsControllerUpdateResponse,
    AdminServiceAlertsControllerUpdateError,
    AdminServiceAlertsControllerUpdateVariables
  >({
    mutationFn: (variables: AdminServiceAlertsControllerUpdateVariables) =>
      fetchAdminServiceAlertsControllerUpdate({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminServiceAlertsControllerDeleteByIdPathParams = {
  serviceAlertId: string;
};

export type AdminServiceAlertsControllerDeleteByIdError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminServiceAlertsControllerDeleteByIdVariables = {
  pathParams: AdminServiceAlertsControllerDeleteByIdPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminServiceAlertsControllerDeleteById = (
  variables: AdminServiceAlertsControllerDeleteByIdVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    undefined,
    AdminServiceAlertsControllerDeleteByIdError,
    undefined,
    {},
    {},
    AdminServiceAlertsControllerDeleteByIdPathParams
  >({
    url: "/api/v2/admin/service-alerts/{serviceAlertId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useAdminServiceAlertsControllerDeleteById = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      AdminServiceAlertsControllerDeleteByIdError,
      AdminServiceAlertsControllerDeleteByIdVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    undefined,
    AdminServiceAlertsControllerDeleteByIdError,
    AdminServiceAlertsControllerDeleteByIdVariables
  >({
    mutationFn: (variables: AdminServiceAlertsControllerDeleteByIdVariables) =>
      fetchAdminServiceAlertsControllerDeleteById({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type EventNotificationsControllerGetAllQueryParams = {
  limit?: number;
  page?: number;
  /**
   * Type
   */
  notificationType?: ("push" | "email")[];
  /**
   * Search
   */
  search?: string;
  /**
   * Sort order
   */
  sort?: "updatedAt" | "orderIndex" | "description";
  /**
   * Sort order
   */
  order?: "asc" | "desc";
};

export type EventNotificationsControllerGetAllError =
  Fetcher.ErrorWrapper<undefined>;

export type EventNotificationsControllerGetAllResponse =
  Schemas.PaginatedResponse & {
    data?: Schemas.EventNotificationsResDto[];
  };

export type EventNotificationsControllerGetAllVariables = {
  queryParams?: EventNotificationsControllerGetAllQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchEventNotificationsControllerGetAll = (
  variables: EventNotificationsControllerGetAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    EventNotificationsControllerGetAllResponse,
    EventNotificationsControllerGetAllError,
    undefined,
    {},
    EventNotificationsControllerGetAllQueryParams,
    {}
  >({
    url: "/api/v1/admin/event-notifications",
    method: "get",
    ...variables,
    signal,
  });

export const useEventNotificationsControllerGetAll = <
  TData = EventNotificationsControllerGetAllResponse,
>(
  variables: EventNotificationsControllerGetAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      EventNotificationsControllerGetAllResponse,
      EventNotificationsControllerGetAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    EventNotificationsControllerGetAllResponse,
    EventNotificationsControllerGetAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/event-notifications",
      operationId: "eventNotificationsControllerGetAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchEventNotificationsControllerGetAll(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type EventNotificationsControllerGetByIdPathParams = {
  id: string;
};

export type EventNotificationsControllerGetByIdError =
  Fetcher.ErrorWrapper<undefined>;

export type EventNotificationsControllerGetByIdResponse = {
  data?: Schemas.EventNotificationsResDto;
  statusCode?: number;
};

export type EventNotificationsControllerGetByIdVariables = {
  body: Schemas.EventNotificationsUpdateReqDto;
  pathParams: EventNotificationsControllerGetByIdPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchEventNotificationsControllerGetById = (
  variables: EventNotificationsControllerGetByIdVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    EventNotificationsControllerGetByIdResponse,
    EventNotificationsControllerGetByIdError,
    Schemas.EventNotificationsUpdateReqDto,
    {},
    {},
    EventNotificationsControllerGetByIdPathParams
  >({
    url: "/api/v1/admin/event-notifications/{id}",
    method: "patch",
    ...variables,
    signal,
  });

export const useEventNotificationsControllerGetById = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      EventNotificationsControllerGetByIdResponse,
      EventNotificationsControllerGetByIdError,
      EventNotificationsControllerGetByIdVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    EventNotificationsControllerGetByIdResponse,
    EventNotificationsControllerGetByIdError,
    EventNotificationsControllerGetByIdVariables
  >({
    mutationFn: (variables: EventNotificationsControllerGetByIdVariables) =>
      fetchEventNotificationsControllerGetById({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteCommitStateControllerCommitLiveRoutesPathParams = {
  /**
   * Must be in the format YYYY-MM-DD.
   */
  date: string;
};

export type AdminRouteCommitStateControllerCommitLiveRoutesError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteCommitStateControllerCommitLiveRoutesVariables = {
  pathParams: AdminRouteCommitStateControllerCommitLiveRoutesPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteCommitStateControllerCommitLiveRoutes = (
  variables: AdminRouteCommitStateControllerCommitLiveRoutesVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    undefined,
    AdminRouteCommitStateControllerCommitLiveRoutesError,
    undefined,
    {},
    {},
    AdminRouteCommitStateControllerCommitLiveRoutesPathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/live/commit",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminRouteCommitStateControllerCommitLiveRoutes = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      AdminRouteCommitStateControllerCommitLiveRoutesError,
      AdminRouteCommitStateControllerCommitLiveRoutesVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    undefined,
    AdminRouteCommitStateControllerCommitLiveRoutesError,
    AdminRouteCommitStateControllerCommitLiveRoutesVariables
  >({
    mutationFn: (
      variables: AdminRouteCommitStateControllerCommitLiveRoutesVariables,
    ) =>
      fetchAdminRouteCommitStateControllerCommitLiveRoutes({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteCommitStateControllerRollbackLiveRoutesPathParams = {
  /**
   * Must be in the format YYYY-MM-DD.
   */
  date: string;
};

export type AdminRouteCommitStateControllerRollbackLiveRoutesError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteCommitStateControllerRollbackLiveRoutesVariables = {
  pathParams: AdminRouteCommitStateControllerRollbackLiveRoutesPathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteCommitStateControllerRollbackLiveRoutes = (
  variables: AdminRouteCommitStateControllerRollbackLiveRoutesVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    undefined,
    AdminRouteCommitStateControllerRollbackLiveRoutesError,
    undefined,
    {},
    {},
    AdminRouteCommitStateControllerRollbackLiveRoutesPathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/live/rollback",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminRouteCommitStateControllerRollbackLiveRoutes = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      AdminRouteCommitStateControllerRollbackLiveRoutesError,
      AdminRouteCommitStateControllerRollbackLiveRoutesVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    undefined,
    AdminRouteCommitStateControllerRollbackLiveRoutesError,
    AdminRouteCommitStateControllerRollbackLiveRoutesVariables
  >({
    mutationFn: (
      variables: AdminRouteCommitStateControllerRollbackLiveRoutesVariables,
    ) =>
      fetchAdminRouteCommitStateControllerRollbackLiveRoutes({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminRouteCommitStateControllerResetRouteShiftStatePathParams = {
  /**
   * Must be in the format YYYY-MM-DD.
   */
  date: string;
};

export type AdminRouteCommitStateControllerResetRouteShiftStateError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminRouteCommitStateControllerResetRouteShiftStateVariables = {
  pathParams: AdminRouteCommitStateControllerResetRouteShiftStatePathParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminRouteCommitStateControllerResetRouteShiftState = (
  variables: AdminRouteCommitStateControllerResetRouteShiftStateVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    undefined,
    AdminRouteCommitStateControllerResetRouteShiftStateError,
    undefined,
    {},
    {},
    AdminRouteCommitStateControllerResetRouteShiftStatePathParams
  >({
    url: "/api/v2/admin/routes/date/{date}/state/reset",
    method: "post",
    ...variables,
    signal,
  });

export const useAdminRouteCommitStateControllerResetRouteShiftState = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      AdminRouteCommitStateControllerResetRouteShiftStateError,
      AdminRouteCommitStateControllerResetRouteShiftStateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useEstabrookContext();
  return reactQuery.useMutation<
    undefined,
    AdminRouteCommitStateControllerResetRouteShiftStateError,
    AdminRouteCommitStateControllerResetRouteShiftStateVariables
  >({
    mutationFn: (
      variables: AdminRouteCommitStateControllerResetRouteShiftStateVariables,
    ) =>
      fetchAdminRouteCommitStateControllerResetRouteShiftState({
        ...fetcherOptions,
        ...variables,
      }),
    ...options,
  });
};

export type AdminCustomerLocationControllerGetAllQueryParams = {
  limit?: number;
  page?: number;
  /**
   * Customer reports enabled
   */
  reportsEnabled?: boolean;
  /**
   * Location type
   */
  type?: "onDemand" | "annual";
  /**
   * Sort by
   */
  sort?:
    | "fullName"
    | "address"
    | "emailConfirmed"
    | "reportsEnabled"
    | "region"
    | "serviceTime"
    | "elevation"
    | "serviceAccess";
  /**
   * Order
   */
  order?: "asc" | "desc";
  /**
   * Search
   */
  search?: string;
};

export type AdminCustomerLocationControllerGetAllError =
  Fetcher.ErrorWrapper<undefined>;

export type AdminCustomerLocationControllerGetAllResponse = {
  metaData?: Schemas.AdminCustomersLocationsPaginatedMetaDataDto;
  data?: Schemas.AdminCustomerLocationResDto[];
};

export type AdminCustomerLocationControllerGetAllVariables = {
  queryParams?: AdminCustomerLocationControllerGetAllQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchAdminCustomerLocationControllerGetAll = (
  variables: AdminCustomerLocationControllerGetAllVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    AdminCustomerLocationControllerGetAllResponse,
    AdminCustomerLocationControllerGetAllError,
    undefined,
    {},
    AdminCustomerLocationControllerGetAllQueryParams,
    {}
  >({
    url: "/api/v1/admin/customers-locations",
    method: "get",
    ...variables,
    signal,
  });

export const useAdminCustomerLocationControllerGetAll = <
  TData = AdminCustomerLocationControllerGetAllResponse,
>(
  variables: AdminCustomerLocationControllerGetAllVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      AdminCustomerLocationControllerGetAllResponse,
      AdminCustomerLocationControllerGetAllError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    AdminCustomerLocationControllerGetAllResponse,
    AdminCustomerLocationControllerGetAllError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/admin/customers-locations",
      operationId: "adminCustomerLocationControllerGetAll",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAdminCustomerLocationControllerGetAll(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type GuestPlowStatusControllerGetCurrentQueryParams = {
  /**
   * The timezone of the location
   */
  timezone?: string;
};

export type GuestPlowStatusControllerGetCurrentError =
  Fetcher.ErrorWrapper<undefined>;

export type GuestPlowStatusControllerGetCurrentResponse = {
  data?: Schemas.GuestPlowStatusResDto;
  statusCode?: number;
};

export type GuestPlowStatusControllerGetCurrentVariables = {
  queryParams?: GuestPlowStatusControllerGetCurrentQueryParams;
} & EstabrookContext["fetcherOptions"];

export const fetchGuestPlowStatusControllerGetCurrent = (
  variables: GuestPlowStatusControllerGetCurrentVariables,
  signal?: AbortSignal,
) =>
  estabrookFetch<
    GuestPlowStatusControllerGetCurrentResponse,
    GuestPlowStatusControllerGetCurrentError,
    undefined,
    {},
    GuestPlowStatusControllerGetCurrentQueryParams,
    {}
  >({ url: "/api/v1/guest/plow-status", method: "get", ...variables, signal });

export const useGuestPlowStatusControllerGetCurrent = <
  TData = GuestPlowStatusControllerGetCurrentResponse,
>(
  variables: GuestPlowStatusControllerGetCurrentVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GuestPlowStatusControllerGetCurrentResponse,
      GuestPlowStatusControllerGetCurrentError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);
  return reactQuery.useQuery<
    GuestPlowStatusControllerGetCurrentResponse,
    GuestPlowStatusControllerGetCurrentError,
    TData
  >({
    queryKey: queryKeyFn({
      path: "/api/v1/guest/plow-status",
      operationId: "guestPlowStatusControllerGetCurrent",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGuestPlowStatusControllerGetCurrent(
        { ...fetcherOptions, ...variables },
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export type QueryOperation =
  | {
      path: "/api/v1/admin/organizations/current";
      operationId: "adminOrganizationContextControllerGetCurrent";
      variables: AdminOrganizationContextControllerGetCurrentVariables;
    }
  | {
      path: "/api/v1/admin/organizations";
      operationId: "adminOrganizationControllerFindMany";
      variables: AdminOrganizationControllerFindManyVariables;
    }
  | {
      path: "/api/v1/admin/organizations/{id}";
      operationId: "adminOrganizationControllerFindById";
      variables: AdminOrganizationControllerFindByIdVariables;
    }
  | {
      path: "/api/v1/admin/customers/{customerId}";
      operationId: "adminCustomerControllerGetOne";
      variables: AdminCustomerControllerGetOneVariables;
    }
  | {
      path: "/api/v1/admin/customers";
      operationId: "adminCustomerControllerGetAll";
      variables: AdminCustomerControllerGetAllVariables;
    }
  | {
      path: "/api/v2/admin/regions";
      operationId: "adminRegionV2ControllerGetRegions";
      variables: AdminRegionV2ControllerGetRegionsVariables;
    }
  | {
      path: "/api/v2/admin/regions/select-options";
      operationId: "adminRegionV2ControllerGetRegionsForSelectOptions";
      variables: AdminRegionV2ControllerGetRegionsForSelectOptionsVariables;
    }
  | {
      path: "/api/v2/admin/regions/paged";
      operationId: "adminRegionV2ControllerGetRegionsPaged";
      variables: AdminRegionV2ControllerGetRegionsPagedVariables;
    }
  | {
      path: "/api/v2/admin/regions/{id}/suggested-drivers";
      operationId: "adminRegionV2ControllerGetSuggestedDrivers";
      variables: AdminRegionV2ControllerGetSuggestedDriversVariables;
    }
  | {
      path: "/api/v2/admin/regions/{id}";
      operationId: "adminRegionV2ControllerGetRegionById";
      variables: AdminRegionV2ControllerGetRegionByIdVariables;
    }
  | {
      path: "/api/v1/admin/vehicles";
      operationId: "vehicleControllerGetAll";
      variables: VehicleControllerGetAllVariables;
    }
  | {
      path: "/api/v1/admin/vehicles/csv";
      operationId: "vehicleControllerGetAllCsv";
      variables: VehicleControllerGetAllCsvVariables;
    }
  | {
      path: "/api/v1/admin/vehicles/unassigned";
      operationId: "vehicleControllerGetUnassignedVehicles";
      variables: VehicleControllerGetUnassignedVehiclesVariables;
    }
  | {
      path: "/api/v1/admin/vehicles/{vehicleId}";
      operationId: "vehicleControllerGetVehicleById";
      variables: VehicleControllerGetVehicleByIdVariables;
    }
  | {
      path: "/api/v1/admin/vehicle-types";
      operationId: "vehicleTypeControllerFindMany";
      variables: VehicleTypeControllerFindManyVariables;
    }
  | {
      path: "/api/v1/admin/vehicle-types/{typeId}";
      operationId: "vehicleTypeControllerGetVehicleType";
      variables: VehicleTypeControllerGetVehicleTypeVariables;
    }
  | {
      path: "/api/v2/admin/routes";
      operationId: "adminRouteV2ControllerGetRoutes";
      variables: AdminRouteV2ControllerGetRoutesVariables;
    }
  | {
      path: "/api/v2/admin/routes/paged";
      operationId: "adminRouteV2ControllerGetRoutesPaged";
      variables: AdminRouteV2ControllerGetRoutesPagedVariables;
    }
  | {
      path: "/api/v2/admin/routes/states";
      operationId: "adminRouteV2ControllerGetRoutesStatusesPerMonth";
      variables: AdminRouteV2ControllerGetRoutesStatusesPerMonthVariables;
    }
  | {
      path: "/api/v2/admin/routes/build-date";
      operationId: "adminRouteV2ControllerGetDateOfBuildRoutes";
      variables: AdminRouteV2ControllerGetDateOfBuildRoutesVariables;
    }
  | {
      path: "/api/v2/admin/routes/live-date";
      operationId: "adminRouteV2ControllerGetDateOfLiveRoutes";
      variables: AdminRouteV2ControllerGetDateOfLiveRoutesVariables;
    }
  | {
      path: "/api/v2/admin/routes/date/{date}/locations";
      operationId: "adminRouteV2ControllerFindRouteLocations";
      variables: AdminRouteV2ControllerFindRouteLocationsVariables;
    }
  | {
      path: "/api/v2/admin/routes/date/{date}/notifications";
      operationId: "adminRouteV2ControllerGetRouteNotificationsPerDate";
      variables: AdminRouteV2ControllerGetRouteNotificationsPerDateVariables;
    }
  | {
      path: "/api/v2/admin/routes/date/{date}/state/check";
      operationId: "adminRouteV2ControllerGetRouteStateDiff";
      variables: AdminRouteV2ControllerGetRouteStateDiffVariables;
    }
  | {
      path: "/api/v1/admin/locations";
      operationId: "adminLocationControllerGetAll";
      variables: AdminLocationControllerGetAllVariables;
    }
  | {
      path: "/api/v1/admin/locations/csv";
      operationId: "adminLocationControllerGetAllCSV";
      variables: AdminLocationControllerGetAllCSVVariables;
    }
  | {
      path: "/api/v1/admin/locations/csv-template";
      operationId: "adminLocationControllerGetCsvTemplate";
      variables: AdminLocationControllerGetCsvTemplateVariables;
    }
  | {
      path: "/api/v1/admin/locations/csv/state";
      operationId: "adminLocationControllerGetCsvUploadState";
      variables: AdminLocationControllerGetCsvUploadStateVariables;
    }
  | {
      path: "/api/v1/admin/locations/{locationId}";
      operationId: "adminLocationControllerGetOne";
      variables: AdminLocationControllerGetOneVariables;
    }
  | {
      path: "/api/v1/admin/customers/{customerId}/locations";
      operationId: "adminLocationCustomerControllerGetAll";
      variables: AdminLocationCustomerControllerGetAllVariables;
    }
  | {
      path: "/api/v1/admin/locations/eligibility/{date}";
      operationId: "adminLocationEligibilityControllerGetLocationsOnDate";
      variables: AdminLocationEligibilityControllerGetLocationsOnDateVariables;
    }
  | {
      path: "/api/v1/admin/locations/eligibility/{date}/statistics";
      operationId: "adminLocationEligibilityControllerGetStatisticsOnDate";
      variables: AdminLocationEligibilityControllerGetStatisticsOnDateVariables;
    }
  | {
      path: "/api/v2/admin/locations";
      operationId: "adminLocationV2ControllerGetAllLocations";
      variables: AdminLocationV2ControllerGetAllLocationsVariables;
    }
  | {
      path: "/api/v2/admin/locations/paged";
      operationId: "adminLocationV2ControllerFindMany";
      variables: AdminLocationV2ControllerFindManyVariables;
    }
  | {
      path: "/api/v2/admin/locations/date/{date}/unassigned";
      operationId: "adminLocationV2ControllerGetUnassignedRouteLocationsForDate";
      variables: AdminLocationV2ControllerGetUnassignedRouteLocationsForDateVariables;
    }
  | {
      path: "/api/v1/admin/customers/csv";
      operationId: "adminCustomerControllerGetAllCSV";
      variables: AdminCustomerControllerGetAllCSVVariables;
    }
  | {
      path: "/api/v1/admin/accounts";
      operationId: "adminAccountControllerGetAll";
      variables: AdminAccountControllerGetAllVariables;
    }
  | {
      path: "/api/v2/admin/locations/{locationId}/location-notes";
      operationId: "adminLocationNotesControllerFindAll";
      variables: AdminLocationNotesControllerFindAllVariables;
    }
  | {
      path: "/api/v1/admin/drivers";
      operationId: "driverControllerGetAll";
      variables: DriverControllerGetAllVariables;
    }
  | {
      path: "/api/v1/admin/drivers/csv";
      operationId: "driverControllerGetAllCsv";
      variables: DriverControllerGetAllCsvVariables;
    }
  | {
      path: "/api/v1/admin/drivers/{driverId}";
      operationId: "driverControllerGetDriverById";
      variables: DriverControllerGetDriverByIdVariables;
    }
  | {
      path: "/api/v1/guest/sent-notification-recipients";
      operationId: "guestSentNotificationRecipientControllerGetAll";
      variables: GuestSentNotificationRecipientControllerGetAllVariables;
    }
  | {
      path: "/api/v1/admin/auth/session";
      operationId: "adminAuthControllerGetSession";
      variables: AdminAuthControllerGetSessionVariables;
    }
  | {
      path: "/api/v1/admin/leads";
      operationId: "adminLeadControllerGetAll";
      variables: AdminLeadControllerGetAllVariables;
    }
  | {
      path: "/api/v1/admin/leads/csv";
      operationId: "adminLeadControllerGetAllCSV";
      variables: AdminLeadControllerGetAllCSVVariables;
    }
  | {
      path: "/api/v1/admin/leads/csv-template";
      operationId: "adminLeadControllerGetCsvTemplate";
      variables: AdminLeadControllerGetCsvTemplateVariables;
    }
  | {
      path: "/api/v1/admin/leads/{leadId}";
      operationId: "adminLeadControllerGetOne";
      variables: AdminLeadControllerGetOneVariables;
    }
  | {
      path: "/api/v1/admin/location-actions";
      operationId: "adminLocationActionControllerGetAll";
      variables: AdminLocationActionControllerGetAllVariables;
    }
  | {
      path: "/api/v1/admin/locations/{locationId}/location-actions";
      operationId: "adminLocationLocationActionControllerGetAllForLocation";
      variables: AdminLocationLocationActionControllerGetAllForLocationVariables;
    }
  | {
      path: "/api/v1/admin/weather-reports";
      operationId: "adminWeatherReportControllerGetAll";
      variables: AdminWeatherReportControllerGetAllVariables;
    }
  | {
      path: "/api/v1/admin/weather-reports/{reportId}";
      operationId: "adminWeatherReportControllerFindOne";
      variables: AdminWeatherReportControllerFindOneVariables;
    }
  | {
      path: "/api/v1/admin/locations/{locationId}/weather-reports";
      operationId: "adminLocationWeatherReportControllerGetAll";
      variables: AdminLocationWeatherReportControllerGetAllVariables;
    }
  | {
      path: "/api/v1/admin/locations/{locationId}/weather-reports/latest";
      operationId: "adminLocationWeatherReportControllerGetLatest";
      variables: AdminLocationWeatherReportControllerGetLatestVariables;
    }
  | {
      path: "/api/v1/admin/elevation-levels";
      operationId: "adminElevationLevelControllerFindMany";
      variables: AdminElevationLevelControllerFindManyVariables;
    }
  | {
      path: "/api/v1/admin/elevation-levels/{id}";
      operationId: "adminElevationLevelControllerFindById";
      variables: AdminElevationLevelControllerFindByIdVariables;
    }
  | {
      path: "/api/v1/admin/locations/{locationId}/weather-forecast/daily";
      operationId: "adminLocationWeatherForecastControllerGetDaily";
      variables: AdminLocationWeatherForecastControllerGetDailyVariables;
    }
  | {
      path: "/api/v1/admin/locations/{locationId}/weather-forecast/hourly";
      operationId: "adminLocationWeatherForecastControllerGetHourly";
      variables: AdminLocationWeatherForecastControllerGetHourlyVariables;
    }
  | {
      path: "/api/v1/admin/locations/{locationId}/weather-forecast/snowfall-amount";
      operationId: "adminLocationWeatherForecastControllerGetSnowfallAmount";
      variables: AdminLocationWeatherForecastControllerGetSnowfallAmountVariables;
    }
  | {
      path: "/api/v1/guest/weather-forecast/daily";
      operationId: "guestWeatherForecastControllerGetDaily";
      variables: GuestWeatherForecastControllerGetDailyVariables;
    }
  | {
      path: "/api/v1/admin/notifications";
      operationId: "adminNotificationControllerGetAll";
      variables: AdminNotificationControllerGetAllVariables;
    }
  | {
      path: "/api/v1/admin/notifications/{notificationId}";
      operationId: "adminNotificationControllerGetOne";
      variables: AdminNotificationControllerGetOneVariables;
    }
  | {
      path: "/api/v1/admin/notifications/{notificationId}/notification-recipients";
      operationId: "adminNotificationNotificationRecipientControllerGetAll";
      variables: AdminNotificationNotificationRecipientControllerGetAllVariables;
    }
  | {
      path: "/api/v1/admin/location-noaa-sync";
      operationId: "locationNoaaSyncControllerGetFailedJobs";
      variables: LocationNoaaSyncControllerGetFailedJobsVariables;
    }
  | {
      path: "/api/v2/admin/driver-status";
      operationId: "adminDriverStatusControllerFindAll";
      variables: AdminDriverStatusControllerFindAllVariables;
    }
  | {
      path: "/api/v2/admin/service-alerts";
      operationId: "adminServiceAlertsControllerFindMany";
      variables: AdminServiceAlertsControllerFindManyVariables;
    }
  | {
      path: "/api/v2/admin/service-alerts/{serviceAlertId}";
      operationId: "adminServiceAlertsControllerFindById";
      variables: AdminServiceAlertsControllerFindByIdVariables;
    }
  | {
      path: "/api/v1/admin/event-notifications";
      operationId: "eventNotificationsControllerGetAll";
      variables: EventNotificationsControllerGetAllVariables;
    }
  | {
      path: "/api/v1/admin/customers-locations";
      operationId: "adminCustomerLocationControllerGetAll";
      variables: AdminCustomerLocationControllerGetAllVariables;
    }
  | {
      path: "/api/v1/guest/plow-status";
      operationId: "guestPlowStatusControllerGetCurrent";
      variables: GuestPlowStatusControllerGetCurrentVariables;
    };
